// src/edges/index.ts
import { keyBy } from "lodash-es";
import * as z6 from "zod";

// src/edges/dataKey.edge-type.ts
import * as z4 from "zod";

// src/guide_item.types.ts
import * as z3 from "zod";

// src/string.types.ts
import * as z2 from "zod";

// src/chatHints.ts
import * as z from "zod";
var chatHintsRegistry = z.registry();
function getChatHints(schema) {
  const chain = walkInnerTypes(schema);
  const hits = chain.map((s) => chatHintsRegistry.get(s)).filter((h) => !!h);
  if (hits.length === 0) return void 0;
  const innerToOuter = [...hits].reverse();
  const commentaryParts = innerToOuter.map((h) => h.commentary).filter((c) => !!c);
  const merged = {};
  if (commentaryParts.length > 0) merged.commentary = commentaryParts.join("\n\n");
  const outerWinning = hits.find((h) => h.formOnly !== void 0);
  if (outerWinning?.formOnly !== void 0) merged.formOnly = outerWinning.formOnly;
  const askWin = hits.find((h) => h.askBeforeCommit !== void 0);
  if (askWin?.askBeforeCommit !== void 0) merged.askBeforeCommit = askWin.askBeforeCommit;
  const promptWin = hits.find((h) => h.promptHint !== void 0);
  if (promptWin?.promptHint !== void 0) merged.promptHint = promptWin.promptHint;
  return merged;
}
function getChainedDescription(schema) {
  const chain = walkInnerTypes(schema);
  const descs = chain.map((s) => s.description).filter((d) => !!d);
  if (descs.length === 0) return void 0;
  return [...descs].reverse().join("\n\n");
}
function walkInnerTypes(schema) {
  const out = [];
  let current = schema;
  while (current) {
    out.push(current);
    const innerType = current._def?.innerType;
    current = innerType;
  }
  return out;
}

// src/string.types.ts
var DomainEventIdSchema = z2.string().brand();
var NotionWebhookEventIdSchema = z2.string().brand();
var TimestampSchema = z2.iso.datetime({ offset: true });
var LocalDateSchema = z2.string().transform((value) => value.slice(0, 10)).pipe(z2.iso.date()).brand();
var UserIdSchema = z2.string().brand();
var HomeIdSchema = z2.string().brand();
var RoomIdSchema = z2.string().brand().describe("A branded RoomId \u2014 an opaque Firestore document id for a room.").register(chatHintsRegistry, {
  commentary: 'Pull the value from the home context\'s rooms list \u2014 specifically the value in `[brackets]` after each room entry (e.g. `- Kitchen (kitchen) [hsYUL5DZPJEQOwhje8rd]` \u2192 use `hsYUL5DZPJEQOwhje8rd`). Users speak in names and types ("the kitchen", "main bedroom", "garage"); NEVER pass the user\'s words as the id.'
});
var GuideVisitIdSchema = z2.string().brand();
var ModelIdSchema = z2.string().brand();
var ModelDocumentIdSchema = z2.string().brand();
var DocumentSearchIdSchema = z2.string().brand();
var DocumentCoverageKeySchema = z2.string().brand();
var ModelSpellingKeySchema = z2.string().brand();
var ModelNumberSchema = z2.string().brand();
var SerialNumberSchema = z2.string().brand();
var SpaceIdSchema = z2.string().brand().describe(
  "A branded SpaceId \u2014 taxonomy-level area of a home (e.g. `appliances`, `hvac`, `plumbing`). Drawn from the `DashboardSpaceId` enum; `kitchen` etc. are rooms, not spaces."
).register(chatHintsRegistry, {
  commentary: 'A branded SpaceId from the `DashboardSpaceId` enum (`home`, `hvac`, `appliances`, `plumbing`, `safety`, `electrical`, `surfaces`, `garage`). Get it from the `list_equipment_types` result for the matching type, or copy it from an existing equipment entry of the same type in the home context. NEVER translate the user\'s words ("the kitchen", "downstairs") into a SpaceId \u2014 those are rooms, not spaces.'
});
var BlockIdSchema = z2.string().brand();
var GuideStarterIdSchema = z2.string().brand();
var FolderIdSchema = z2.string().brand();
var SystemInfoIdSchema = z2.string().brand();
var EquipmentIdSchema = z2.string().brand().describe("A branded EquipmentId \u2014 an opaque Firestore document id for a piece of equipment.").register(chatHintsRegistry, {
  commentary: 'Pull the value from the home context\'s equipment list \u2014 specifically the value in `[brackets]` after each equipment entry (e.g. `- Dishwasher in Kitchen [hsYUL5DZPJEQOwhje8rd]` \u2192 use `hsYUL5DZPJEQOwhje8rd`). Users speak by nickname, type, or location ("my AC", "the upstairs water heater"); NEVER pass the user\'s words as the id.'
});
var ConsiderationIdSchema = z2.string().brand();
var LessonIdSchema = z2.string().brand();
var BrandIdSchema = z2.string().brand();
var BrandNameSchema = z2.string().brand();
var BrandAliasKeySchema = z2.string().brand();
var BrandRequestIdSchema = z2.string().brand();
var BrandMergeIdSchema = z2.string().brand();
var BrandResearchIdSchema = z2.string().brand();
var NotificationIdSchema = z2.string().brand();
var UserNotificationIdSchema = z2.string().brand();
var DeviceIdSchema = z2.string().brand();
var RockDocIdSchema = z2.string().brand();
var CompanyIdSchema = z2.string().brand();
var ContactIdSchema = z2.string().brand();
var CodeWordSchema = z2.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/, "Only letters, numbers, dashes, and underscores are allowed").min(1).max(50);
var DataKeySchema = z2.string().regex(
  /^[a-zA-Z0-9$:_~-]+$/,
  'Only letters, numbers, dashes, and some special characters (any of "$:-~_") are allowed'
).min(1).max(50).brand("DataKey");
var RecentlyViewedIdSchema = z2.string().brand();
var HomeEvalJobIdSchema = z2.string().brand();
var HomeEvalResultIdSchema = z2.string().brand();
var HomeQuestionIdSchema = z2.string().brand();
var PlanReviewIdSchema = z2.string().brand();
var TaskGenerationJobRunIdSchema = z2.string().brand();
var RockManualsJobIdSchema = z2.string().brand();
var AppliesToDraftResultIdSchema = z2.string().brand();
var ProcessHowOftenResultIdSchema = z2.string().brand();
var RecallIdSchema = z2.string().brand();
var RecallVerdictIdSchema = z2.string().brand();
var RecallJobIdSchema = z2.string().brand();
var HelpTypeSchema = z2.enum(["help-me-decide", "model-number-help", "none"]);
var CUSTOM_ID_PREFIX = "c:";
function isCustomId(id) {
  return id.startsWith(CUSTOM_ID_PREFIX);
}
var TypeIdSchema = z2.string().regex(/^[a-zA-Z0-9_:-]+$/, "Only letters, numbers, dashes, underscores, and colons are allowed").min(1).max(50);
var EquipmentTypeIdSchema = TypeIdSchema.brand().describe("A branded EquipmentTypeId \u2014 catalog id for a kind of equipment (e.g. central AC, dishwasher).").register(chatHintsRegistry, {
  commentary: 'Resolve via the `list_equipment_types` tool. Users speak in plain words ("water heater", "central AC", "smoke detector"); NEVER pass the user\'s words as the id (e.g. do NOT pass "dishwasher" or "Dishwasher"). Always call list_equipment_types first to find the matching catalog id, then pass that id here. If no entry matches, ask the user to clarify rather than guessing.'
});
var EquipmentTypeIdOrContextSchema = z2.union([EquipmentTypeIdSchema, z2.literal("[context]")]);
var EquipmentTypeCandidateIdSchema = TypeIdSchema.brand();
var InfoTypeIdSchema = TypeIdSchema.brand();
var RecallTopicIdSchema = TypeIdSchema.brand();
var ConsiderationTypeIdSchema = CodeWordSchema.brand();
var LessonTypeIdSchema = CodeWordSchema.brand();
var TaskTypeIdSchema = TypeIdSchema.brand();
var TaskIdSchema = z2.string().brand();
var HomeTaskSubscriptionIdSchema = z2.string().brand();
var ChatConversationIdSchema = z2.string().brand();
var ChatMessageIdSchema = z2.string().brand();

// src/util/assertUnreachable.ts
function assertUnreachable(_) {
  throw new Error("Didn't expect to get here");
}

// src/guide_item.types.ts
var GuideEquipmentItemSchema = z3.object({
  category: z3.literal("equipment"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema
});
var GuideInfoItemSchema = z3.object({
  category: z3.literal("info"),
  infoTypeId: InfoTypeIdSchema
});
var GuideConsiderationItemSchema = z3.object({
  category: z3.literal("consideration"),
  considerationTypeId: ConsiderationTypeIdSchema
});
function getGuideItemId(item) {
  switch (item.category) {
    case "equipment":
      return item.equipmentTypeId;
    case "info":
      return item.infoTypeId;
    case "consideration":
      return item.considerationTypeId;
    default:
      assertUnreachable(item);
  }
}
var AnyGuideItemSchema = z3.discriminatedUnion("category", [
  GuideEquipmentItemSchema,
  GuideInfoItemSchema,
  GuideConsiderationItemSchema
]);

// src/edges/dataKey.edge-type.ts
var DataKeyDataSchema = z4.object({
  type: z4.literal("dataKey"),
  ignoreForValidation: z4.boolean().optional(),
  items: z4.object({
    equipment: z4.array(GuideEquipmentItemSchema).optional(),
    considerations: z4.array(GuideConsiderationItemSchema).optional()
  }).optional()
});

// src/edges/outgoingHandles.ts
import * as z5 from "zod";
var OutgoingHandleSchema = z5.string().brand();
var outgoingHandles = {
  // Address Lookup
  "address-lookup": (nodeId, _) => ({
    next: `${nodeId}:next`,
    failure: `${nodeId}:failure`
  }),
  // Create Equipment
  "create-equipment": (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Exit
  exit: () => ({}),
  // Equipment
  equipment: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Equipment Attribute
  "equipment-attribute": (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Equipment List
  ["equipment-list"]: (nodeId, _) => ({
    addAnother: `${nodeId}:add`,
    done: `${nodeId}:done`
  }),
  // Entry
  entry: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // If
  if: (nodeId, _) => ({
    true: `${nodeId}:true`,
    false: `${nodeId}:false`
  }),
  // Set
  set: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Select
  select: (nodeId, data) => data.multi?.enabled ? {
    type: "multi",
    next: `${nodeId}:next`
  } : {
    type: "single",
    sectionOptions: data.sections.map(
      (section, sectionIndex) => section.options.map(
        (_option, optionIndex) => `${nodeId}:section-${sectionIndex}:option-${optionIndex}`
      )
    )
  },
  // Yes-No
  "yes-no": (nodeId, data) => ({
    yes: `${nodeId}:yes`,
    no: `${nodeId}:no`,
    ...data.forceSelection ? {} : { unsure: `${nodeId}:unsure` }
  }),
  // Image
  image: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Info
  info: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Initial Rooms
  "initial-rooms": (nodeId, _) => ({
    done: `${nodeId}:done`
  }),
  // Input
  input: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Room Starter Selection
  "room-starter-selection": (nodeId, _) => ({
    done: `${nodeId}:done`
  }),
  // Return
  return: (nodeId, data) => data.returnType === "edge" ? { type: "edge", returnTo: `${nodeId}:returnTo` } : { type: "count" },
  // Room
  room: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Scan
  scan: (nodeId, _) => ({
    next: `${nodeId}:next`
  }),
  // Sub-Guide
  "sub-guide": (nodeId, _) => ({
    next: `${nodeId}:next`
  })
};
function getAllOutgoingHandles(nodeId, data) {
  switch (data.type) {
    case "address-lookup":
      return Object.values(outgoingHandles["address-lookup"](nodeId, data));
    case "create-equipment":
      return Object.values(outgoingHandles["create-equipment"](nodeId, data));
    case "entry":
      return Object.values(outgoingHandles.entry(nodeId, data));
    case "equipment":
      return Object.values(outgoingHandles.equipment(nodeId, data));
    case "equipment-attribute":
      return Object.values(outgoingHandles["equipment-attribute"](nodeId, data));
    case "equipment-list":
      return Object.values(outgoingHandles["equipment-list"](nodeId, data));
    case "exit":
      return [];
    case "if":
      return Object.values(outgoingHandles.if(nodeId, data));
    case "select": {
      const selectHandles = outgoingHandles.select(nodeId, data);
      return selectHandles.type === "multi" ? [selectHandles.next] : selectHandles.sectionOptions.flat();
    }
    case "yes-no":
      return Object.values(outgoingHandles["yes-no"](nodeId, data));
    case "image":
      return Object.values(outgoingHandles.image(nodeId, data));
    case "info":
      return Object.values(outgoingHandles.info(nodeId, data));
    case "initial-rooms":
      return Object.values(outgoingHandles["initial-rooms"](nodeId, data));
    case "input":
      return Object.values(outgoingHandles.input(nodeId, data));
    case "return": {
      const returnHandles = outgoingHandles.return(nodeId, data);
      return returnHandles.type === "edge" ? [returnHandles.returnTo] : [];
    }
    case "room":
      return Object.values(outgoingHandles.room(nodeId, data));
    case "room-starter-selection":
      return Object.values(outgoingHandles["room-starter-selection"](nodeId, data));
    case "scan":
      return Object.values(outgoingHandles.scan(nodeId, data));
    case "set":
      return Object.values(outgoingHandles.set(nodeId, data));
    case "sub-guide":
      return Object.values(outgoingHandles["sub-guide"](nodeId, data));
    default:
      assertUnreachable(data);
  }
}

// src/edges/index.ts
var schemas = [DataKeyDataSchema];
var edgeDataSchemas = keyBy(schemas, (s) => s.shape.type.value);
var AppEdgeDataSchema = z6.discriminatedUnion("type", schemas);

// src/image.types.ts
import * as z8 from "zod";

// src/util/schemaUtils.ts
import * as z7 from "zod";
function isObjectType(val, objectType) {
  return typeof val === "object" && val !== null && "object" in val && val.object === objectType;
}
function isObjectSchema(val, objectType, version) {
  return isObjectType(val, objectType) && "schema" in val && val.schema === version;
}
function withSingularArrayFields(schema, mappings) {
  return z7.preprocess((val) => {
    if (typeof val !== "object" || val === null) return val;
    const out = { ...val };
    for (const [singular, plural] of mappings) {
      if (out[plural] === void 0 && out[singular] !== void 0 && out[singular] !== null) {
        out[plural] = [out[singular]];
      }
      delete out[singular];
    }
    return out;
  }, schema);
}

// src/image.types.ts
var BoundingBoxSchema = z8.object({
  x: z8.number(),
  y: z8.number(),
  width: z8.number(),
  height: z8.number()
});
var ImageObjectSchema = z8.object({
  id: z8.string(),
  /** Who added it; absent on one Rock found (a catalog picture, a rehost). */
  addedBy: UserIdSchema.optional(),
  url: z8.string(),
  width: z8.number(),
  height: z8.number(),
  thumbnail: z8.string(),
  title: z8.string().optional(),
  notes: z8.string().optional(),
  uploadPending: z8.boolean().optional(),
  mediaType: z8.enum(["image", "video"]).optional(),
  duration: z8.number().optional(),
  /** Render-time rotation in degrees, normalized to [0, 360). 0/undefined = no rotation. */
  rotation: z8.number().optional(),
  /**
   * Regions of the image worth highlighting (e.g. the model-number AND serial-number boxes on a
   * shared name-plate photo) in the original (un-rotated) image coordinate space. Drawn as overlays
   * in the full-screen viewer whenever present.
   */
  highlights: z8.array(BoundingBoxSchema).optional()
});
var ImageSchema = withSingularArrayFields(ImageObjectSchema, [["highlight", "highlights"]]);
var ImageOptionsSchema = z8.object({
  current: ImageSchema,
  options: z8.array(ImageSchema)
});

// src/imageCollection.ts
function orderedImages(doc) {
  const { images, imageOrder } = doc;
  if (!images) return [];
  if (Array.isArray(images)) return images;
  const ordered = [];
  const seen = /* @__PURE__ */ new Set();
  for (const id of imageOrder ?? []) {
    const image = images[id];
    if (image && !seen.has(id)) {
      ordered.push(image);
      seen.add(id);
    }
  }
  const unordered = Object.values(images).filter((img) => !seen.has(img.id));
  unordered.sort((a, b) => a.id.localeCompare(b.id));
  return [...ordered, ...unordered];
}
function primaryImage(doc) {
  return orderedImages(doc)[0];
}
function toImageCollection(images) {
  const map = {};
  const order = [];
  for (const image of images) {
    if (!(image.id in map)) order.push(image.id);
    map[image.id] = image;
  }
  return { images: map, imageOrder: order };
}

// src/json.ts
import { z as z9, ZodIssueCode } from "zod";
var parseJsonPreprocessor = (value, ctx) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: e.message
      });
    }
  }
  return value;
};
function jsonParser(schema) {
  return z9.preprocess(parseJsonPreprocessor, schema);
}

// src/nodes/index.ts
import { keyBy as keyBy3 } from "lodash-es";
import * as z32 from "zod";

// src/nodes/address-lookup.node-type.ts
import * as z11 from "zod";

// src/nodes/common.node-type.ts
import * as z10 from "zod";
var CommonNodeDataSchema = z10.object({
  pageTitle: z10.string().min(2).max(30),
  prompts: z10.array(
    z10.object({
      text: z10.string().min(2).max(250),
      minimal: z10.boolean().optional()
    })
  ).min(1, "At least one prompt is required")
});

// src/nodes/address-lookup.node-type.ts
var AddressLookupDataSchema = CommonNodeDataSchema.extend({
  type: z11.literal("address-lookup"),
  dataKey: DataKeySchema,
  infoTypeId: InfoTypeIdSchema.optional(),
  buttonLabel: z11.string().min(1)
});

// src/nodes/create-equipment.node-type.ts
import * as z12 from "zod";
var CreateEquipmentDataSchema = z12.object({
  type: z12.literal("create-equipment"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema,
  resetValuesAfterCreate: z12.boolean().optional(),
  allowMultiple: z12.boolean().optional()
});

// src/nodes/entry.node-type.ts
import * as z13 from "zod";
var GuideIntroSchema = z13.enum(["space", "generic", "skip"]);
var DEFAULT_GUIDE_INTRO = "space";
var EntryDataSchema = z13.object({
  type: z13.literal("entry"),
  intro: GuideIntroSchema.optional()
});

// src/nodes/equipment-attribute.node-type.ts
import * as z14 from "zod";
var EquipmentAttributeDataSchema = CommonNodeDataSchema.extend({
  type: z14.literal("equipment-attribute"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema,
  fieldDataKey: DataKeySchema,
  // Defaulted so a select field, which hides the form field, can't fail validation on it.
  buttonLabel: z14.string().min(1).default("Continue"),
  optional: z14.boolean(),
  helpType: HelpTypeSchema.default("none")
});

// src/nodes/equipment-list.node-type.ts
import * as z15 from "zod";
var EquipmentListDataSchema = z15.object({
  pageTitle: z15.string().min(2).max(30),
  type: z15.literal("equipment-list"),
  grouping: z15.enum(["none", "by-room"]),
  emptyText: z15.string(),
  addLabel: z15.string(),
  addFirstLabel: z15.string().optional(),
  doneLabel: z15.string(),
  onlyEquipmentTypeId: EquipmentTypeIdOrContextSchema.optional()
});

// src/nodes/equipment.node-type.ts
import * as z16 from "zod";
var FieldDisplayAllSchema = z16.object({
  display: z16.literal("all-fields")
});
var FieldDisplayCoreOnlySchema = z16.object({
  display: z16.literal("core-only")
});
var FieldDisplayCustomOnlySchema = z16.object({
  display: z16.literal("custom-only")
});
var FieldDisplayShowExceptSchema = z16.object({
  display: z16.literal("show-except"),
  hiddenFields: z16.array(z16.object({ dataKey: CodeWordSchema }))
});
var FieldDisplayHideExceptSchema = z16.object({
  display: z16.literal("hide-except"),
  shownFields: z16.array(z16.object({ dataKey: CodeWordSchema }))
});
var FieldDisplaySchema = z16.discriminatedUnion("display", [
  FieldDisplayAllSchema,
  FieldDisplayCoreOnlySchema,
  FieldDisplayCustomOnlySchema,
  FieldDisplayShowExceptSchema,
  FieldDisplayHideExceptSchema
]);
var EquipmentNodeDataSchema = CommonNodeDataSchema.extend({
  type: z16.literal("equipment"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema,
  buttonLabel: z16.string().min(1),
  fieldDisplay: FieldDisplaySchema
});

// src/nodes/exit.node-type.ts
import * as z17 from "zod";
var GuideWrapUpSchema = z17.enum(["summary", "skip"]);
var DEFAULT_GUIDE_WRAP_UP = "summary";
var ExitDataSchema = z17.object({
  type: z17.literal("exit"),
  wrapUp: GuideWrapUpSchema.optional()
});

// src/nodes/if.node-type.ts
import * as z18 from "zod";
var IfTransformFnSchema = z18.enum(["years-since", "months-since", "days-since"]);
var IfOpSchema = z18.enum(["==", "!=", ">", ">=", "<", "<="]);
var IfDataSchema = z18.object({
  type: z18.literal("if"),
  field: z18.string(),
  // Using string instead of DataKeySchema to support variable substitution
  fn: IfTransformFnSchema.optional(),
  op: IfOpSchema,
  value: z18.string()
});

// src/nodes/image.node-type.ts
import * as z19 from "zod";
var ImageDataSchema = CommonNodeDataSchema.extend({
  type: z19.literal("image"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema
});

// src/nodes/info.node-type.ts
import * as z20 from "zod";
var InfoDataSchema = CommonNodeDataSchema.extend({
  type: z20.literal("info"),
  buttonLabel: z20.string().min(1)
});

// src/nodes/initial-rooms.node-type.ts
import * as z22 from "zod";

// src/room.types.ts
import { keyBy as keyBy2 } from "lodash-es";
import z21 from "zod";
var RoomTypeSchema = z21.enum([
  "attic",
  "bathroom",
  "bedroom",
  "den",
  "dining",
  "family",
  "foyer",
  "garage",
  "hallway",
  "kitchen",
  "laundry",
  "living",
  "lounge",
  "office",
  "playroom",
  "pool",
  "utility",
  "workout",
  "external",
  "outside",
  "other"
]);
var RoomGroupIdSchema = z21.enum([
  "living-spaces",
  "kitchen-dining",
  "laundry",
  "bedrooms",
  "bathrooms",
  "garage-outdoor",
  "other"
]);
var RoomGroups = [
  {
    id: "living-spaces",
    label: "Living Spaces",
    types: ["living", "den", "family", "playroom", "lounge"],
    iconType: "living"
  },
  {
    id: "kitchen-dining",
    label: "Kitchen / Dining",
    types: ["kitchen", "dining"],
    iconType: "dining"
  },
  {
    id: "laundry",
    label: "Laundry / Utility",
    types: ["laundry", "utility"],
    iconType: "laundry"
  },
  {
    id: "bedrooms",
    label: "Bedrooms",
    types: ["bedroom"],
    iconType: "bedroom"
  },
  {
    id: "bathrooms",
    label: "Bathrooms",
    types: ["bathroom"],
    iconType: "bathroom"
  },
  {
    id: "garage-outdoor",
    label: "Garage / Outdoor",
    types: ["garage", "outside", "external", "pool"],
    iconType: "outside"
  },
  {
    id: "other",
    label: "Other",
    types: ["office", "workout", "foyer", "hallway", "attic", "other"],
    iconType: "foyer"
  }
];
var RoomGroupsById = keyBy2(RoomGroups, "id");
var ROOM_FLOOR_BASEMENT = -1;
var ROOM_FLOOR_EXTERNAL_OTHER = 0;
var RoomSchema = z21.object({
  object: z21.literal("room"),
  schema: z21.literal(1),
  id: RoomIdSchema,
  type: RoomTypeSchema,
  floor: z21.number().optional(),
  // -1 = Basement, 0 = External / Other, 1 = First Floor, 2 = Second Floor, ...
  active: z21.boolean(),
  homeId: HomeIdSchema,
  name: z21.string(),
  image: z21.string().optional(),
  // The stock photo assigned at room creation (cycled from the shared `rooms/` library). Retained so a
  // user-customized room can revert to it, and so we can tell a stock photo (no enhancement offered)
  // from a user-provided one (`image === stockImage` ⇒ still stock).
  stockImage: z21.string().optional(),
  // When set, `image` is an accepted AI enhancement and this points to the pre-enhancement original.
  // Drives non-destructive revert AND gates re-enhancement (an already-enhanced room can't be
  // re-enhanced — revert first). Cleared whenever a fresh photo replaces `image`.
  originalImage: z21.string().optional(),
  // Stamped at creation; backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional(),
  createdBy: UserIdSchema.optional()
});
var RoomTypeLabels = {
  [RoomTypeSchema.enum.attic]: "Attic",
  [RoomTypeSchema.enum.bathroom]: "Bathroom",
  [RoomTypeSchema.enum.bedroom]: "Bedroom",
  [RoomTypeSchema.enum.den]: "Den",
  [RoomTypeSchema.enum.dining]: "Dining Room",
  [RoomTypeSchema.enum.family]: "Family Room",
  [RoomTypeSchema.enum.foyer]: "Foyer",
  [RoomTypeSchema.enum.garage]: "Garage",
  [RoomTypeSchema.enum.hallway]: "Hallway",
  [RoomTypeSchema.enum.kitchen]: "Kitchen",
  [RoomTypeSchema.enum.laundry]: "Laundry Room",
  [RoomTypeSchema.enum.living]: "Living Room",
  [RoomTypeSchema.enum.lounge]: "Lounge",
  [RoomTypeSchema.enum.office]: "Office",
  [RoomTypeSchema.enum.playroom]: "Playroom",
  [RoomTypeSchema.enum.pool]: "Pool",
  [RoomTypeSchema.enum.utility]: "Utility Room",
  [RoomTypeSchema.enum.workout]: "Workout Room",
  [RoomTypeSchema.enum.external]: "External / Outside",
  [RoomTypeSchema.enum.outside]: "Outside",
  [RoomTypeSchema.enum.other]: "Other"
};

// src/nodes/initial-rooms.node-type.ts
var InitialRoomsPreselectSchema = z22.object({
  dataKey: DataKeySchema,
  values: z22.array(z22.string().min(1)).min(1),
  roomType: RoomTypeSchema
});
var InitialRoomsDefaultSchema = z22.object({
  roomType: RoomTypeSchema,
  count: z22.number().int().min(1)
});
var InitialRoomsDataSchema = CommonNodeDataSchema.extend({
  type: z22.literal("initial-rooms"),
  floorsDataKey: DataKeySchema,
  hasBasementDataKey: DataKeySchema,
  defaultRooms: z22.array(InitialRoomsDefaultSchema).default([]),
  preselectRooms: z22.array(InitialRoomsPreselectSchema).default([]),
  inputMode: z22.enum(["simple", "name-and-floor"]).default("simple"),
  doneLabel: z22.string()
});

// src/nodes/input.node-type.ts
import * as z23 from "zod";
var InputDataSchema = CommonNodeDataSchema.extend({
  type: z23.literal("input"),
  dataKey: DataKeySchema,
  placeholder: z23.string().optional(),
  infoTypeId: InfoTypeIdSchema,
  optional: z23.boolean(),
  // Defaulted so a select info type, which hides the field, can't fail validation on it.
  buttonLabel: z23.string().min(1).default("Continue"),
  helpType: HelpTypeSchema.default("none")
});

// src/nodes/return.node-type.ts
import * as z24 from "zod";
var ReturnDataSchema = z24.object({
  type: z24.literal("return"),
  returnType: z24.enum(["count", "edge"]).default("count"),
  dismissCount: z24.number().optional()
});

// src/nodes/room.node-type.ts
import * as z25 from "zod";
var RoomDataSchema = CommonNodeDataSchema.extend({
  type: z25.literal("room"),
  // Source of the fallback roomGroupHint / autoSelectRoomType. Defaults to the current guide context at runtime.
  // Resolved to the sub-guide's equipment type when this node is injected from a sub-guide.
  equipmentTypeId: EquipmentTypeIdOrContextSchema.optional(),
  roomGroupHint: RoomGroupIdSchema.optional(),
  autoSelectRoomType: RoomTypeSchema.optional()
});

// src/nodes/room-starter-selection.node-type.ts
import * as z26 from "zod";
var RoomStarterSelectionDataSchema = CommonNodeDataSchema.extend({
  type: z26.literal("room-starter-selection"),
  /** Where the selected room ids (RoomId[]) live in the guide values. */
  dataKey: DataKeySchema,
  /** Room TYPES whose existing rooms start checked (e.g. kitchen-dining + laundry for appliances). */
  preselectRoomTypes: z26.array(RoomTypeSchema).default([]),
  doneLabel: z26.string()
});

// src/nodes/scan.node-type.ts
import * as z27 from "zod";
var ScanDataSchema = z27.object({
  type: z27.literal("scan"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema
});

// src/nodes/select.node-type.ts
import * as z28 from "zod";
var SelectDataSchema = CommonNodeDataSchema.extend({
  type: z28.literal("select"),
  dataKey: DataKeySchema,
  infoTypeId: InfoTypeIdSchema.optional(),
  // Every node that offers help defaults to 'none'; the 20260914 patch stamped the value each
  // existing node was already behaving as, so absence no longer carries a per-node meaning.
  helpType: HelpTypeSchema.default("none"),
  modelHelpEquipmentTypeId: EquipmentTypeIdOrContextSchema.optional(),
  multi: z28.object({
    enabled: z28.boolean().default(false),
    buttonLabel: z28.string().optional()
  }).optional(),
  sections: z28.array(
    z28.object({
      heading: z28.string().max(50).optional(),
      options: z28.array(
        z28.object({
          value: z28.string().min(1).max(50),
          title: z28.string().min(1).max(50),
          subtitle: z28.string().max(100).optional(),
          bgImage: z28.string().url().optional(),
          equipmentTypeId: EquipmentTypeIdSchema.optional(),
          defaultSelected: z28.boolean().default(false)
        })
      ).min(1, "At least one option is required")
    })
  ).min(1, "At least one section is required")
}).superRefine((data, ctx) => {
  data.sections.forEach((section, sectionIndex) => {
    const values = section.options.map((opt) => opt.value);
    const duplicates = values.filter((v, idx) => values.indexOf(v) !== idx);
    if (duplicates.length) {
      ctx.addIssue({
        path: ["sections", sectionIndex, "options"],
        message: "Option values must be unique within each section",
        code: z28.ZodIssueCode.custom
      });
    }
  });
});
function getMultiSelectDefaultValues(data) {
  if (!data.multi?.enabled) {
    return [];
  }
  return data.sections.flatMap(
    (section) => section.options.filter((option) => option.defaultSelected).map((option) => option.value)
  );
}

// src/nodes/set.node-type.ts
import * as z29 from "zod";
var SetDataSchema = z29.object({
  type: z29.literal("set"),
  field: DataKeySchema,
  value: z29.string()
});

// src/nodes/sub-guide.node-type.ts
import * as z30 from "zod";
var SubGuideDataSchema = z30.object({
  type: z30.literal("sub-guide"),
  equipmentTypeId: EquipmentTypeIdSchema,
  scopeKey: DataKeySchema
});

// src/nodes/yes-no.node-type.ts
import * as z31 from "zod";
var YesNoDataSchema = CommonNodeDataSchema.extend({
  type: z31.literal("yes-no"),
  dataKey: DataKeySchema,
  yesLabel: z31.string().min(1),
  noLabel: z31.string().min(1),
  noFirst: z31.boolean().optional(),
  infoTypeId: InfoTypeIdSchema.optional(),
  forceSelection: z31.boolean().optional(),
  helpType: HelpTypeSchema.default("none"),
  equipmentTypeId: EquipmentTypeIdOrContextSchema.optional()
});

// src/nodes/index.ts
var schemas2 = [
  AddressLookupDataSchema,
  CreateEquipmentDataSchema,
  EntryDataSchema,
  EquipmentAttributeDataSchema,
  EquipmentNodeDataSchema,
  EquipmentListDataSchema,
  ExitDataSchema,
  IfDataSchema,
  ImageDataSchema,
  InfoDataSchema,
  InitialRoomsDataSchema,
  InputDataSchema,
  ReturnDataSchema,
  RoomDataSchema,
  RoomStarterSelectionDataSchema,
  SelectDataSchema,
  ScanDataSchema,
  SetDataSchema,
  SubGuideDataSchema,
  YesNoDataSchema
];
var nodeDataSchemas = keyBy3(schemas2, (s) => s.shape.type.value);
var AppNodeDataSchema = z32.discriminatedUnion("type", schemas2);
function infoBindingForNode(data) {
  switch (data.type) {
    case "input":
    case "select":
    case "yes-no":
    case "address-lookup":
      return data.infoTypeId ? { infoTypeId: data.infoTypeId, dataKey: data.dataKey } : void 0;
    default:
      return void 0;
  }
}
var NODE_GUIDE_TYPES = {
  "address-lookup": ["space"],
  "initial-rooms": ["space"],
  "create-equipment": ["equipment"],
  equipment: ["equipment"],
  "equipment-attribute": ["equipment"],
  "equipment-list": ["equipment"],
  image: ["equipment"],
  scan: ["equipment"],
  room: ["equipment"],
  "room-starter-selection": ["space"],
  "sub-guide": ["equipment"],
  entry: ["space", "equipment"],
  exit: ["space", "equipment"],
  return: ["space", "equipment"],
  if: ["space", "equipment"],
  set: ["space", "equipment"],
  info: ["space", "equipment"],
  input: ["space", "equipment"],
  select: ["space", "equipment"],
  "yes-no": ["space", "equipment"]
};
function isNodeAllowedInGuide(nodeType, guideType) {
  return NODE_GUIDE_TYPES[nodeType].includes(guideType);
}
function getAllowedNodeTypes(guideType) {
  return Object.keys(NODE_GUIDE_TYPES).filter(
    (nodeType) => NODE_GUIDE_TYPES[nodeType].includes(guideType)
  );
}

// src/app.types.ts
import z37 from "zod";

// src/equipment_type.types.ts
import * as z35 from "zod";

// src/commerce.types.ts
import * as z33 from "zod";
var RetailerIdSchema = z33.enum(["amazon", "homeDepot", "lowes"]);
var RETAILER_IDS = RetailerIdSchema.options;
var DEFAULT_RETAILER = "amazon";

// src/field.types.ts
import * as z34 from "zod";
var FieldDataTypeSchema = z34.enum([
  "text",
  "number",
  "boolean",
  "model-number",
  "serial-number",
  "brand",
  "room-id",
  "dimensions",
  "year",
  "month-year",
  "date",
  "image",
  "quantity",
  "address",
  "select"
]);
var LengthUnitSchema = z34.enum(["ft", "in", "cm", "m"]);
var DimensionsSubTypeSchema = z34.enum(["length", "sqft", "LxW", "LxWxH"]);
function isNumericDataType(dataType) {
  return dataType === "number" || dataType === "year" || dataType === "quantity";
}
function isScannableCodeType(dataType) {
  return dataType === "model-number" || dataType === "serial-number";
}
var SelectOptionSchema = z34.object({
  value: z34.string().min(1).max(50),
  title: z34.string().min(1).max(50),
  subtitle: z34.string().max(100).optional(),
  bgImage: z34.string().url().optional()
});
function isSelectField(field) {
  return field.dataType === "select" && !!field.options?.length;
}
var FieldMixinSchema = z34.object({
  dataType: FieldDataTypeSchema,
  defaultLengthSelection: LengthUnitSchema.optional(),
  dimensionsSubType: DimensionsSubTypeSchema.optional(),
  renderCalculatedValue: z34.boolean().optional(),
  options: z34.array(SelectOptionSchema).optional()
});
var InputFieldSchema = z34.object({
  dataKey: DataKeySchema,
  label: z34.string(),
  helpText: z34.string().optional(),
  ...FieldMixinSchema.shape,
  defaultLengthSelection: LengthUnitSchema.default("ft")
});

// src/equipment_type.types.ts
var EquipmentTypeScopeSchema = z35.enum(["normal", "system", "component", "supplies"]);
var EQUIPMENT_CORE_FIELD_KEYS = {
  MODEL_NUMBER: "modelNumber",
  /** The name a product was sold under — the identity observation for a product with no model number. */
  PRODUCT_NAME: "productName",
  SERIAL_NUMBER: "serialNumber",
  /** The manufacture date or date code exactly as printed on the rating plate (`03/2014`, `200561`). */
  DATE_CODE: "dateCode",
  ROOM_ID: "roomId",
  BRAND: "brand",
  QUANTITY: "quantity"
};
var EquipmentSpecSchema = z35.object({
  code: CodeWordSchema,
  label: z35.string(),
  description: z35.string(),
  confirmed: z35.boolean()
});
var PurchaseLinkSchema = z35.discriminatedUnion("kind", [
  z35.object({
    provider: RetailerIdSchema,
    kind: z35.literal("search"),
    query: z35.string().min(1)
  }),
  z35.object({
    provider: RetailerIdSchema,
    kind: z35.literal("direct"),
    // Require http(s): `z.string().url()` alone also accepts javascript:/data:/etc., and this URL
    // is handed straight to the OS opener (Linking.openURL) on the client.
    url: z35.string().url().refine((u) => /^https?:\/\//i.test(u), "Must be an http(s) URL")
  })
]);
var EquipmentTypeSchemaV1 = z35.object({
  object: z35.literal("equipment-type"),
  schema: z35.literal(1),
  id: EquipmentTypeIdSchema,
  spaceId: SpaceIdSchema,
  title: z35.string().min(1),
  shortTitle: z35.string().optional(),
  subtitle: z35.string().optional(),
  icon: z35.string().optional(),
  group: z35.string(),
  order: z35.number(),
  scope: EquipmentTypeScopeSchema.default("normal"),
  image: ImageSchema.optional(),
  specs: z35.array(EquipmentSpecSchema).optional(),
  inputFields: z35.array(InputFieldSchema).optional(),
  systemParts: z35.array(
    z35.object({
      typeId: EquipmentTypeIdSchema
    })
  ).optional()
});
var EquipmentTypeSchemaV2 = z35.object({
  ...EquipmentTypeSchemaV1.omit({ image: true }).shape,
  schema: z35.literal(2),
  images: z35.array(ImageSchema).optional(),
  roomGroupHint: RoomGroupIdSchema.optional(),
  autoSelectRoomType: RoomTypeSchema.optional(),
  purchaseLinks: z35.array(PurchaseLinkSchema).refine((links) => new Set(links.map((l) => l.provider)).size === links.length, "One link per provider max").optional(),
  /**
   * Set when an admin retired the type. A retired type still resolves by id — existing equipment,
   * models, manuals and recalls keep working — but `isOfferableType` keeps it out of every place a
   * new type is chosen. Live-only: publish never takes it from a draft.
   */
  retiredAt: TimestampSchema.optional(),
  /** The type a retire moved this type's equipment onto. Live-only, like `retiredAt`. */
  replacedBy: EquipmentTypeIdSchema.optional(),
  /**
   * Present (and always `true`) only on copies living in the `_draft/db` mirror — the admin
   * working copy awaiting publish. Stamped/stripped by DraftLayer choke points (never by hand);
   * MUST be declared here or parsing strips it on read-modify-write. Live docs never carry it,
   * and `computeDraftStatus` ignores it unconditionally.
   */
  _draft: z35.literal(true).optional()
});
function isEquipmentTypeV1(val) {
  return isObjectSchema(val, "equipment-type", 1);
}
function isEquipmentTypeV2(val) {
  return isObjectSchema(val, "equipment-type", 2);
}
function migrateEquipmentTypeV1ToV2(val) {
  return {
    ...val,
    images: val.image ? [val.image] : [],
    schema: 2
  };
}
var EquipmentTypeSchema = z35.preprocess(
  (val, ctx) => {
    if (isEquipmentTypeV2(val)) {
      return val;
    }
    if (isEquipmentTypeV1(val)) {
      return migrateEquipmentTypeV1ToV2(val);
    }
    if (isObjectType(val, "equipment-type")) {
      return { ...val, schema: 2 };
    }
    ctx.addIssue({
      code: "custom",
      message: "Object is not an equipment type",
      input: val
    });
    return z35.NEVER;
  },
  EquipmentTypeSchemaV2
);
function isOfferableType(type) {
  return !type.retiredAt && !type.replacedBy;
}
var CustomEquipmentTypeSchema = EquipmentTypeSchemaV2.extend({
  object: z35.literal("custom-equipment-type"),
  schema: z35.literal(1),
  homeId: HomeIdSchema,
  createdBy: UserIdSchema,
  // Links this home-scoped custom to a cross-home candidate so admins can see which
  // suggestions are popular. Optional only because customs created before candidate
  // tracking shipped won't have it.
  candidateId: EquipmentTypeCandidateIdSchema.optional(),
  // Set when an admin remapped this custom onto a catalog type (`remapEquipmentType`): the home's
  // equipment now lives on that type and this doc is a tombstone — stale references still resolve
  // by id, but the custom is no longer offered anywhere new equipment picks a type.
  replacedBy: EquipmentTypeIdSchema.optional(),
  // What the lookup knew when the home made this custom, which `onCustomEquipmentTypeCreated` records
  // on the candidate: the user's own words, and the AI's shared-or-custom call for a new candidate.
  sighting: z35.object({
    description: z35.string().optional(),
    aiClassification: z35.enum(["shared", "custom"]).optional()
  }).optional()
});
var EquipmentTypeCandidateSchema = z35.object({
  object: z35.literal("equipment-type-candidate"),
  schema: z35.literal(1),
  id: EquipmentTypeCandidateIdSchema,
  spaceId: SpaceIdSchema,
  title: z35.string().min(1),
  shortTitle: z35.string().optional(),
  subtitle: z35.string().optional(),
  icon: z35.string().optional(),
  group: z35.string(),
  scope: EquipmentTypeScopeSchema.default("normal"),
  // The AI's recommendation when first seen: 'shared' = broadly useful (catalog candidate),
  // 'custom' = personal/niche. Informational for admin triage.
  aiClassification: z35.enum(["shared", "custom"]),
  // Distinct homes that have a custom linked to this candidate (deduped). Default [] for
  // candidates created before home tracking shipped.
  homeIds: z35.array(HomeIdSchema).default([]),
  // Demand signal, denormalized for sorting: always === homeIds.length.
  count: z35.number().int().nonnegative(),
  // User descriptions that produced this candidate, append-only (unbounded for now).
  sampleDescriptions: z35.array(z35.string()),
  firstSeenAt: TimestampSchema,
  lastSeenAt: TimestampSchema,
  // The catalog type an admin pointed this candidate at, from the moment the promotion starts —
  // the type may still be an unpublished draft. Purely an admin bookkeeping pointer: it tracks
  // where the promotion is headed so the candidate page can link to (and remap onto) that type.
  // AI matching ignores it, so a candidate whose type is still a draft keeps collecting sightings.
  promotionTypeId: EquipmentTypeIdSchema.optional(),
  // The catalog type this demand ended up served by — stamped when the last linked custom is
  // remapped (whether the type was newly promoted or already existed). The candidate stays for the
  // demand history; admin lists can filter on it.
  promotedTo: EquipmentTypeIdSchema.optional()
});
var RemapEquipmentTypeRequestSchema = z35.object({
  homeId: HomeIdSchema,
  /** The home-scoped `c:{cuid}` type being retired. */
  customTypeId: EquipmentTypeIdSchema,
  /** The catalog type the equipment (and its models) should live on. */
  targetTypeId: EquipmentTypeIdSchema
});
var RemapEquipmentTypeResponseSchema = z35.object({
  remappedEquipment: z35.number().int().nonnegative(),
  /** Models whose `typeId` flipped to the target, in place — model ids are opaque, so nothing re-keys. */
  retypedModels: z35.number().int().nonnegative(),
  /** Published documents filed under the custom type, re-filed under the target. */
  remappedDocuments: z35.number().int().nonnegative(),
  /** True when this call consumed the candidate's last remaining custom. */
  candidateMarked: z35.boolean()
});
var RetireEquipmentTypeRequestSchema = z35.object({
  typeId: EquipmentTypeIdSchema,
  /**
   * Same space and scope as `typeId`, live and not retired. Absent = retire only. Nullish because the
   * callable client sends an `undefined` value as `null`.
   */
  targetTypeId: EquipmentTypeIdSchema.nullish(),
  dryRun: z35.boolean()
});
var RetiredTypeReferenceKindSchema = z35.enum([
  "consideration-type",
  "lesson-type",
  "custom-reminder",
  "system-part",
  "guide",
  "test-library",
  "core-field",
  "task-supplies"
]);
var RetiredTypeReferenceSchema = z35.object({
  kind: RetiredTypeReferenceKindSchema,
  id: z35.string(),
  title: z35.string(),
  /** The space a guide reference lives in, for linking. */
  spaceId: SpaceIdSchema.optional()
});
var RetireMoveCountsSchema = z35.object({
  equipment: z35.number().int().nonnegative(),
  models: z35.number().int().nonnegative(),
  documents: z35.number().int().nonnegative(),
  recalls: z35.number().int().nonnegative(),
  starters: z35.number().int().nonnegative()
});
var RetireEquipmentTypeUsageSchema = z35.object({
  /** What a move would carry to the target (and what stays on the type when retiring only). */
  movable: RetireMoveCountsSchema,
  /** Distinct homes holding equipment of the type. */
  homes: z35.number().int().nonnegative(),
  references: z35.array(RetiredTypeReferenceSchema),
  /**
   * Approved applies-to queries naming the type. A move is refused while any remains: the moved
   * equipment no longer matches them, so homes would lose the reminders and suggestions they give them.
   */
  moveBlockedBy: z35.array(RetiredTypeReferenceSchema)
});
function unmatchedInputFields(source, target) {
  const targetKeys = new Set(target.inputFields?.map((field) => field.dataKey));
  return (source.inputFields ?? []).filter((field) => !targetKeys.has(field.dataKey)).map(({ dataKey, label }) => ({ dataKey, label }));
}
var RetireEquipmentTypeResponseSchema = z35.object({
  usage: RetireEquipmentTypeUsageSchema,
  /** Absent on a dry run. All zero on a retire without a target. */
  moved: RetireMoveCountsSchema.optional()
});
var RestoreEquipmentTypeRequestSchema = z35.object({ typeId: EquipmentTypeIdSchema });
var RestoreEquipmentTypeResponseSchema = z35.object({
  reopenedStarters: z35.number().int().nonnegative()
});
var DisplayableEquipmentTypeSchema = z35.union([EquipmentTypeSchema, CustomEquipmentTypeSchema]);

// src/task_screen.types.ts
import * as z36 from "zod";
var TaskScreenPictureSlotCategorySchema = z36.enum(["before", "after", "other"]);
var TaskScreenPictureSlotObjectSchema = z36.object({
  category: TaskScreenPictureSlotCategorySchema,
  title: z36.string()
});
var TaskScreenPictureSlotSchema = z36.preprocess((val) => {
  if (typeof val !== "object" || val === null) return val;
  const out = val;
  return out.title === void 0 && typeof out.label === "string" ? { ...out, title: out.label } : out;
}, TaskScreenPictureSlotObjectSchema);
var SuppliesEquipmentConfigSchema = z36.object({
  equipmentTypeId: EquipmentTypeIdSchema,
  quantityPerTask: z36.number().default(1)
});
var TaskScreenSectionInstructionsSchema = z36.object({
  type: z36.literal("instructions"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional()
});
var TaskScreenSectionHistorySchema = z36.object({
  type: z36.literal("history"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional()
});
var TaskScreenSectionPicturesSchema = z36.object({
  type: z36.literal("pictures"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional(),
  slots: z36.array(TaskScreenPictureSlotSchema)
});
var TaskScreenSectionNotesSchema = z36.object({
  type: z36.literal("notes"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional()
});
var TaskScreenSectionContactSchema = z36.object({
  type: z36.literal("contact"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional()
});
var TaskScreenSectionSuppliesSchema = z36.object({
  type: z36.literal("supplies"),
  enabled: z36.boolean(),
  title: z36.string().optional(),
  description: z36.string().optional(),
  suppliesConfig: z36.array(SuppliesEquipmentConfigSchema)
});
var TaskScreenSectionSchema = z36.discriminatedUnion("type", [
  TaskScreenSectionInstructionsSchema,
  TaskScreenSectionHistorySchema,
  TaskScreenSectionPicturesSchema,
  TaskScreenSectionNotesSchema,
  TaskScreenSectionSuppliesSchema,
  TaskScreenSectionContactSchema
]);
var TaskScreenConfigSchema = z36.object({
  sections: z36.array(TaskScreenSectionSchema)
});
var AppTaskScreenSectionInstructionsSchema = TaskScreenSectionInstructionsSchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionHistorySchema = TaskScreenSectionHistorySchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionPicturesSchema = TaskScreenSectionPicturesSchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionNotesSchema = TaskScreenSectionNotesSchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionSuppliesSchema = TaskScreenSectionSuppliesSchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionContactSchema = TaskScreenSectionContactSchema.extend({
  defaultEnabled: z36.boolean()
});
var AppTaskScreenSectionSchema = z36.discriminatedUnion("type", [
  AppTaskScreenSectionInstructionsSchema,
  AppTaskScreenSectionHistorySchema,
  AppTaskScreenSectionPicturesSchema,
  AppTaskScreenSectionNotesSchema,
  AppTaskScreenSectionSuppliesSchema,
  AppTaskScreenSectionContactSchema
]);
var TaskTypeScreenSectionInstructionsSchema = TaskScreenSectionInstructionsSchema.extend({
  useDefault: z36.boolean()
});
var TaskTypeScreenSectionHistorySchema = TaskScreenSectionHistorySchema.extend({
  useDefault: z36.boolean()
});
var TaskTypeScreenSectionPicturesSchema = TaskScreenSectionPicturesSchema.extend({
  useDefault: z36.boolean(),
  slots: z36.array(TaskScreenPictureSlotSchema).optional()
});
var TaskTypeScreenSectionNotesSchema = TaskScreenSectionNotesSchema.extend({
  useDefault: z36.boolean()
});
var TaskTypeScreenSectionSuppliesSchema = TaskScreenSectionSuppliesSchema.extend({
  useDefault: z36.boolean(),
  suppliesConfig: z36.array(SuppliesEquipmentConfigSchema).optional()
});
var TaskTypeScreenSectionContactSchema = TaskScreenSectionContactSchema.extend({
  useDefault: z36.boolean()
});
var TaskTypeScreenSectionSchema = z36.discriminatedUnion("type", [
  TaskTypeScreenSectionInstructionsSchema,
  TaskTypeScreenSectionHistorySchema,
  TaskTypeScreenSectionPicturesSchema,
  TaskTypeScreenSectionNotesSchema,
  TaskTypeScreenSectionSuppliesSchema,
  TaskTypeScreenSectionContactSchema
]);
var TaskTypeScreenConfigSchema = z36.object({
  sections: z36.array(TaskTypeScreenSectionSchema)
});
var UserTaskScreenSectionSchema = z36.object({
  type: z36.string(),
  enabled: z36.boolean().optional()
});
var UserTaskScreenConfigSchema = z36.object({
  object: z36.literal("user-task-screen-config"),
  schema: z36.literal(1),
  id: TaskTypeIdSchema,
  userId: UserIdSchema,
  sections: z36.array(UserTaskScreenSectionSchema).default([]),
  updatedAt: TimestampSchema
});

// src/app.types.ts
var CoreFieldSchema = InputFieldSchema.extend({
  equipmentScopes: z37.array(EquipmentTypeScopeSchema).optional(),
  /**
   * The catalog types the field is offered for; absent = every type in scope. A field a recall check
   * wrote on a type outside the list is still shown (`fieldsForEquipment`), never asked for.
   */
  equipmentTypeIds: z37.array(EquipmentTypeIdSchema).optional(),
  /**
   * Whether the field is live for users. Defaults to `true`, so the fields that predate the flag
   * carry no key and keep rendering. A field stored `enabled: false` stays registered and visible
   * in rock-admin but reaches no form or detail row — which is how a new core field ships dark and
   * is turned on with one toggle on the Core Fields page instead of a migration.
   *
   * Only builds that filter through `visibleCoreFields` honor it; an older app renders whatever is
   * registered, so registering a disabled field still shows it to homes that haven't upgraded.
   */
  enabled: z37.boolean().default(true)
});
var AppEquipmentConfigSchema = z37.object({
  object: z37.literal("app-equipment-config"),
  schema: z37.literal(1),
  id: z37.literal("equipment-config"),
  coreFields: z37.array(CoreFieldSchema)
});
var AppTaskScreenConfigSchema = z37.object({
  object: z37.literal("app-task-screen-config"),
  schema: z37.literal(1),
  id: z37.literal("task-screen-config"),
  sections: z37.array(AppTaskScreenSectionSchema)
});

// src/appStatus.types.ts
import * as z38 from "zod";
var AppStatusSeveritySchema = z38.enum(["info", "warning", "critical"]);
var AppStatusAlertSchema = z38.object({
  object: z38.literal("app-status-alert"),
  schema: z38.literal(1),
  id: z38.literal("alert"),
  enabled: z38.boolean().nullish(),
  message: z38.string().nullish(),
  /** Banner color; the app defaults to 'warning' when unset. */
  severity: AppStatusSeveritySchema.nullish(),
  updatedAt: TimestampSchema,
  _draft: z38.literal(true).optional()
});
var AppStatusRequiredUpgradeSchema = z38.object({
  object: z38.literal("app-status-required-upgrade"),
  schema: z38.literal(1),
  id: z38.literal("required-upgrade"),
  enabled: z38.boolean().nullish(),
  /** Minimum allowed native app version, dotted-numeric ("0.21.1"). Unparseable → gate no-ops. */
  minVersion: z38.string().nullish(),
  /** Shown on the blocking screen; blank → the app's default copy. */
  message: z38.string().nullish(),
  updatedAt: TimestampSchema,
  _draft: z38.literal(true).optional()
});
var AppStatusEasUpdateSchema = z38.object({
  object: z38.literal("app-status-eas-update"),
  schema: z38.literal(1),
  id: z38.literal("eas-update"),
  enabled: z38.boolean().nullish(),
  updatedAt: TimestampSchema,
  _draft: z38.literal(true).optional()
});
var AppStatusMaintenanceSchema = z38.object({
  object: z38.literal("app-status-maintenance"),
  schema: z38.literal(1),
  id: z38.literal("maintenance"),
  enabled: z38.boolean().nullish(),
  /** Shown on the blocking screen; blank → the app's default copy. */
  message: z38.string().nullish(),
  updatedAt: TimestampSchema,
  _draft: z38.literal(true).optional()
});
var AppStatusStoreUrlsSchema = z38.object({
  object: z38.literal("app-status-store-urls"),
  schema: z38.literal(1),
  id: z38.literal("store-urls"),
  ios: z38.string().nullish(),
  android: z38.string().nullish(),
  updatedAt: TimestampSchema,
  _draft: z38.literal(true).optional()
});
var AppStatusDocKindSchema = z38.enum(["alert", "required-upgrade", "eas-update", "maintenance", "store-urls"]);
var APP_STATUS_DOC_SCHEMAS = {
  alert: AppStatusAlertSchema,
  "required-upgrade": AppStatusRequiredUpgradeSchema,
  "eas-update": AppStatusEasUpdateSchema,
  maintenance: AppStatusMaintenanceSchema,
  "store-urls": AppStatusStoreUrlsSchema
};

// src/auth_profile.types.ts
import * as z39 from "zod";
var LookupAuthProfileRequestSchema = z39.object({ userId: UserIdSchema });
var AuthProfileSchema = z39.object({
  name: z39.string().nullish(),
  email: z39.string().nullish(),
  imageUrl: z39.string().nullish()
});

// src/brand.types.ts
import * as z40 from "zod";
var BrandLogoImageSchema = ImageObjectSchema.extend({
  // Where the bytes came from, and nothing else — an unknown source reads as absent rather than
  // failing the brand (and with it the app's logo) on a reader that predates the value. It carries no
  // authority: whether enrichment may replace this asset is `pinned`'s question, not this one. The
  // retired `'manual'` value conflated the two, which is why it answered neither — it overwrote the
  // real origin of every admin pick (see the 20260915 patch, which reads those as `'web-search'`).
  source: z40.enum(["brandfetch", "web-search", "upload"]).optional().catch(void 0),
  /**
   * A human chose this asset for this slot, so automated enrichment must leave it alone (see
   * `isPinned` in rock-functions' brand enrichment). Same sense as a pinned brand domain: it survives
   * the next refetch, and only an admin can undo it.
   */
  pinned: z40.boolean().optional(),
  transparent: z40.boolean().optional(),
  svgUrl: z40.string().optional(),
  /**
   * Which wordmark derivation rules made this rendition (`WORDMARK_DERIVATION_REVISION` in rock-functions'
   * rehost). A wordmark with an older or missing revision is derived again; only the wordmark slots read it.
   */
  derivationRevision: z40.number().int().optional()
});
var BrandfetchInfoSchema = z40.object({
  domain: z40.string(),
  fetchedAt: TimestampSchema
});
var BrandfetchLogoFormatSchema = z40.looseObject({
  src: z40.string(),
  format: z40.string().nullish(),
  width: z40.number().nullish(),
  height: z40.number().nullish(),
  /** `"transparent"` or a hex background, or null — echoed onto the picked asset's `transparent`. */
  background: z40.string().nullish(),
  size: z40.number().nullish()
});
var BrandfetchLogoSchema = z40.looseObject({
  /** `logo` | `icon` | `symbol` | `other`. */
  type: z40.string().nullish(),
  /**
   * `light` | `dark` | null — Brandfetch's tag for the artwork variant. A lossy hint (the two `null`
   * lockups A.O. Smith ships are a dark-ink one AND a paint-anywhere mark), so it only seeds the
   * default slot assignment in `pickBrandAssets`; the app never reads it. See `BrandLogoImageSchema`.
   */
  theme: z40.string().nullish(),
  formats: z40.array(BrandfetchLogoFormatSchema).optional()
});
var BrandfetchPayloadSchema = z40.looseObject({
  name: z40.string().nullish(),
  domain: z40.string().nullish(),
  description: z40.string().nullish(),
  logos: z40.array(BrandfetchLogoSchema).optional(),
  colors: z40.array(z40.looseObject({ hex: z40.string(), type: z40.string().nullish() })).optional()
});
var BRANDFETCH_SOURCE_ID = "brandfetch";
var BrandSourceSchema = z40.object({
  object: z40.literal("brand-source"),
  schema: z40.literal(1),
  id: z40.literal(BRANDFETCH_SOURCE_ID),
  brandId: BrandIdSchema,
  domain: z40.string(),
  fetchedAt: TimestampSchema,
  payload: BrandfetchPayloadSchema
});
var BrandReviewStatusSchema = z40.enum(["unreviewed", "needs-attention", "approved", "no-name", "junk"]);
var BrandLineSchema = z40.object({
  /** The fold of the line's name ("BRUNSWICK"), unique within the brand. */
  id: z40.string(),
  /** What a homeowner reads after the brand ("Brunswick"). */
  displayName: z40.string()
});
var BrandSchema = z40.object({
  object: z40.literal("brand"),
  schema: z40.literal(1),
  id: BrandIdSchema,
  canonicalName: z40.string(),
  canonicalKey: z40.string(),
  /** Properly-cased human name (e.g. "Whirlpool"), distinct from the uppercase-folded `canonicalName`. */
  displayName: z40.string().optional(),
  aliases: z40.array(z40.string()),
  website: z40.string().optional(),
  /**
   * Additional hostnames this brand serves content from, normalized (lowercase, no scheme/www/path) —
   * regional sites, support portals, asset/CDN hosts where manuals live. Together with `website`'s host
   * and `brandfetch.domain` these form the brand's OWNED domains (see `brandOwnedDomains` in
   * `@rock/brands`), the input to `isBrandOwnedHost` — the seam a coming auto-confirm check will use to
   * treat a document fetched from an owned host (or any subdomain of one) as manufacturer-published.
   * A separate CDN domain (e.g. whirlpoolassets.com) must be listed explicitly — subdomain matching
   * can't infer it.
   */
  domains: z40.array(z40.string()).optional(),
  /**
   * Hostnames found by AI research rather than typed into rock-admin: rock-migrator's brand-domain seed, and
   * the domains an admin keeps ticked when applying a brand request's research run (each one fetched and
   * checked first). Same trust tier as `domains` for `brandOwnedDomains`/`isBrandOwnedHost` — kept in a
   * separate field purely so the admin Domains tab can label its provenance distinctly and an admin can
   * still remove one that turns out wrong. Never written by rock-admin's own add-domain flow.
   */
  researchedDomains: z40.array(z40.string()).optional(),
  /**
   * Hostnames the brand does NOT own but whose copies of ITS documents an admin vouches for — an
   * authorized dealer or retailer (e.g. fireplacesdirect.com for a hearth brand). Scoped to this brand,
   * unlike the global `TRUSTED_DOCUMENT_HOSTS` in `@rock/manuals`: a document for another brand served
   * from the same host earns nothing. Never part of `brandOwnedDomains` — a multi-brand retailer isn't
   * owned by each brand it sells — but the auto-confirm gate's domain tier credits it (`isBrandTrustedHost`).
   */
  trustedDomains: z40.array(z40.string()).optional(),
  description: z40.string().optional(),
  /** The wordmark for LIGHT backgrounds — shown as-is in light mode, plated as the dark-mode fallback. */
  logo: BrandLogoImageSchema.optional(),
  /**
   * The wordmark for DARK backgrounds, when the brand has a distinct one — shown as-is in dark mode.
   * Populated automatically (from a Brandfetch `theme: 'light'` variant); an admin can reassign it to
   * any candidate that reads on dark (e.g. a paint-anywhere color mark). Absent ⇒ dark mode plates `logo`.
   */
  logoOnDark: BrandLogoImageSchema.optional(),
  /** Square mark (favicon-style), when the enrichment source distinguishes it from the full logo. */
  icon: BrandLogoImageSchema.optional(),
  /** Primary brand color as a hex string (e.g. "#F89C1C"). */
  primaryColor: z40.string().optional(),
  brandfetch: BrandfetchInfoSchema.optional(),
  // `.catch` rather than required: brands minted before this field existed still parse as 'unreviewed'
  // ahead of the backfill migration (the migration is what makes the value explicit in Firestore for
  // the admin list's server-side status filter, which can't match an absent field), AND a status
  // member added later reads as 'unreviewed' on a reader compiled before it — the one safe default
  // for the app's imagery gate (`approvedBrandAssets`), which serves only `approved`.
  reviewStatus: BrandReviewStatusSchema.catch("unreviewed"),
  /** Stamped whenever `reviewStatus` changes — an admin's edit, never an automated writer. */
  statusChangedAt: TimestampSchema.optional(),
  /** Why an admin set the status (e.g. what needs attention); every `setBrandReviewStatus` replaces or clears it. */
  statusNote: z40.string().optional(),
  /**
   * Set by a brand merge: this brand was folded into `mergedInto` and is now a tombstone (`junk`, so no
   * surface suggests it). Its alias docs point at the winner, so nothing resolves here any more; the doc
   * itself is kept because `brands/{id}/` Storage is wiped on delete and the ledger needs the record.
   */
  mergedInto: BrandIdSchema.optional(),
  mergedAt: TimestampSchema.optional(),
  /** Set with `mergedInto` when the merge was AS A LINE of the winner: the line the loser's names carry. */
  mergedAsLineId: z40.string().optional(),
  /** The brand's product lines (tens at most, so they ship in the search index). */
  lines: z40.array(BrandLineSchema).optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
function brandDisplayLabel(brand, lineId) {
  const label = brand.displayName ?? brand.canonicalName;
  const line = lineId ? brand.lines?.find((candidate) => candidate.id === lineId) : void 0;
  return line ? `${label} ${line.displayName}` : label;
}
function approvedBrandAssets(brand) {
  if (brand?.reviewStatus !== "approved") {
    return { logo: void 0, logoOnDark: void 0, icon: void 0 };
  }
  return { logo: brand.logo, logoOnDark: brand.logoOnDark, icon: brand.icon };
}
var BrandSearchEntrySchema = z40.object({
  id: BrandIdSchema,
  /** What a pick writes: `displayName` when set, else the uppercase `canonicalName`. */
  name: z40.string(),
  canonicalName: z40.string(),
  /** Display-form aliases. Omitted (not `[]`) when the brand has none, to keep the doc small. */
  aliases: z40.array(z40.string()).optional(),
  /** The brand's product lines, as their own typeahead rows ("Hunter Brunswick"). Omitted when none. */
  lines: z40.array(z40.object({ id: z40.string(), displayName: z40.string() })).optional()
});
var SharedNameplateRouteSchema = z40.object({
  spaceId: SpaceIdSchema,
  brandId: BrandIdSchema.optional(),
  note: z40.string().optional()
});
var SharedNameplateSchema = z40.object({
  object: z40.literal("shared-nameplate"),
  schema: z40.literal(1),
  /** The bare key both companies fold to (`normalizeBrandName(...).key`); also the doc id. */
  id: z40.string(),
  key: z40.string(),
  /** Why the bare name is denied — stored on the deny docs, shown in admin. */
  reason: z40.string(),
  /** The qualified brands, one per company. */
  sides: z40.array(BrandIdSchema),
  routes: z40.array(SharedNameplateRouteSchema),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var SharedNameplateEntrySchema = z40.object({
  key: z40.string(),
  sides: z40.array(BrandIdSchema),
  routes: z40.array(SharedNameplateRouteSchema.pick({ spaceId: true, brandId: true }))
});
var BrandSearchIndexSchema = z40.object({
  object: z40.literal("brand-search-index"),
  schema: z40.literal(1),
  /** Build stamp — the client compares it to decide whether to swap its cached copy. */
  version: TimestampSchema,
  entries: z40.array(BrandSearchEntrySchema),
  /**
   * The shared nameplates, so the typeahead and HQL route a bare "Maytag" without another read. Absent
   * on an index built before the table existed (and stripped by a client that predates the field),
   * which readers treat as "no shared nameplates" — and the app treats as a reason to refetch.
   */
  sharedNameplates: z40.array(SharedNameplateEntrySchema).optional()
});
var BrandAliasSchema = z40.object({
  object: z40.literal("brand-alias"),
  schema: z40.literal(1),
  id: BrandAliasKeySchema,
  brandId: BrandIdSchema.optional(),
  canonicalKey: z40.string(),
  /** Set (only ever `true`) on deny placeholders — mutually exclusive with `brandId`. */
  denied: z40.literal(true).optional(),
  /** Why this key is denied — shown in admin when someone tries to claim it. */
  reason: z40.string().optional(),
  /** The `shared-nameplates` entry a deny placeholder projects. */
  nameplateKey: z40.string().optional(),
  /** The brand's product line this key names, when it names one ("HUNTER BRUNSWICK" → Brunswick). */
  lineId: z40.string().optional(),
  updatedAt: TimestampSchema,
  createdAt: TimestampSchema
}).refine((alias) => !!alias.brandId !== !!alias.denied, {
  message: "A brand alias is either a mapping (brandId) or a deny placeholder (denied) \u2014 exactly one"
});
var BrandRequestSourceSchema = z40.enum(["equipment", "recall", "admin"]);
var BrandRequestSightingSchema = z40.object({
  source: BrandRequestSourceSchema,
  modelId: ModelIdSchema.optional(),
  /** The home whose unit named the model: one model in two homes is two sightings. */
  homeId: HomeIdSchema.optional(),
  recallId: RecallIdSchema.optional(),
  /** A unit with no model to name (no model number yet): its sightings count once per unit. */
  equipmentId: EquipmentIdSchema.optional(),
  spaceId: SpaceIdSchema.optional(),
  /** `observed` = read off the unit (user, photo AI, notice); `researched` = a web-enrichment guess. */
  provenance: z40.enum(["observed", "researched"]).optional(),
  /** The page the name was extracted from, when there is one — an admin's evidence link. */
  evidenceUrl: z40.string().optional(),
  at: TimestampSchema
});
var BRAND_REQUEST_SIGHTING_CAP = 20;
var BrandRequestStatusSchema = z40.enum(["pending", "parked", "resolved"]);
function isBrandRequestOpen(request) {
  return request.status !== "resolved";
}
var BrandRequestParkSchema = z40.object({
  by: UserIdSchema,
  at: TimestampSchema,
  seenAgain: BrandRequestSightingSchema.optional()
});
var BrandRequestResolutionKindSchema = z40.enum(["brand", "alias", "line", "shared-nameplate", "not-a-brand"]);
var BrandRequestResolutionSchema = z40.object({
  kind: BrandRequestResolutionKindSchema,
  brandId: BrandIdSchema.optional(),
  lineId: z40.string().optional(),
  reason: z40.string().optional(),
  /** The admin's user id, or `migration` for requests resolved before the queue existed. */
  by: z40.union([z40.literal("migration"), UserIdSchema]),
  at: TimestampSchema
});
var BrandRequestSchema = z40.object({
  object: z40.literal("brand-request"),
  schema: z40.literal(1),
  id: BrandRequestIdSchema,
  /** The name as first seen (display form; the fold is the doc id). */
  brand: z40.string(),
  status: BrandRequestStatusSchema.catch("pending"),
  sightings: z40.array(BrandRequestSightingSchema).default([]),
  sightingCount: z40.number().int().nonnegative().default(0),
  sourceCounts: z40.object({
    equipment: z40.number().int().nonnegative().optional(),
    recall: z40.number().int().nonnegative().optional(),
    admin: z40.number().int().nonnegative().optional()
  }).default({}),
  /** Every space the name was seen in — the list's "spaces" column, and the Phase 2 routing hint. */
  spaceIds: z40.array(SpaceIdSchema).default([]),
  firstSeenAt: TimestampSchema.optional(),
  lastSeenAt: TimestampSchema.optional(),
  parked: BrandRequestParkSchema.optional(),
  resolution: BrandRequestResolutionSchema.optional(),
  updatedAt: TimestampSchema.optional(),
  // Legacy (pre-queue) fields, kept so the history reads.
  space: SpaceIdSchema.optional(),
  serp: z40.any().optional(),
  sourceModelId: ModelIdSchema.optional(),
  /** The brand the old auto-mint path created or resolved; `resolution.brandId` from now on. */
  canonicalBrandId: z40.string().optional(),
  canonicalBrandKey: z40.string().optional()
});
var BrandRequestSuggestionSchema = z40.object({
  kind: z40.enum(["alias", "line", "shared"]),
  brandId: BrandIdSchema.optional(),
  brandName: z40.string().optional(),
  detail: z40.string()
});
var BRAND_MERGE_STAGES = [
  "aliases",
  "recalls-union",
  // The winner joins each document's brands; `documents-remove` drops the loser once models have moved.
  "documents",
  "models",
  "documents-remove",
  "recalls-remove",
  "bookkeeping",
  "loser",
  "index"
];
var BrandMergeStageSchema = z40.enum(BRAND_MERGE_STAGES);
var BrandMergeStageResultSchema = z40.object({
  status: z40.enum(["done", "failed"]),
  /** Docs this stage wrote on this run (a resumed run counts only what was still left). */
  count: z40.number().int().nonnegative(),
  at: TimestampSchema,
  error: z40.string().optional()
});
var BrandMergeTouchedSchema = z40.object({
  path: z40.string(),
  field: z40.string(),
  before: z40.unknown().optional()
});
var BRAND_MERGE_TOUCHED_CAP = 2e3;
var BrandMergeSchema = z40.object({
  object: z40.literal("brand-merge"),
  schema: z40.literal(1),
  id: BrandMergeIdSchema,
  loserId: BrandIdSchema,
  winnerId: BrandIdSchema,
  loserName: z40.string(),
  winnerName: z40.string(),
  requestedBy: UserIdSchema,
  status: z40.enum(["running", "done", "failed"]),
  stages: z40.partialRecord(BrandMergeStageSchema, BrandMergeStageResultSchema),
  touched: z40.array(BrandMergeTouchedSchema),
  touchedTruncated: z40.boolean().optional(),
  /** Merged as a line: the loser's names and models carry this line on the winner. */
  line: BrandLineSchema.optional(),
  error: z40.string().optional(),
  startedAt: TimestampSchema,
  finishedAt: TimestampSchema.optional()
});
var MergeBrandsRequestSchema = z40.object({
  loserId: BrandIdSchema,
  winnerId: BrandIdSchema,
  /** Count what would change without writing anything — the admin preview. */
  dryRun: z40.boolean(),
  /**
   * Merge AS A LINE: the loser was really a product line of the winner ("Hunter Brunswick"). Its names
   * resolve to the winner with this line, and its models carry the line.
   */
  asLine: z40.object({ displayName: z40.string() }).optional()
});
var BrandMergePreviewSchema = z40.object({
  aliasDocs: z40.number().int(),
  recalls: z40.number().int(),
  /** Approved recalls among `recalls` — these re-apply when their brand gate changes. */
  liveRecalls: z40.number().int(),
  liveRecallTitles: z40.array(z40.string()),
  models: z40.number().int(),
  /** Models whose brand change may re-arm a paid manual hunt (parked or given up). */
  modelsMayRehunt: z40.number().int(),
  documents: z40.number().int(),
  recallMatches: z40.number().int(),
  brandRequests: z40.number().int(),
  /** The winner has no logo and the loser does, so the merge carries it over. */
  carriesLogo: z40.boolean(),
  /** Merging as a line: the line the loser's names and models will carry. */
  line: z40.object({ id: z40.string(), displayName: z40.string() }).optional(),
  /** Line mode: the loser's bare keys (not qualified by a brand name) that will be deleted, not carried. */
  droppedKeys: z40.array(z40.string())
});
var MergeBrandsResponseSchema = z40.discriminatedUnion("status", [
  z40.object({ status: z40.literal("refused"), reason: z40.string() }),
  z40.object({ status: z40.literal("preview"), preview: BrandMergePreviewSchema }),
  z40.object({ status: z40.literal("merged"), mergeId: BrandMergeIdSchema, preview: BrandMergePreviewSchema })
]);
var BrandfetchCandidateSchema = z40.object({
  domain: z40.string(),
  name: z40.string().optional(),
  /** Brandfetch CDN icon — preview-only (candidates are transient; applied images get rehosted). */
  iconUrl: z40.string().optional()
});
var EnrichBrandRequestSchema = z40.object({
  brandId: BrandIdSchema,
  /** Present when the admin picked a candidate; absent to search by the brand's name. */
  domain: z40.string().optional()
});
var EnrichBrandResponseSchema = z40.discriminatedUnion("status", [
  /** Brand data fetched and written onto the brand doc. */
  z40.object({ status: z40.literal("applied"), domain: z40.string() }),
  /** No auto-applicable exact match — the caller should offer these for a human pick. */
  z40.object({ status: z40.literal("candidates"), candidates: z40.array(BrandfetchCandidateSchema) }),
  /** Brandfetch has nothing for this name/domain. */
  z40.object({ status: z40.literal("not-found") })
]);

// src/brand_research.types.ts
import * as z41 from "zod";
var BrandResearchDecisionSchema = z41.enum(["new-brand", "alias-of", "line-of", "not-a-brand"]);
var BrandLabelKindSchema = z41.enum(["established", "marketplace-label", "unknown"]);
var ResearchedDomainRoleSchema = z41.enum(["primary", "regional", "support", "manuals", "assets"]);
var ResearchedAliasKindSchema = z41.enum(["spelling", "former-name", "company-name", "regional-name"]);
var BrandResearchAnswerSchema = z41.object({
  decision: BrandResearchDecisionSchema,
  confidence: z41.number().describe("0 to 1, for the decision"),
  summary: z41.string().describe("Two sentences an admin reads first"),
  brandName: z41.string().nullish().describe("new-brand: the name its products print, properly cased"),
  targetBrand: z41.string().nullish().describe("alias-of / line-of / not-a-brand: the brand as its products print it"),
  lineName: z41.string().nullish(),
  notABrandReason: z41.string().nullish(),
  labelKind: BrandLabelKindSchema,
  owner: z41.object({ company: z41.string().nullish(), country: z41.string().nullish(), history: z41.string().nullish() }),
  website: z41.string().nullish(),
  domains: z41.array(z41.object({ host: z41.string(), role: ResearchedDomainRoleSchema, evidenceUrl: z41.string() })),
  aliases: z41.array(
    z41.object({ name: z41.string(), kind: ResearchedAliasKindSchema, evidenceUrl: z41.string().nullish() })
  ),
  samePendingRequests: z41.array(z41.string()),
  otherUsesOfName: z41.array(
    z41.object({ company: z41.string(), products: z41.string(), evidenceUrl: z41.string().nullish() })
  ),
  exampleModels: z41.array(z41.object({ modelNumber: z41.string(), productType: z41.string(), evidenceUrl: z41.string() })),
  sources: z41.array(z41.string())
});
var BrandResearchCallSchema = z41.object({
  provider: z41.string(),
  model: z41.string(),
  ms: z41.number(),
  costUsd: z41.number(),
  searches: z41.number().int().nonnegative(),
  answer: BrandResearchAnswerSchema.optional(),
  error: z41.string().optional()
});
var ResearchCheckVerdictSchema = z41.enum(["ok", "doubtful", "rejected"]);
var ResearchedDomainSchema = z41.object({
  host: z41.string(),
  role: ResearchedDomainRoleSchema,
  evidenceUrl: z41.string(),
  /** The providers that proposed it. */
  sources: z41.array(z41.string()),
  verdict: ResearchCheckVerdictSchema,
  detail: z41.string(),
  /** Set when another catalog brand already owns the host. */
  ownerBrandId: BrandIdSchema.optional()
});
var ResearchedAliasSchema = z41.object({
  name: z41.string(),
  kind: z41.union([ResearchedAliasKindSchema, z41.literal("pending-request")]),
  sources: z41.array(z41.string()),
  noop: z41.boolean().optional(),
  takenBy: BrandIdSchema.optional()
});
var ResearchedModelCheckSchema = z41.enum(["found", "missing", "blocked"]);
var ResearchedModelSchema = z41.object({
  modelNumber: z41.string(),
  productType: z41.string(),
  evidenceUrl: z41.string(),
  sources: z41.array(z41.string()),
  check: ResearchedModelCheckSchema
});
var BrandResearchProposalSchema = z41.object({
  decision: BrandResearchDecisionSchema,
  /** Whether the providers agreed on the decision (and its brand), or only one answered. */
  agreement: z41.enum(["agreed", "split", "single"]),
  brandName: z41.string().optional(),
  targetBrandId: BrandIdSchema.optional(),
  /** The brand a not-a-brand name really belongs to, or an alias-of target Rock lacks, as the research named it. */
  targetBrandName: z41.string().optional(),
  lineName: z41.string().optional(),
  notABrandReason: z41.string().optional(),
  labelKind: BrandLabelKindSchema,
  summary: z41.string(),
  owner: z41.object({ company: z41.string().optional(), country: z41.string().optional(), history: z41.string().optional() }),
  website: z41.string().optional(),
  domains: z41.array(ResearchedDomainSchema),
  aliases: z41.array(ResearchedAliasSchema),
  otherUsesOfName: z41.array(z41.object({ company: z41.string(), products: z41.string(), sources: z41.array(z41.string()) })),
  exampleModels: z41.array(ResearchedModelSchema),
  /** What each provider decided, when they did not agree. */
  alternatives: z41.array(z41.object({ provider: z41.string(), decision: BrandResearchDecisionSchema, brand: z41.string() }))
});
var BrandResearchStatusSchema = z41.enum(["requested", "running", "done", "failed"]);
var BrandResearchSchema = z41.object({
  object: z41.literal("brand-research"),
  schema: z41.literal(1),
  id: BrandResearchIdSchema,
  requestId: BrandRequestIdSchema,
  /** The name researched, as the request held it. */
  name: z41.string(),
  requestedBy: UserIdSchema,
  status: BrandResearchStatusSchema,
  /** The prompt and planner version, stamped when the run is claimed, so an admin can tell an old run from a new one. */
  version: z41.string().optional(),
  calls: z41.array(BrandResearchCallSchema).default([]),
  proposal: BrandResearchProposalSchema.optional(),
  costUsd: z41.number().default(0),
  error: z41.string().optional(),
  requestedAt: TimestampSchema,
  /** Set when the trigger claims the run. */
  startedAt: TimestampSchema.optional(),
  finishedAt: TimestampSchema.optional(),
  /** The trigger event that claimed the run; a redelivered event finds it taken and does nothing. */
  eventId: z41.string().optional()
});
var BRAND_RESEARCH_ABANDONED_AFTER_MS = 15 * 6e4;
function isBrandResearchUnderway(run, now) {
  if (run.status !== "requested" && run.status !== "running") return false;
  return now.getTime() - new Date(run.requestedAt).getTime() < BRAND_RESEARCH_ABANDONED_AFTER_MS;
}

// src/chat.types.ts
import * as z42 from "zod";
var ChatMessageRoleSchema = z42.enum(["user", "assistant", "tool-call", "tool-result"]);
var ChatToolCallSchema = z42.object({
  callId: z42.string(),
  toolName: z42.string(),
  args: z42.record(z42.string(), z42.unknown())
});
var ChatToolResultSchema = z42.object({
  callId: z42.string(),
  toolName: z42.string(),
  // A tool may legitimately return undefined, and Firestore/JSON drop undefined keys; zod 4.4+ rejects a
  // MISSING key for a non-optional z.unknown(), so the key itself must be optional (type stays unknown).
  result: z42.unknown().optional()
});
var ChatMessageSchema = z42.object({
  id: ChatMessageIdSchema,
  role: ChatMessageRoleSchema,
  createdAt: TimestampSchema,
  // Always set for `user` and `assistant` messages.
  content: z42.string().nullish(),
  // Set when role === 'tool-call'.
  toolCall: ChatToolCallSchema.nullish(),
  // Set when role === 'tool-result'.
  toolResult: ChatToolResultSchema.nullish()
});
var CONSULT_MANUAL_TOOL_NAME = "consult_manual";
var ConsultManualAnswerSchema = z42.object({
  answer: z42.string(),
  manualUrl: z42.string().nullable()
});
var ChatIntentSchema = z42.discriminatedUnion("kind", [
  z42.object({
    kind: z42.literal("add-reminder"),
    equipmentId: EquipmentIdSchema.nullish(),
    spaceId: SpaceIdSchema.nullish()
  }),
  // Open-ended "Ask a Question" entry point, rooted on a subject the user was
  // already looking at (today: a piece of equipment). Unlike add-reminder it
  // doesn't bias a specific tool — it just tells the agent what the
  // conversation is about so replies don't re-ask which thing they mean.
  z42.object({
    kind: z42.literal("ask-about"),
    equipmentId: EquipmentIdSchema.nullish(),
    spaceId: SpaceIdSchema.nullish()
  }),
  // Placeholder variant kept solely so TypeScript's discriminated-union
  // narrowing reaches `never` in the default branch of switches — gives us
  // a real exhaustiveness check (via `assertUnreachable`) when new kinds
  // are added. Not emitted by any caller at runtime; handlers should
  // treat it as a no-op.
  z42.object({
    kind: z42.literal("placeholder")
  })
]);
var CHAT_MODELS = ["gpt-5.4-mini", "claude-haiku-5-5", "claude-sonnet-5-5"];
var ChatModelSchema = z42.enum(CHAT_MODELS);
var ChatConversationSchema = z42.object({
  object: z42.literal("chat-conversation"),
  schema: z42.literal(1),
  id: ChatConversationIdSchema,
  userId: UserIdSchema,
  homeId: HomeIdSchema,
  messages: z42.array(ChatMessageSchema),
  // Single-line conversation label (≤60 chars). Generated server-side after the
  // first assistant reply and shown in recent-chat lists. Older conversations
  // created before this field existed will be `nullish` — callers should fall
  // back to the first user message via `getConversationLabel()`.
  summary: z42.string().nullish(),
  // Cumulative cost telemetry for the session.
  tokensIn: z42.number().default(0),
  tokensOut: z42.number().default(0),
  estimatedCostUsd: z42.number().default(0),
  // The provider's id for the most recent response. On OpenAI it is passed back as
  // `previous_response_id` so the model carries reasoning + tool state forward
  // without us replaying it. OpenAI retains this state for ~30 days; older
  // conversations should be treated as read-only by the UI.
  lastResponseId: z42.string().nullish(),
  // The model every turn runs on (a `ChatModel`, kept a string so a model later retired still reads). Absent on
  // conversations from before the choice existed.
  model: z42.string().nullish(),
  // Draft tool-result message id → when the user saved it, so the thread keeps showing a saved card with its
  // "Saved" / "Sent to the team" indicator. Written with the turn that committed it.
  committedDraftIds: z42.record(ChatMessageIdSchema, TimestampSchema).default({}),
  // Draft tool-result message id → when the user cancelled it from its card. A cancelled draft can no longer
  // be saved and the thread stops showing it (see `findLiveDraft` in @rock/actions).
  cancelledDraftIds: z42.record(ChatMessageIdSchema, TimestampSchema).default({}),
  // Cumulative names of tools the agent has explicitly loaded (via the
  // `load_tools` meta-tool) at any point in this conversation. Re-included
  // on every subsequent turn so the model can call those tools directly
  // without paying another `load_tools` round-trip. Empty/absent on
  // conversations created before lazy tool loading shipped.
  loadedToolNames: z42.array(z42.string()).default([]),
  // Stage-setting context recorded at conversation creation. Describes the
  // entry point ("Add a Reminder", scoped to a specific equipment/space,
  // etc.) so the thread UI can render a banner matching the same hint we
  // gave the agent on its first turn. Never mutated after creation.
  intent: ChatIntentSchema.nullish(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var CHAT_CONVERSATION_READONLY_AFTER_DAYS = 30;
var CHAT_READONLY_AFTER_MS = CHAT_CONVERSATION_READONLY_AFTER_DAYS * 24 * 60 * 60 * 1e3;
function isConversationReadOnly(conversation, now = /* @__PURE__ */ new Date()) {
  const updatedAt = new Date(conversation.updatedAt).getTime();
  if (Number.isNaN(updatedAt)) return false;
  return now.getTime() - updatedAt > CHAT_READONLY_AFTER_MS;
}
var CHAT_CONVERSATION_READONLY_REASON = `This conversation is older than ${CHAT_CONVERSATION_READONLY_AFTER_DAYS} days. Start a new chat to continue.`;
function getConversationLabel(conversation) {
  if (conversation.summary) return conversation.summary;
  const firstUser = conversation.messages.find((m) => m.role === "user");
  const text = firstUser?.content?.trim();
  if (!text) return "New chat";
  return text.length > 60 ? `${text.slice(0, 57)}\u2026` : text;
}
var HomeContextRoomSchema = z42.object({
  id: z42.string(),
  name: z42.string(),
  type: z42.string().nullish()
});
var HomeContextAttributeSchema = z42.object({
  label: z42.string(),
  value: z42.string()
});
var HomeContextEquipmentSchema = z42.object({
  id: z42.string(),
  spaceId: z42.string(),
  typeId: z42.string(),
  typeTitle: z42.string(),
  nickname: z42.string().nullish(),
  roomId: z42.string().nullish(),
  roomName: z42.string().nullish(),
  // Brand, model, and any custom fields the user has filled in — derived from
  // Equipment.fields + EquipmentType.inputFields. Empty array when nothing is set.
  attributes: z42.array(HomeContextAttributeSchema).default([])
});
var HomeContextSystemInfoBlockSchema = z42.object({
  spaceId: z42.string(),
  spaceName: z42.string(),
  entries: z42.array(HomeContextAttributeSchema)
});
var HomeContextSubscriptionSchema = z42.object({
  id: z42.string(),
  taskTypeId: z42.string(),
  taskTitle: z42.string().nullish(),
  /** The units the reminder covers, by equipment id. */
  coveredEquipmentIds: z42.array(z42.string()).default([]),
  /**
   * The day of the reminder's next chore: its open task's date while one waits (from an earlier week
   * too), else the reminder's own `nextTaskDate` — one chore per reminder, as `homeChores` counts it.
   */
  nextTaskDate: LocalDateSchema.nullish(),
  /** The day the reminder's open chore was first due, when that is before today: it is waiting, not upcoming. */
  waitingSince: LocalDateSchema.nullish(),
  cadence: z42.string().nullish(),
  status: z42.enum(["active", "paused"])
});
var HomeContextSummarySchema = z42.object({
  user: z42.object({
    id: UserIdSchema,
    name: z42.string().nullish(),
    timezone: z42.string().nullish(),
    locale: z42.string().nullish()
  }),
  home: z42.object({
    id: HomeIdSchema,
    name: z42.string(),
    zip: z42.string().nullish(),
    city: z42.string().nullish(),
    state: z42.string().nullish()
  }),
  rooms: z42.array(HomeContextRoomSchema),
  equipment: z42.array(HomeContextEquipmentSchema),
  systemInfo: z42.array(HomeContextSystemInfoBlockSchema).default([]),
  subscriptions: z42.array(HomeContextSubscriptionSchema),
  counts: z42.object({
    roomCount: z42.number(),
    equipmentCount: z42.number(),
    subscriptionCount: z42.number()
  })
});
var ChatTurnClientAppSchema = z42.object({
  env: z42.string(),
  version: z42.string(),
  platform: z42.string(),
  platformId: z42.string()
});
var ChatTurnRequestSchema = z42.object({
  homeId: HomeIdSchema,
  conversationId: ChatConversationIdSchema.nullish(),
  userMessageId: ChatMessageIdSchema,
  message: z42.string().min(1).max(4e3),
  clientApp: ChatTurnClientAppSchema,
  // Only meaningful on the very first turn of a conversation — describes the
  // entry point the user came from (e.g. tapping "Add a Reminder" while
  // viewing a piece of equipment). Persisted onto the new conversation and
  // injected into the agent's system prompt for that first turn only.
  intent: ChatIntentSchema.nullish(),
  // Set when the user cancelled a draft from its card: this message is the cancellation, and the draft it names
  // is retired before the agent replies.
  cancelDraftId: ChatMessageIdSchema.nullish(),
  // The model a new conversation runs on, chosen from the app's dev menu; ignored on a conversation that exists.
  model: ChatModelSchema.nullish()
});
var ChatWarmupRequestSchema = z42.object({ warmup: z42.literal(true) });
function isChatWarmupRequest(data) {
  return ChatWarmupRequestSchema.safeParse(data).success;
}
var ChatTurnResponseSchema = z42.object({
  conversationId: ChatConversationIdSchema,
  reply: z42.string(),
  estimatedTurnCostUsd: z42.number().nullish()
});

// src/company.types.ts
import * as z43 from "zod";
var CompanyAddressSchema = z43.object({
  formatted: z43.string(),
  street: z43.string().optional(),
  city: z43.string().optional(),
  state: z43.string().optional(),
  zip: z43.string().optional(),
  country: z43.string().optional()
});
var CompanyGeoSchema = z43.object({
  latitude: z43.number(),
  longitude: z43.number()
});
var CompanyBrandSchema = z43.object({
  logoUrl: z43.string().optional(),
  iconUrl: z43.string().optional(),
  primaryColor: z43.string().optional(),
  fetchedAt: TimestampSchema,
  notFound: z43.boolean().optional()
});
var CompanyBusinessStatusSchema = z43.enum(["operational", "closed_temporarily", "closed_permanently"]);
var CompanySchema = z43.object({
  object: z43.literal("company"),
  schema: z43.literal(1),
  id: CompanyIdSchema,
  active: z43.boolean(),
  name: z43.string().min(1),
  phone: z43.string().optional(),
  website: z43.string().optional(),
  domain: z43.string().optional(),
  address: CompanyAddressSchema.optional(),
  geo: CompanyGeoSchema.optional(),
  googlePlaceTypes: z43.array(z43.string()).optional(),
  // Google's businessStatus signals whether the place is still open for business. Undefined
  // means we haven't asked yet or Google didn't return a status (treat as operational).
  businessStatus: CompanyBusinessStatusSchema.optional(),
  // Google Photo resource name (e.g. "places/{placeId}/photos/{ref}"). We intentionally do NOT
  // store a full URL here because that would leak the API key. The proxy function resolves the
  // name to an image via the private server-side key.
  googlePhotoRef: z43.string().optional(),
  brand: CompanyBrandSchema.optional(),
  logoOverrideUrl: z43.string().optional(),
  googleRefreshedAt: TimestampSchema,
  // When the nightly scheduled refresh should re-pull this company from Google/Brandfetch.
  // Interval depends on businessStatus — operational: 30d, temporarily closed: 7d, permanently
  // closed: 180d. Queried by the scheduler, so it lives on the doc instead of being derived.
  nextRefreshAt: TimestampSchema.optional(),
  createdAt: TimestampSchema
});

// src/consideration.types.ts
import * as z44 from "zod";
var ConsiderationStatusSchema = z44.enum(["pending", "accepted", "ignored", "withdrawn"]);
var ConsiderationStatusOptions = ConsiderationStatusSchema.options;
var ConsiderationStatusLabels = {
  pending: "Active",
  accepted: "Accepted",
  ignored: "Ignored",
  withdrawn: "No Longer Applies"
};
var ConsiderationSourceTypeSchema = z44.enum(["guide", "manual", "home-eval"]);
var DocumentStatusSchema = z44.enum(["draft", "revisit", "approved", "retired"]);
function isWithheldStatus(status) {
  return status === "draft" || status === "revisit" || status === "retired";
}
var ConsiderationCriticalitySchema = z44.enum(["optional", "recommended", "necessary", "urgent", "critical"]);
var ConsiderationCriticalityOptions = ConsiderationCriticalitySchema.options;
var ConsiderationCriticalityLabels = {
  optional: "Optional",
  recommended: "Recommended",
  necessary: "Necessary",
  urgent: "Urgent",
  critical: "Critical"
};
var MaybeLaterDurationSchema = z44.enum(["week", "month", "3months", "6months", "year"]);
var ConsiderationDeclineReasonSchema = z44.enum(["already-handled", "not-applicable"]);
var ConsiderationSchema = z44.object({
  object: z44.literal("consideration"),
  schema: z44.literal(1),
  id: ConsiderationIdSchema,
  typeId: ConsiderationTypeIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema.optional(),
  // Legacy: the one unit a pre-query, per-unit consideration was for. Read through the coverage
  // function (`@rock/hql` `coverageOf`), never written.
  equipmentId: EquipmentIdSchema.optional(),
  // A one-time action's units, recorded when it was suggested: the type's query suggests it again
  // only for a unit no consideration of the type recorded. A recurring suggestion records nothing;
  // it covers whatever its query matches now.
  coveredEquipmentIds: z44.array(EquipmentIdSchema).optional(),
  status: ConsiderationStatusSchema,
  sourceType: ConsiderationSourceTypeSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  acceptedAt: TimestampSchema.optional(),
  // Stamped when the homeowner pushes a suggestion out themselves ("Maybe Later") rather than us
  // pacing it. The push itself is just a later `reviewAt` — this records who moved it, which is
  // what the activity feed reads and what the home review uses to keep a set-aside row set aside.
  snoozedAt: TimestampSchema.optional(),
  // The weekend (its first day, `YYYY-MM-DD`) this suggestion is brought up on the weekend checklist
  // as a Suggested Reminder. Absent, the suggestion is QUEUED: the week's fill (`fill-weekend`)
  // gives it a weekend when one has room. Act-now severities are dated today wherever they are
  // created, and the eval and the fill date any that slipped through. A pending item with a FUTURE
  // `reviewAt` stays visible in Suggestions but is not counted/badged.
  reviewAt: LocalDateSchema.optional(),
  // Stamped when the user resolves an item from anywhere (accept / push out / ignore) — the checklist,
  // the suggestion detail, or the review. The app reads it to keep a row answered this week on This
  // Week (`isAnsweredSince`) and on the weekend checklist; the status change is what stops it surfacing.
  reviewedAt: TimestampSchema.optional(),
  // Who answered it (accept / push out / ignore); leaves with the answer when it is taken back.
  decidedBy: UserIdSchema.optional(),
  // Written with `status: 'ignored'` when the user picks a "never" reason on the review
  // ("Already handled" / "Doesn't apply to my home"). Analytics/content signal, not a behavior switch.
  declineReason: ConsiderationDeclineReasonSchema.optional(),
  read: z44.boolean().optional(),
  lastReadAt: TimestampSchema.optional(),
  criticality: ConsiderationCriticalitySchema.optional()
});
var UNTITLED_CONSIDERATION = "Suggestion";
function titledConsideration(consideration, type) {
  return { ...consideration, title: considerationTitle(type) };
}
function considerationTitle(type) {
  return type?.title ?? UNTITLED_CONSIDERATION;
}
var CONSIDERATIONS_TITLE = "Suggested Reminders";
var AppliesToDraftSchema = z44.object({
  /** Absent when the condition couldn't be expressed; `issue` says why. */
  query: z44.string().optional(),
  /** Why a human must look closer: the model couldn't express the condition, or its query doesn't validate. */
  issue: z44.string().optional(),
  /** The When To Apply (or a lesson's When To Show) text the draft was made from. */
  whenToApply: z44.string(),
  draftedAt: TimestampSchema
});
var AppliesToFieldsSchema = z44.object({
  // The approved applies-to query (HQL, `@rock/hql`): which homes get this type and, for a
  // consideration, which equipment it covers. Written only by an admin approving it, never by the
  // drafting step.
  appliesTo: z44.string().optional(),
  appliesToApprovedAt: TimestampSchema.optional(),
  appliesToApprovedBy: UserIdSchema.optional(),
  appliesToDraft: AppliesToDraftSchema.optional(),
  currentAppliesToDraftTaskVersion: z44.number().optional(),
  requestedAppliesToDraftTaskVersion: z44.number().optional()
});
var ConsiderationTypeSchema = z44.object({
  object: z44.literal("consideration-type"),
  schema: z44.literal(1),
  id: ConsiderationTypeIdSchema,
  title: z44.string().min(1),
  spaceId: SpaceIdSchema.optional(),
  modelNumber: z44.string().optional(),
  defaultCriticality: ConsiderationCriticalitySchema.optional(),
  considerationDocId: RockDocIdSchema.optional(),
  overview: z44.string().optional(),
  // The content's When To Apply text, which the applies-to query is drafted from.
  aiWhenToApply: z44.string().optional(),
  ...AppliesToFieldsSchema.shape,
  // Review status from Notion (only 'approved' types are applied to homes)
  documentStatus: DocumentStatusSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var HomeEvalAiResponseSchema = z44.object({
  // OpenAI's structured outputs API requires optional fields to also be nullable (nullish)
  // See: https://platform.openai.com/docs/guides/structured-outputs?api-mode=responses#all-fields-must-be-required
  // For home-scoped consideration types: true to create one consideration at the space/home level.
  applyToSpace: z44.boolean().nullish(),
  // For equipment-scoped consideration types: list of equipment IDs to create considerations for.
  applyToEquipmentIds: z44.array(EquipmentIdSchema).nullish(),
  isPossible: z44.boolean().nullish(),
  issueMessage: z44.string().nullish(),
  summary: z44.string()
});
var HomeEvalResultV1Schema = z44.object({
  object: z44.literal("home-eval-result"),
  schema: z44.literal(1),
  id: HomeEvalResultIdSchema,
  homeId: HomeIdSchema,
  jobId: HomeEvalJobIdSchema,
  // Which review run produced this result, when it came from a review-time evaluation (admin tracing).
  planReviewId: PlanReviewIdSchema.optional(),
  considerationTypeId: ConsiderationTypeIdSchema,
  aiResponse: HomeEvalAiResponseSchema,
  createdConsiderationIds: z44.array(ConsiderationIdSchema).optional(),
  aiContext: z44.object({
    platform: z44.literal("openai"),
    model: z44.string(),
    systemMessage: z44.string(),
    userMessage: z44.string()
  }),
  createdAt: TimestampSchema
});
var HqlValueSchema = z44.union([z44.string(), z44.number(), z44.boolean(), z44.array(z44.string())]);
var HomeEvalFactUnitSchema = z44.object({
  id: EquipmentIdSchema,
  typeId: EquipmentTypeIdSchema,
  spaceId: SpaceIdSchema,
  brandId: BrandIdSchema.optional(),
  brandKey: z44.string().optional(),
  fields: z44.record(z44.string(), HqlValueSchema)
});
var HomeEvalFactsSchema = z44.object({
  info: z44.record(z44.string(), HqlValueSchema.nullable()),
  equipment: z44.array(HomeEvalFactUnitSchema)
});
var HomeEvalResultV2Schema = z44.object({
  object: z44.literal("home-eval-result"),
  schema: z44.literal(2),
  id: HomeEvalResultIdSchema,
  homeId: HomeIdSchema,
  jobId: HomeEvalJobIdSchema,
  planReviewId: PlanReviewIdSchema.optional(),
  considerationTypeId: ConsiderationTypeIdSchema,
  query: z44.string(),
  /** Every unit the query matched. */
  matchedEquipmentIds: z44.array(EquipmentIdSchema),
  /** The units the created consideration covers: all it matched, or for a one-time action only those no earlier action recorded. */
  coveredEquipmentIds: z44.array(EquipmentIdSchema),
  createdConsiderationIds: z44.array(ConsiderationIdSchema),
  facts: HomeEvalFactsSchema,
  createdAt: TimestampSchema
});
var HomeEvalResultSchema = z44.discriminatedUnion("schema", [HomeEvalResultV1Schema, HomeEvalResultV2Schema]);
var AppliesToDraftResultSchema = z44.object({
  object: z44.literal("applies-to-draft-result"),
  schema: z44.literal(1),
  id: AppliesToDraftResultIdSchema,
  considerationTypeId: ConsiderationTypeIdSchema.optional(),
  lessonTypeId: LessonTypeIdSchema.optional(),
  whenToApply: z44.string(),
  result: z44.object({
    query: z44.string().nullish(),
    issue: z44.string().nullish(),
    summary: z44.string()
  }),
  /** What `checkHql` found wrong with the drafted query, against the catalog at the time. */
  validationErrors: z44.array(z44.string()),
  /** What it flagged without rejecting (e.g. a brand the catalog lacks). */
  validationWarnings: z44.array(z44.string()).optional(),
  aiContext: z44.object({
    platform: z44.literal("openai"),
    model: z44.string(),
    taskVersion: z44.number(),
    systemMessage: z44.string(),
    userMessage: z44.string()
  }),
  createdAt: TimestampSchema
}).refine((result) => !!result.considerationTypeId !== !!result.lessonTypeId, {
  message: "Exactly one of considerationTypeId / lessonTypeId must be set"
});

// src/device.types.ts
import * as z45 from "zod";
var DevicePlatformSchema = z45.enum(["ios", "android", "web", "macos", "windows", "unknown"]);
var DeviceSchema = z45.object({
  object: z45.literal("device"),
  schema: z45.literal(1),
  id: DeviceIdSchema,
  name: z45.string().optional(),
  platform: DevicePlatformSchema,
  platformVersion: z45.string().optional(),
  appVersion: z45.string().optional(),
  appId: z45.string().optional(),
  currentUserId: UserIdSchema.nullish(),
  pushToken: z45.string().nullish(),
  pushTokenUpdatedAt: TimestampSchema.nullish(),
  updatedAt: TimestampSchema,
  createdAt: TimestampSchema.optional()
});

// src/domain_event.types.ts
import * as z59 from "zod";

// src/equipment.types.ts
import * as z56 from "zod";

// src/citation.types.ts
import * as z46 from "zod";
var CitationDetailsSchema = z46.object({
  url: z46.string(),
  domain: z46.string(),
  title: z46.string().optional(),
  description: z46.string().optional(),
  favicon: z46.string().optional(),
  // Display hint only; an unknown kind reads as absent so a new citation type never fails the
  // equipment/model parse on a reader compiled before it.
  type: z46.enum(["url", "pdf"]).optional().catch(void 0)
});

// src/guide.types.ts
import * as z47 from "zod";
var SpaceGuideContextSchema = z47.object({ type: z47.literal("space"), spaceId: SpaceIdSchema });
var EquipmentGuideContextSchema = z47.object({
  type: z47.literal("equipment"),
  typeId: EquipmentTypeIdSchema
});
var GuideContextSchema = z47.discriminatedUnion("type", [SpaceGuideContextSchema, EquipmentGuideContextSchema]);
var GuideNodeIdSchema = z47.string().min(1).brand();
var GuideNodeSchema = z47.object({
  id: GuideNodeIdSchema,
  data: AppNodeDataSchema
});
var GuideEdgeSchema = z47.object({
  id: z47.string(),
  sourceHandleId: OutgoingHandleSchema,
  target: GuideNodeIdSchema,
  data: AppEdgeDataSchema
});
var HandleCrumbSchema = z47.object({
  type: z47.literal("handle"),
  sourceNode: GuideNodeSchema,
  edge: GuideEdgeSchema,
  targetNode: GuideNodeSchema
});
var SubRouteCrumbSchema = z47.object({
  type: z47.literal("sub-route"),
  node: GuideNodeSchema,
  fromSubRoute: z47.string().optional(),
  toSubRoute: z47.string()
});
var GuideCrumbSchema = z47.discriminatedUnion("type", [HandleCrumbSchema, SubRouteCrumbSchema]);
var GuideSchema = z47.object({
  object: z47.literal("guide"),
  schema: z47.literal(1),
  id: z47.string(),
  space: SpaceIdSchema,
  equipmentTypeId: EquipmentTypeIdSchema.optional(),
  flowId: z47.string(),
  name: z47.string(),
  nodes: z47.array(GuideNodeSchema),
  edges: z47.array(GuideEdgeSchema),
  publishedAt: TimestampSchema,
  /**
   * Present (and always `true`) only on copies living in the `_draft/db` mirror — here a staged
   * preview awaiting publish. Stamped/stripped by the draft layer choke points; MUST be declared
   * here or parsing strips it on read-modify-write. Live docs never carry it.
   */
  _draft: z47.literal(true).optional()
});
var GuideValueSchema = z47.union([
  z47.string(),
  z47.boolean(),
  z47.number(),
  z47.array(z47.string()),
  z47.record(DataKeySchema, z47.unknown()),
  ImageSchema
]);
var GuideValuesSchema = z47.record(DataKeySchema, GuideValueSchema);
var GuideEquipmentComponentSchema = z47.object({
  type: z47.literal("equipment"),
  equipmentTypeId: EquipmentTypeIdSchema
});
var GuideInfoComponentSchema = z47.object({
  type: z47.literal("info"),
  infoTypeId: InfoTypeIdSchema
});
var GuideConsiderationComponentSchema = z47.object({
  type: z47.literal("consideration"),
  considerationTypeId: ConsiderationTypeIdSchema
});
var DEFAULT_EQUIPMENT_SPACE_ID = "default-equipment";
var AppFlowParamsSchema = z47.object({
  spaceId: SpaceIdSchema,
  equipmentTypeId: EquipmentTypeIdSchema.optional()
});
function getGuideType(params) {
  if (params.spaceId === DEFAULT_EQUIPMENT_SPACE_ID) return "equipment";
  return params.equipmentTypeId ? "equipment" : "space";
}
var AppFlowObjectSchema = z47.object({
  object: z47.literal("app-flow"),
  schema: z47.literal(1),
  space: SpaceIdSchema,
  equipmentTypeId: EquipmentTypeIdSchema.optional(),
  id: z47.string(),
  name: z47.string(),
  nodes: z47.array(z47.unknown()),
  // AppNode from rock-admin
  edges: z47.array(z47.unknown()),
  // AppEdge from rock-admin
  nextId: z47.number().default(0)
});

// src/home.types.ts
import * as z51 from "zod";

// src/property_lookup.types.ts
import * as z48 from "zod";
var PropertyLookupRequestIdSchema = z48.string().brand();
var PropertyLookupResultSchema = z48.object({
  yearBuilt: z48.number().optional(),
  squareFeet: z48.number().optional(),
  stories: z48.number().optional(),
  hasBasement: z48.boolean().optional(),
  bedrooms: z48.number().optional(),
  bathrooms: z48.number().optional(),
  heatingType: z48.string().optional(),
  coolingType: z48.string().optional(),
  roofType: z48.string().optional(),
  constructionType: z48.string().optional(),
  lotAcres: z48.number().optional(),
  propertyType: z48.string().optional(),
  fireplace: z48.boolean().optional(),
  // ATTOM's `saleTransDate`, already a calendar day (`YYYY-MM-DD`); normalized at the API boundary.
  lastSaleDate: LocalDateSchema.optional(),
  lastSaleAmount: z48.number().optional()
});
var PropertyLookupStatusSchema = z48.enum(["requested", "in-progress", "complete", "error"]);
var PropertyLookupRequestSchema = z48.object({
  object: z48.literal("property-lookup-request"),
  schema: z48.literal(1),
  id: PropertyLookupRequestIdSchema,
  homeId: HomeIdSchema,
  streetAddress: z48.string().min(1),
  city: z48.string().optional(),
  state: z48.string().optional(),
  zip: z48.string(),
  status: PropertyLookupStatusSchema,
  updatedAt: TimestampSchema,
  result: PropertyLookupResultSchema.optional(),
  rawPropertyResult: z48.record(z48.string(), z48.unknown()).optional(),
  rawSaleResult: z48.record(z48.string(), z48.unknown()).optional(),
  error: z48.string().optional()
});

// src/recall_topic.types.ts
import * as z49 from "zod";
var RecallTopicIconSchema = z49.enum([
  "waves",
  "baby",
  "toy-brick",
  "bed-double",
  "accessibility",
  "flame",
  "battery-charging",
  "pill",
  "bike",
  "shovel",
  "tractor",
  "dumbbell",
  "lightbulb",
  "shield",
  "house",
  "plug"
]);
var RecallTopicSchema = z49.object({
  object: z49.literal("recall-topic"),
  schema: z49.literal(1),
  id: RecallTopicIdSchema,
  title: z49.string().min(1),
  /** What the topic covers, in a sentence: shown to the homeowner and to the classifier. */
  description: z49.string().min(1),
  /** Ascending sort key for lists; ties fall back to the title. */
  order: z49.number(),
  /** The app shows a shield when absent, or when it doesn't know the icon yet. */
  icon: RecallTopicIconSchema.optional().catch(void 0),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var RecallTopicFollowSchema = z49.object({ since: LocalDateSchema });
var RecallTopicFollowsSchema = z49.record(RecallTopicIdSchema, RecallTopicFollowSchema);
function sortRecallTopics(topics) {
  return [...topics].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
}

// src/setup_checklist.types.ts
import * as z50 from "zod";
var SETUP_CHECKLIST_USER_ITEMS = ["notifications", "ask-rock", "weekends"];
var SETUP_CHECKLIST_HOME_ITEMS = ["home-photo", "rooms", "spaces"];
var SetupChecklistUserItemSchema = z50.enum(SETUP_CHECKLIST_USER_ITEMS);
var SetupChecklistHomeItemSchema = z50.enum(SETUP_CHECKLIST_HOME_ITEMS);
var SetupChecklistItemSchema = z50.enum([...SETUP_CHECKLIST_USER_ITEMS, ...SETUP_CHECKLIST_HOME_ITEMS]);
var SETUP_CHECKLIST_RECORDED_ITEMS = ["home-photo", "weekends"];
var SetupChecklistRecordedItemSchema = z50.enum(SETUP_CHECKLIST_RECORDED_ITEMS);
var SetupChecklistOutcomeSchema = z50.enum(["done", "skipped"]);
var SetupChecklistMarkerSchema = z50.object({
  outcome: SetupChecklistOutcomeSchema,
  at: TimestampSchema,
  /** Who resolved it, on a home item; a user item is the user's own. */
  by: UserIdSchema.optional()
});
var UserSetupChecklistSchema = z50.partialRecord(SetupChecklistUserItemSchema, SetupChecklistMarkerSchema);
var HomeSetupChecklistSchema = z50.partialRecord(SetupChecklistHomeItemSchema, SetupChecklistMarkerSchema);

// src/home.types.ts
var SpaceSummaryStatusSchema = z51.enum(["not-started", "in-progress", "finished"]);
var SpaceSummaryFinishedViaSchema = z51.enum(["guide", "skipped", "equipment-added", "info-added"]);
var SpaceSummarySchema = z51.object({
  blockCount: z51.number().default(0),
  status: SpaceSummaryStatusSchema,
  finishedVia: SpaceSummaryFinishedViaSchema.optional()
});
var HomeLifecycleSchema = z51.enum(["setting-up", "reviewing", "active"]);
var WeekendStartSchema = z51.number().int().min(0).max(6);
var HomeSchema = z51.object({
  object: z51.literal("home"),
  schema: z51.literal(1),
  id: HomeIdSchema,
  owner: UserIdSchema,
  // Everyone the home belongs to, the owner included: THE membership check for rules and queries. Never a
  // member's write: the home's own creation and the server (`syncHomeMembershipOnHome`, the membership
  // callables) keep it beside `members/*`. Read through `homeMemberIds`, which always counts the owner.
  memberIds: z51.array(UserIdSchema).optional(),
  active: z51.boolean(),
  name: z51.string().min(1),
  zip: z51.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code"),
  geo: z51.object({
    found: z51.boolean().optional(),
    city: z51.string().optional(),
    state: z51.string().optional(),
    country: z51.string().optional(),
    latitude: z51.number().optional(),
    longitude: z51.number().optional(),
    timezone: z51.string().optional()
  }).optional(),
  // The home's own weekend (`WeekendStartSchema`): its checklist week and the timing of its weekend email
  // and push. Read through `homeWeekConfig` (`@rock/util`); an absent or bad value reads as Saturday.
  weekendStart: WeekendStartSchema.optional().catch(void 0),
  propertyData: PropertyLookupResultSchema.optional(),
  spaceImages: z51.record(SpaceIdSchema, z51.string()).optional(),
  // `true` on homes belonging to seeded E2E accounts, `false` on real ones — written on every home
  // so it can be queried server-side. Mirrors `User.test`; see that field for the rationale.
  test: z51.boolean().optional(),
  // Stamped at creation (`BaseHomeDao.createHome`); backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional(),
  waitlistedAt: TimestampSchema.optional(),
  lastAccessedAt: TimestampSchema.optional(),
  spaceSummary: z51.record(SpaceIdSchema, SpaceSummarySchema).default({}),
  doneAddingRooms: z51.boolean().optional(),
  // The home-scoped setup-checklist markers, and the card's dismissal — dismissing is a shared
  // decision for everyone in the home, like `doneAddingRooms`. See `setup_checklist.types.ts`.
  setupChecklist: HomeSetupChecklistSchema.optional(),
  setupChecklistDismissedAt: TimestampSchema.optional(),
  setupChecklistDismissedBy: UserIdSchema.optional(),
  // The recall topics the home follows, each from its own start date: the approved recalls under one,
  // published since then, reach it as FYIs. A shared choice for everyone in the home, written one topic at a
  // time (dot-path), so two members can't drop each other's.
  recallTopicFollows: RecallTopicFollowsSchema.optional(),
  // The followed topic ids again, kept in step with `recallTopicFollows` by BaseHomeDao: the array the server
  // queries homes by (`array-contains-any` can't match map keys).
  recallTopicIds: z51.array(RecallTopicIdSchema).optional(),
  // Absent on homes created before the lifecycle existed — those are `active` by contract.
  // Always read through homeLifecycle(); never default this in the schema (a full-doc rewrite
  // by an older client would materialize the fallback and mask a missing migration).
  lifecycle: HomeLifecycleSchema.optional(),
  // Stamped by the lifecycle transitions (BaseHomeDao.submitHomeForReview / acceptHomePlan).
  submittedForReviewAt: TimestampSchema.optional(),
  planAcceptedAt: TimestampSchema.optional(),
  planAcceptedBy: UserIdSchema.optional(),
  // Set server-side when the post-submit eval settles (markHomePlanReady); cleared on each
  // submit so a re-review waits for its own eval. The review screen waits on this.
  planReadyAt: TimestampSchema.optional(),
  // The review doc (homes/{homeId}/plan-reviews/{id}) for the CURRENT submit. Set by
  // BaseHomeDao.submitHomeForReview, cleared by returnHomeToSetup, left in place by acceptHomePlan
  // (history). Progress only — `planReadyAt` stays the single durable "plan is presentable" signal.
  planReviewId: PlanReviewIdSchema.optional()
});
function isHomeMember(home, userId) {
  return homeMemberIds(home).includes(userId);
}
function homeMemberIds(home) {
  return [.../* @__PURE__ */ new Set([home.owner, ...home.memberIds ?? []])];
}
function homeLifecycle(home) {
  return home.lifecycle ?? "active";
}
function finishedSpaceIds(home) {
  return Object.entries(home.spaceSummary).filter(([, summary]) => summary.status === "finished").map(([spaceId]) => SpaceIdSchema.decode(spaceId));
}
var HomeSpaceSchema = z51.object({
  object: z51.literal("home-space"),
  schema: z51.literal(1),
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  guideVisitIds: z51.array(GuideVisitIdSchema).optional(),
  status: SpaceSummaryStatusSchema.optional(),
  // Stamped at creation; backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional()
});
var SystemInfoSchema = z51.object({
  object: z51.literal("system-info"),
  schema: z51.literal(1),
  id: SystemInfoIdSchema,
  active: z51.boolean(),
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  typeId: InfoTypeIdSchema,
  inputValue: GuideValueSchema.optional(),
  // Stamped at creation; backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional(),
  /** Who first answered it. */
  createdBy: UserIdSchema.optional()
});

// src/info_type.types.ts
import * as z53 from "zod";

// src/space.types.ts
import * as z52 from "zod";
var SpaceGroupIdSchema = z52.enum([
  "whole",
  "exterior",
  "kitchen",
  "laundry",
  "bathroom",
  "living",
  "attic",
  "garage"
]);
var SpaceSchema = z52.object({
  object: z52.literal("space"),
  schema: z52.literal(1),
  id: SpaceIdSchema,
  groupId: SpaceGroupIdSchema,
  published: z52.boolean(),
  name: z52.string().min(3),
  systemTitle: z52.string().min(3),
  icon: z52.string(),
  intro: z52.array(
    z52.object({
      text: z52.string().min(1)
    })
  ).min(1),
  addEquipmentLabel: z52.string().optional(),
  contactsTitle: z52.string().optional(),
  contactsEmptyText: z52.string().optional()
});
var SpaceGroupLabels = {
  whole: "General Info",
  exterior: "Exterior",
  kitchen: "Kitchen",
  laundry: "Laundry",
  bathroom: "Bathroom",
  living: "Living Spaces",
  attic: "Attic / Basement",
  garage: "Garage / Workshop"
};
var DashboardSpaceIdSchema = z52.enum([
  "home",
  "hvac",
  "appliances",
  "plumbing",
  "safety",
  "electrical",
  // 'structure',
  "surfaces",
  "garage"
]);
var HOME_SPACE_ID = SpaceIdSchema.parse("home");
var DashboardSpaceLabels = {
  home: "General Info",
  hvac: "Heating and Cooling",
  appliances: "Appliances",
  plumbing: "Plumbing",
  safety: "Safety and Security",
  electrical: "Electrical",
  // structure: 'Structure',
  surfaces: "Surfaces",
  garage: "Garage / Other"
};

// src/info_type.types.ts
var KEY_INFO_TITLE = "Key Information";
var INFO_TYPES = {
  home: {
    numberOfStories: {
      spaceId: HOME_SPACE_ID,
      typeId: "multilevel-home"
    },
    hasBasement: {
      spaceId: HOME_SPACE_ID,
      typeId: "basement"
    },
    yearBuilt: {
      spaceId: HOME_SPACE_ID,
      typeId: "year-home-built-0"
    },
    squareFootage: {
      spaceId: HOME_SPACE_ID,
      typeId: "home-sq-footage"
    },
    purchaseDate: {
      spaceId: HOME_SPACE_ID,
      typeId: "year-home-purchased-0"
    },
    address: {
      spaceId: HOME_SPACE_ID,
      typeId: "home-address"
    }
  }
};
var InfoTypeSchema = z53.object({
  object: z53.literal("info-type"),
  schema: z53.literal(1),
  id: InfoTypeIdSchema,
  spaceId: SpaceIdSchema,
  title: z53.string().min(1),
  helpText: z53.string().optional(),
  /** How a card asks it ("Does your home have a pool?"); the `title` is a noun ("Pool Type"). */
  question: z53.string().optional(),
  /**
   * Ask homes that already finished this type's space through a dashboard card, set when the guide
   * starts asking it, so a home past the guide is asked too. `since` records when it was turned on;
   * `when` is an HQL condition the home must meet to be asked.
   */
  askExistingHomes: z53.object({ since: TimestampSchema, when: z53.string().optional() }).optional(),
  ...FieldMixinSchema.shape
});

// src/model.types.ts
import * as z54 from "zod";
var ModelCanonicalSchema = z54.string().min(1).max(200).regex(/^[A-Z0-9]+$/, "Canonical model values may only contain uppercase letters and numbers");
var ModelVariantKeySchema = z54.string().min(1).max(200).regex(/^[A-Z0-9]+$/, "Variant keys may only contain uppercase letters and numbers");
var FactSourceSchema = z54.enum(["research", "decoded", "ocr", "vision", "user", "document", "admin"]);
var ModelAttributeSchema = z54.object({
  label: z54.string(),
  value: z54.string(),
  // The code of the equipment type's declared spec this row answers, when the extraction was handed a
  // list. A plain string, not `CodeWordSchema`: this is the AI's answer, and validating its shape here
  // fails the WHOLE research payload — brand, type, identity and every other row — over one stray
  // character, after paying for it (prod holds `specId: "1"`, a citation index). Writers fold it
  // (`sanitizeModelDetails`), readers match it folded and fall back to the label when it names nothing
  // declared (`consolidateSpecs`).
  specId: z54.string().nullable().optional().describe("The code of the declared spec this row answers, when a list of specs was given.")
});
var ModelDetailsSchema = z54.object({
  brand: BrandNameSchema.optional(),
  // A BARE equipment category only. The enrichment task is free-text, so the model occasionally answers
  // with editorial commentary + citation markers ("Pool Pump (not Central AC as misstated)[1]"); the
  // description tightens the structured-output contract and `sanitizeModelType` scrubs anything that slips
  // through on write. Kept loose (`.string()`, no `.max`) so a legacy polluted doc still parses on read.
  type: z54.string().describe(
    'A short, bare equipment type/category ONLY \u2014 e.g. "Central AC", "Pool Pump", "Tankless Water Heater". No commentary, corrections, explanations, or citation markers.'
  ).optional(),
  attributes: z54.array(ModelAttributeSchema)
});
var ModelKnownAsKindSchema = z54.enum(["productName", "family", "baseModel", "catalogNumber"]);
var ModelKnownAsSchema = z54.object({
  name: z54.string(),
  kind: ModelKnownAsKindSchema,
  sourceUrl: z54.string().optional(),
  confirmed: z54.boolean().optional()
});
var ModelBrandSuggestionSchema = z54.object({
  name: z54.string(),
  /** The evidence page the brand was extracted from. */
  sourceUrl: z54.string().optional(),
  /**
   * The `brand-requests` entry this name is waiting on, when the name is not in the catalog. Resolving
   * that request links the model (fill-only) and clears this suggestion.
   */
  requestId: BrandRequestIdSchema.optional(),
  /**
   * `observed` = read off the unit (typed, photo AI, a manufacturer page the resolve task read); the
   * request fan-out links these. `researched` = a web-enrichment guess with no brand-owned citation — it
   * stays a suggestion for a human even once the name is cataloged (the white-label guard). Absent on
   * suggestions written before this existed and on the manuals worker's (`resolveIdentity`), which the
   * fan-out treats as researched: those never reach the queue, a human confirms them on the model.
   */
  provenance: z54.enum(["observed", "researched"]).optional(),
  at: TimestampSchema
});
var ModelIdentityExtractionSchema = z54.object({
  productName: z54.string().nullish(),
  family: z54.string().nullish(),
  baseModel: z54.string().nullish(),
  alternateNumbers: z54.array(z54.string()).nullish()
});
var SupersededViaSchema = z54.enum(["replaced", "rule"]);
var ModelDocumentLinkSchema = z54.object({
  /** The linked document's id (content hash) — equals this entry's map key. */
  id: ModelDocumentIdSchema,
  /**
   * How the link was made: `direct` = a human published this document on this model in rock-admin (or
   * auto-confirm did, explicitly enabled); `coverage` = a coverage rule matched the model's number and
   * linked it automatically. Provenance that makes a bad coverage rule unwindable without touching
   * human-made links.
   */
  via: z54.enum(["direct", "coverage"]),
  /** When this model gained the document (the per-model vouch/apply time). */
  linkedAt: TimestampSchema,
  /**
   * Set when a newer confirmed document replaces this one FOR THIS MODEL; points at the successor's
   * map entry. Superseded links stay in the library (correct-but-outdated is knowledge worth keeping,
   * with provenance) but drop out of every user-facing read (`appVisibleDocuments`, `documentsTagged`).
   * Per-model on purpose: a model-specific manual outranks a family one HERE without demoting the
   * family document anywhere else.
   */
  supersededBy: ModelDocumentIdSchema.optional(),
  /** Who set `supersededBy` (see `SupersededViaSchema`). Absent = a human did. */
  supersededVia: SupersededViaSchema.optional(),
  /**
   * Set (only ever `true`) when this document went live on this model WITHOUT announcing it — the
   * `reconcileModel` planner reads it off the live owner manual's link and skips the
   * `model-manual-added` notification (`notify-manual` step). Provenance of a deliberate silent publish
   * (e.g. re-publishing a manual under a corrected model number whose owners were already notified, or
   * a coverage backfill sweep). Absent = notify normally.
   */
  publishedSilently: z54.boolean().optional()
});
var ModelIdentityKeyedBySchema = z54.enum(["model-number", "product-name"]);
var ModelIdentitySchema = z54.object({
  /** When the identify work last ran to completion. Absent = it hasn't (or predates the field). */
  concludedAt: TimestampSchema.optional(),
  /**
   * What `modelCanonical` IS: a model number (the default, and what every model written before the
   * field existed is), or — for a product sold with no model number — the folded name it was sold under
   * (`normalizeNameKey('Star Treatment 2.0')`), with `modelDisplay` holding the name as written. A
   * name-keyed model is a family-level identity: the manual hunt searches `{brand} {name}`, the verifier
   * matches the name (it also lands in `knownAs` as `productName`), and a later exact nameplate scan
   * resolves to its own numbered model. Written on mint, never changed.
   */
  keyedBy: ModelIdentityKeyedBySchema.optional()
});
var ModelEnrichmentSchema = z54.object({
  /** The `equipment-details` prompt version that produced `details`. */
  promptVersion: z54.number().int().nonnegative(),
  /** The type's declared spec codes the prompt was asked to fill — folded, sorted; empty when free-form. */
  specCodes: z54.array(z54.string()),
  /**
   * The catalog brand the model carried when the research ran — `null` when it had none, ABSENT on a
   * pass recorded before this field existed (so the two cases stay distinguishable). The research is
   * anchored on the brand, so a brand that lands or changes afterwards (an admin correction, a brand
   * request resolving) means the cached answer was to a different question: `researchStaleReason`
   * re-buys once when this disagrees with `Model.brandId`.
   */
  brandId: BrandIdSchema.nullable().optional(),
  at: TimestampSchema
});
var ModelParkReasonSchema = z54.enum(["cant-find", "no-manual-exists", "unidentified"]);
var ModelParkSchema = z54.object({
  reason: ModelParkReasonSchema,
  at: TimestampSchema
});
var ModelRevisitReasonSchema = z54.enum(["new-home", "brand-linked"]);
var ModelRevisitSchema = z54.object({
  reason: ModelRevisitReasonSchema,
  at: TimestampSchema
});
var ModelDocStatusSchema = z54.enum([
  "missing",
  "searching",
  "needs-review",
  "published",
  "library-only",
  "search-failed",
  "ingest-failed",
  "archived",
  "parked",
  "revisit"
]);
var ModelSchema = z54.object({
  object: z54.literal("model"),
  schema: z54.literal(1),
  // An opaque auto id, minted once and never re-keyed — exactly as `brands/{autoId}` works. It encodes
  // NOTHING: not the space (two homes shelving one product differently share the doc), not the type
  // (two observers classifying one product differently must not fork it into two paid researches and
  // two paid hunts), not the brand (it arrives too late to mint with, and a brand-catalog error would
  // split a real product). A number resolves to its model through the `model-spellings` projection
  // alone (`ModelSpellingSchema`); composite ids from before 2026-09-09 were re-keyed by migration.
  id: ModelIdSchema,
  // The space of the FIRST observation — provenance/display only, never identity: it seeds prompt
  // context and orders admin pickers, but two homes shelving the same product differently still share
  // this doc (and this field keeps whichever space saw it first).
  spaceId: SpaceIdSchema,
  // Seeded by the first observation and correctable (rock-admin's type remap writes it in place). An
  // observation, not identity: `Equipment.typeId` no longer has to agree with it — two homes may file
  // one product differently and both stay right locally while sharing its research and its manual.
  // Coverage matching filters on it against a rule's `typeIds`, so a wrong type is a logged near-miss.
  typeId: EquipmentTypeIdSchema,
  brandId: BrandIdSchema.optional(),
  /** The brand's product line this model belongs to (`Brand.lines`), fill-only like `brandId`; display, never a gate. */
  lineId: z54.string().optional(),
  // Proposed brand awaiting human review in rock-admin (see ModelBrandSuggestionSchema).
  // Only ever meaningful while `brandId` is unset; linking a brand clears it.
  brandSuggestion: ModelBrandSuggestionSchema.optional(),
  // What the automatic identify work has settled (see ModelIdentitySchema). `concludedAt` is what the
  // ambient manual-hunt enqueue waits for, so brand resolution always precedes the first search.
  // Optional: models written before the field existed lack it, and the backfill migration stamps them.
  identity: ModelIdentitySchema.optional(),
  modelDisplay: ModelNumberSchema,
  modelCanonical: ModelCanonicalSchema,
  // Legacy: a discriminator the composite id once appended. Never written since 2026-09-09; kept so
  // older docs parse and rock-admin can still show it.
  variantKey: ModelVariantKeySchema.optional(),
  // Observed canonical spellings of THIS identifier (first entry is `modelCanonical`). OCR/typo
  // variants of the same model number — not alternate identities (see `knownAs`).
  spellings: z54.array(ModelCanonicalSchema),
  // Alternate identities of the same product (marketing/family name, base model, extra catalog numbers).
  // Drives manual search + verification + display; never used for spelling dedup.
  // Each entry parses independently (`.catch`): a `kind` added after a reader was compiled drops that
  // ONE entry on the old bundle instead of failing the whole Model — the app never branches on kind.
  // ⚠️ The drop is LOSSY against read-modify-write: `researchModel` merges onto what it
  // parsed, so a rock-functions instance older than the writer would ERASE entries carrying a kind it
  // can't read, not merely hide them. Deploy a new kind to rock-functions before anything writes one.
  knownAs: z54.array(ModelKnownAsSchema.optional().catch(void 0)).transform((entries) => entries.filter((entry) => !!entry)).optional(),
  details: ModelDetailsSchema.optional(),
  sources: z54.array(CitationDetailsSchema).optional(),
  /** Provenance of `details`: which prompt, asked to fill which specs. Written with `details`. */
  enrichment: ModelEnrichmentSchema.optional(),
  // LINKS to confirmed documents, keyed by content hash — pure pointers into the first-class
  // `documents/{id}` collection (the artifact lives there; see ModelDocumentLinkSchema). Only the
  // results of an admin publish / explicit auto-confirm / coverage application — acquisition state
  // lives in the admin-only subcollections. Written via per-link DAO field writes (FieldPath), never
  // full-map hand-assembly. Each entry parses independently (`.catch`): an entry an older compiled
  // schema can't read drops that entry alone instead of failing the whole Model parse — the selectors
  // already skip undefined map values.
  documents: z54.partialRecord(ModelDocumentIdSchema, ModelDocumentLinkSchema.optional().catch(void 0)).optional(),
  // Set when an admin archived this model — a junk IDENTITY (mis-scanned model number, test-device
  // artifact), archived rather than deleted so it works as a tombstone: the spelling projection still
  // resolves re-scans to it (no duplicate junk, no fresh paid manual hunt) and its subcollections stay
  // attached. The admin catalog hides archived models by default and the ambient manual enqueue skips
  // them; re-observation writes are partial updates, so the mark survives.
  archivedAt: TimestampSchema.optional(),
  /**
   * Set with `archivedAt` by a model merge (`mergeModels`): this model was folded into `mergedInto`,
   * its units repointed and its spellings, known-as and manual links carried over. A tombstone, as
   * `Brand.mergedInto` is for brands: the projection entry stays, and the join reads it as its winner
   * (`resolveMergedModel`), so a re-scan of the number lands on the winner. Never a recall candidate.
   */
  mergedInto: ModelIdSchema.optional(),
  // Set when an admin parked the manual hunt on a REAL product (see ModelParkSchema for the reasons).
  // Unlike `archivedAt` it hides nothing: the model stays in the catalog and keeps serving whatever
  // documents it has; only the ambient enqueue skips it (an explicit admin search still runs).
  // SURVIVES a revisit signal — the nudge lands in `revisit` beside it, and this verdict keeps
  // governing what any later signal is allowed to spend. Cleared only by an admin (Unpark, Archive).
  parked: ModelParkSchema.optional(),
  // Set by rock-functions when a settled model (parked, or a hunt that gave up) gets new information
  // worth a human's look — see ModelRevisitSchema. For a `cant-find` park the same signal also files
  // a system search request, so the worker may already have a candidate by the time anyone looks.
  // Cleared by the admin verdict that answers it.
  revisit: ModelRevisitSchema.optional(),
  // DERIVED documentation-status rollup (see ModelDocStatusSchema) — maintained by rock-functions
  // (`reconcileModel` on a model write, the `syncDocStatus*` triggers on a document or job write)
  // whenever any input changes, backfilled by rock-migrator. Never write it by hand: a direct write is overwritten on the next input change.
  // Writes deliberately don't bump `updatedAt` (a job heartbeat isn't a fact about the model).
  // Optional: pre-backfill docs lack it, and readers must treat absent as "not yet computed".
  // `.catch(undefined)`: an unknown member (a reader compiled before it existed) reads as absent
  // rather than failing the whole Model parse — nothing outside rock-admin branches on it.
  docStatus: ModelDocStatusSchema.optional().catch(void 0),
  /**
   * The `app/recalls-state.recallsEpoch` this model was last checked against every approved recall of its
   * brand (`check-recalls`, an enrichment step). Absent = never checked. An echo field: writing it must not
   * re-plan anything.
   */
  recallsCheckedEpoch: z54.number().optional(),
  /**
   * What the matcher read when `recallsCheckedEpoch` was stamped (`recallCheckKey`: brand, canonical
   * number, spellings). A model whose key has moved since is checked again at the same epoch. Absent =
   * re-check on the next write. An echo field, like the epoch.
   */
  recallsCheckedKey: z54.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
function pendingBrandRequestOf(model) {
  const suggestion = model.brandSuggestion;
  if (model.brandId || suggestion?.provenance !== "observed") return void 0;
  return suggestion.requestId;
}
var ModelSpellingEntrySchema = z54.object({
  modelId: ModelIdSchema,
  modelCanonical: ModelCanonicalSchema
});
var ModelSpellingSchema = z54.object({
  object: z54.literal("model-spelling"),
  schema: z54.literal(2),
  id: ModelSpellingKeySchema,
  entries: z54.array(ModelSpellingEntrySchema),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var LegacyModelSpellingSchema = z54.object({
  object: z54.literal("model-spelling"),
  schema: z54.literal(1),
  id: ModelSpellingKeySchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema,
  brandId: BrandIdSchema.optional(),
  modelId: ModelIdSchema,
  modelCanonical: ModelCanonicalSchema,
  modelDisplay: ModelNumberSchema,
  variantKey: ModelVariantKeySchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var MergeModelsRequestSchema = z54.object({
  loserId: ModelIdSchema,
  winnerId: ModelIdSchema,
  /** Count what would change without writing anything — the admin preview. */
  dryRun: z54.boolean()
});
var ModelMergePreviewSchema = z54.object({
  /** Units repointed from the loser to the winner (test homes included); each is decided against the winner's recalls. */
  units: z54.number().int(),
  /** The loser's recall matches dropped (their units' verdicts were withdrawn by the repoints). */
  recallMatches: z54.number().int(),
  /** Manual document links the winner gains; coverage links are the winner's own to derive. */
  documents: z54.number().int(),
  knownAs: z54.number().int(),
  spellings: z54.number().int(),
  /** The loser's type beside the winner's: its own, or one in its family. Unrelated types refuse. */
  typeAffinity: z54.enum(["type", "family"]),
  /**
   * The loser's brand beside the winner's. `replaced`: they differ and none of the loser's units names a
   * brand other than the winner's, so the loser's gives way; a unit that does refuses the merge.
   */
  brand: z54.enum(["same", "open", "replaced"]),
  /** The winner had no brand, so it gains the loser's. */
  carriesBrand: z54.boolean(),
  /** The winner had no research, so it gains the loser's. */
  carriesResearch: z54.boolean()
});
var MergeModelsResponseSchema = z54.discriminatedUnion("status", [
  z54.object({ status: z54.literal("refused"), reason: z54.string() }),
  z54.object({ status: z54.literal("preview"), preview: ModelMergePreviewSchema }),
  z54.object({ status: z54.literal("merged"), preview: ModelMergePreviewSchema })
]);
var AddModelRequestSchema = z54.object({
  /** What the model is called: the number off its plate, or the name it is sold under. */
  subject: z54.discriminatedUnion("kind", [
    z54.object({ kind: z54.literal("model-number"), modelNumber: z54.string().min(1) }),
    z54.object({ kind: z54.literal("product-name"), productName: z54.string().min(1) })
  ]),
  typeId: EquipmentTypeIdSchema,
  brandId: BrandIdSchema.nullish(),
  /** Off: the model is created parked, so no paid hunt arms for documents the admin brings. */
  findDocuments: z54.boolean()
});
var AddModelResponseSchema = z54.discriminatedUnion("status", [
  z54.object({ status: z54.literal("created"), modelId: ModelIdSchema }),
  /** The join handed the number to a model already in the catalog; nothing was written. */
  z54.object({ status: z54.literal("exists"), modelId: ModelIdSchema }),
  /** As `exists`, but the model is archived: an admin unarchives it rather than adding a duplicate. */
  z54.object({ status: z54.literal("archived"), modelId: ModelIdSchema }),
  /** The number is filed under several brands and the given one (or none) cannot pick. */
  z54.object({ status: z54.literal("ambiguous"), brandIds: z54.array(BrandIdSchema) })
]);

// src/task.types.ts
import * as z55 from "zod";
var RefreshIdSchema = z55.string().brand();
var RefreshBrandOptionSchema = z55.object({ brandId: BrandIdSchema, name: BrandNameSchema });
var TaskRefreshSchema = z55.object({
  object: z55.literal("task-refresh"),
  schema: z55.literal(1),
  id: RefreshIdSchema,
  /**
   * `needs-brand` ends an equipment refresh whose model number names several catalog brands' products
   * and whose own brand cannot pick one: the owner is asked (an `equipment-brand` home question), and
   * their pick re-arms it.
   */
  status: z55.enum(["requested", "in-progress", "complete", "error", "needs-brand"]),
  type: z55.string(),
  /**
   * Who asked. `user` is a person pressing a refresh control and is the only origin charged against a
   * daily cap (`equipment-refresh`); absent — the default — is the system refreshing because data it
   * enriches from changed, which must never be capped or a home walk would stop enriching halfway.
   */
  origin: z55.enum(["user", "system"]).optional(),
  updatedAt: TimestampSchema.optional(),
  eventId: z55.string().optional(),
  error: z55.string().optional(),
  details: z55.record(z55.string(), z55.unknown()).optional()
});
var LastPerformedEstimateSchema = z55.enum([
  "last-few-weeks",
  "last-few-months",
  "within-last-year",
  "more-than-a-year",
  "never",
  "not-sure",
  // Exact calendar day — carries its own `lastPerformedAt` (a LocalDate, no time). Kept
  // distinct from the coarse buckets so callers must pick one representation or the other.
  "specific-date"
]);
var TaskSubscriptionIntervalUnitSchema = z55.enum(["days", "weeks", "months", "years"]);
var TaskSubscriptionIntervalSchema = z55.object({
  value: z55.number().positive(),
  unit: TaskSubscriptionIntervalUnitSchema
});
var SeasonPartSchema = z55.enum([
  "early-spring",
  "spring",
  "late-spring",
  "early-summer",
  "summer",
  "late-summer",
  "early-fall",
  "fall",
  "late-fall",
  "early-winter",
  "winter",
  "late-winter"
]);
var SeasonalWindowSchema = z55.array(SeasonPartSchema).describe(
  'Parts of the year the occurrences should land in; null unless a season is named. A sub-year interval needs one separate season per occurrence (every 6 months \u2192 e.g. ["spring", "fall"]); on a yearly cadence several seasons mean either one.'
);
var TaskSubscriptionOneTimeSchema = z55.object({
  type: z55.literal("one-time"),
  offset: TaskSubscriptionIntervalSchema.nullish(),
  // Delay before task is created
  window: SeasonalWindowSchema.nullish()
});
var TaskSubscriptionRecurringSchema = z55.object({
  type: z55.literal("recurring"),
  interval: TaskSubscriptionIntervalSchema,
  offset: TaskSubscriptionIntervalSchema.nullish(),
  // Delay before first task is created
  window: SeasonalWindowSchema.nullish()
});
var TaskSubscriptionSchema = z55.discriminatedUnion("type", [
  TaskSubscriptionOneTimeSchema,
  TaskSubscriptionRecurringSchema
]);
var TaskSupplyChangeSchema = z55.object({
  equipmentId: EquipmentIdSchema,
  spaceId: SpaceIdSchema,
  quantityDelta: z55.number()
});
var TaskImageOptionSchema = withSingularArrayFields(
  ImageObjectSchema.extend({
    category: TaskScreenPictureSlotCategorySchema.optional()
  }),
  [["highlight", "highlights"]]
);
var TaskEffortSchema = z55.number().int().min(1).max(4);
var TaskTypeSchema = z55.object({
  object: z55.literal("task-type"),
  schema: z55.literal(1),
  id: TaskTypeIdSchema,
  title: z55.string().min(1),
  considerationTypeId: ConsiderationTypeIdSchema,
  subscription: TaskSubscriptionSchema.optional(),
  effort: TaskEffortSchema.optional(),
  taskDocId: RockDocIdSchema,
  overview: z55.string().optional(),
  // AI instructions for generating subscription from "How Often?" field
  aiHowOftenInstructions: z55.string().optional(),
  // Task version tracking for re-running on version changes
  currentHowOftenTaskVersion: z55.number().optional(),
  requestedHowOftenTaskVersion: z55.number().optional(),
  taskScreenConfig: TaskTypeScreenConfigSchema.optional(),
  // Review status from Notion
  documentStatus: DocumentStatusSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var CustomTaskTypeSchema = TaskTypeSchema.extend({
  object: z55.literal("custom-task-type"),
  schema: z55.literal(1),
  homeId: HomeIdSchema,
  createdBy: UserIdSchema,
  considerationTypeId: ConsiderationTypeIdSchema.nullish(),
  taskDocId: RockDocIdSchema.nullish(),
  subscription: TaskSubscriptionSchema,
  // required for custom
  // Which equipment the reminder covers, as an HQL query (`@rock/hql`): `equipment[id == "…"]` for
  // one picked unit, `home` for the whole home. Absent on reminders made before queries; their
  // subscription's legacy `equipmentId` says instead.
  appliesTo: z55.string().optional()
});
var HomeTaskSubscriptionSchema = z55.object({
  object: z55.literal("home-task-subscription"),
  schema: z55.literal(1),
  id: HomeTaskSubscriptionIdSchema,
  homeId: HomeIdSchema,
  taskTypeId: TaskTypeIdSchema,
  // Nullish for user-created custom tasks, which have no source Consideration.
  considerationId: ConsiderationIdSchema.nullish(),
  considerationTypeId: ConsiderationTypeIdSchema.nullish(),
  subscription: TaskSubscriptionSchema,
  // Copied from the task type at accept, like `subscription`, so pricing a reminder's chores never
  // needs the catalog (a custom or retired type has nothing to read).
  effort: TaskEffortSchema.optional(),
  // Legacy: the one unit a pre-query, per-unit reminder was for. Read through the coverage function
  // (`@rock/hql` `coverageOf`), never written.
  equipmentId: EquipmentIdSchema.optional(),
  // A one-time reminder's units, copied from its consideration at accept. A recurring reminder records
  // nothing; it covers whatever its query matches now.
  coveredEquipmentIds: z55.array(EquipmentIdSchema).optional(),
  spaceId: SpaceIdSchema.optional(),
  contactId: ContactIdSchema.optional(),
  status: z55.enum(["active", "paused", "orphaned", "deleted"]).default("active"),
  // Who paused it, while it is paused; both leave with the resume.
  pausedAt: TimestampSchema.optional(),
  pausedBy: UserIdSchema.optional(),
  lastPerformedEstimate: LastPerformedEstimateSchema.optional(),
  // Calendar day the task was last performed (`YYYY-MM-DD`, no time) — set only when
  // `lastPerformedEstimate` is `'specific-date'`. Anchors the first reminder ahead of the
  // coarse-bucket back-dating (see resolveLastPerformedAt).
  lastPerformedAt: LocalDateSchema.optional(),
  lastGeneratedAt: TimestampSchema.optional(),
  // Calendar day the next occurrence is scheduled for (`YYYY-MM-DD`, no time). A day a human
  // schedules, not a precise instant — compared as a plain string in queries and date math.
  nextTaskDate: LocalDateSchema.nullish(),
  // Latest notes for tasks generated from this subscription. Copied to each new task at
  // generation time so historical task.notes are preserved.
  taskNotes: z55.string().optional(),
  createdAt: TimestampSchema,
  // Who added it; absent when Rock did.
  createdBy: UserIdSchema.optional(),
  updatedAt: TimestampSchema
});
var TaskStatusSchema = z55.enum(["pending", "completed", "skipped", "orphaned"]);
var TaskSchemaV1 = z55.object({
  object: z55.literal("task"),
  schema: z55.literal(1),
  id: TaskIdSchema,
  taskTypeId: TaskTypeIdSchema,
  // Nullish for tasks generated from user-created custom subscriptions.
  considerationTypeId: ConsiderationTypeIdSchema.nullish(),
  homeId: HomeIdSchema,
  subscriptionId: HomeTaskSubscriptionIdSchema.optional(),
  taskGenerationJobRunId: TaskGenerationJobRunIdSchema.optional(),
  previousCompletedTaskId: TaskIdSchema.optional(),
  spaceId: SpaceIdSchema.optional(),
  // Legacy: the one unit a pre-query, per-unit task was for.
  equipmentId: EquipmentIdSchema.optional(),
  // The units the task covered when it was completed, so each unit's history stays accurate after the
  // reminder's coverage changes.
  coveredEquipmentIds: z55.array(EquipmentIdSchema).optional(),
  contactId: ContactIdSchema.optional(),
  // Calendar day the task is scheduled for (`YYYY-MM-DD`, no time). See nextTaskDate.
  taskDate: LocalDateSchema,
  status: TaskStatusSchema.default("pending"),
  completedAt: TimestampSchema.optional(),
  // Who finished or skipped it; absent when Rock did.
  completedBy: UserIdSchema.optional(),
  skippedAt: TimestampSchema.optional(),
  skippedBy: UserIdSchema.optional(),
  notes: z55.string().optional(),
  images: z55.array(TaskImageOptionSchema).optional(),
  taskScreenConfig: TaskScreenConfigSchema.optional(),
  supplyChanges: z55.array(TaskSupplyChangeSchema).optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var TaskSchemaV2 = z55.object({
  ...TaskSchemaV1.omit({ images: true }).shape,
  schema: z55.literal(2),
  /** Keyed by image id. Read via `orderedImages`/`primaryImage`, never directly. */
  images: z55.record(z55.string(), TaskImageOptionSchema).optional(),
  /** Image ids in display order; first = primary. See `orderedImages` for drift tolerance. */
  imageOrder: z55.array(z55.string()).optional()
});
function isTaskV1(val) {
  return isObjectSchema(val, "task", 1);
}
function isTaskV2(val) {
  return isObjectSchema(val, "task", 2);
}
function migrateTaskV1ToV2(val) {
  const { images, ...rest } = val;
  const v2 = val;
  const collection = Array.isArray(images) ? toImageCollection(images) : { images: v2.images, imageOrder: v2.imageOrder };
  return {
    ...rest,
    ...collection,
    schema: 2
  };
}
var TaskSchema = z55.preprocess((val, ctx) => {
  if (isTaskV2(val)) {
    return Array.isArray(val.images) ? { ...val, ...toImageCollection(val.images) } : val;
  }
  if (isTaskV1(val)) {
    return migrateTaskV1ToV2(val);
  }
  if (isObjectType(val, "task")) {
    return { ...val, schema: 2 };
  }
  ctx.addIssue({
    code: "custom",
    message: "Object is not a task",
    input: val
  });
  return z55.NEVER;
}, TaskSchemaV2);
var TaskDataSchema = z55.object({
  task: TaskSchema,
  subscription: HomeTaskSubscriptionSchema,
  type: z55.discriminatedUnion("object", [TaskTypeSchema, CustomTaskTypeSchema]),
  // Computed metadata for convenience
  isCurrentWeek: z55.boolean().optional(),
  isNextWeek: z55.boolean().optional(),
  isFutureWeek: z55.boolean().optional()
});
var WeeklyTasksSchema = z55.object({
  data: z55.array(TaskDataSchema),
  week: z55.object({
    start: TimestampSchema,
    end: TimestampSchema,
    weekend: TimestampSchema
  }),
  // Computed metadata for convenience
  isCurrentWeek: z55.boolean(),
  isCurrentWeekend: z55.boolean()
});
var ProcessHowOftenResultSchema = z55.object({
  object: z55.literal("process-how-often-result"),
  schema: z55.literal(1),
  id: ProcessHowOftenResultIdSchema,
  taskTypeId: TaskTypeIdSchema,
  subscriptionChanged: z55.boolean(),
  result: z55.object({
    subscription: TaskSubscriptionSchema.optional(),
    summary: z55.string(),
    issues: z55.array(z55.string()).nullish()
  }),
  aiContext: z55.object({
    platform: z55.literal("openai"),
    model: z55.string(),
    systemMessage: z55.string(),
    userMessage: z55.string()
  }),
  createdAt: TimestampSchema
});

// src/equipment.types.ts
var BrandRefSchema = z56.object({
  brandId: BrandIdSchema,
  /** A product line of that brand (Phase 3: `Brand.lines`), when the pick was a line row. */
  lineId: z56.string().optional()
});
var EquipmentFieldsSchema = z56.intersection(
  z56.object({
    modelNumber: ModelNumberSchema.optional(),
    serialNumber: SerialNumberSchema.optional(),
    roomId: RoomIdSchema.optional(),
    brand: BrandNameSchema.optional(),
    /**
     * The catalog brand the homeowner PICKED from the typeahead, when they picked one — what the
     * text alone can't say for a shared nameplate (two "Maytag" rows) or a product line. Cleared
     * whenever the brand text is edited; the server treats it as an observed, authoritative brand
     * once it confirms the text still folds to one of that brand's names.
     */
    brandRef: BrandRefSchema.optional(),
    /**
     * The name a product was sold under, for equipment with NO model number to scan — an assembled or
     * white-label product whose only nameplate belongs to an OEM part ("Star Treatment 2.0"). An
     * observation like `modelNumber`: with no model number it is what the enrichment resolves a
     * product from (a family-level Model keyed on the folded name), so it arms the same refresh.
     */
    productName: z56.string().optional(),
    /**
     * The manufacture date or date code exactly as printed on the rating plate — a date ("03/2014") or
     * an opaque code that encodes one ("200561"). A raw string on purpose: a code is often not a date at
     * all, so it is shown as printed and only captioned when it reads as one.
     */
    dateCode: z56.string().optional(),
    quantity: z56.number().optional()
  }),
  z56.record(DataKeySchema, GuideValueSchema)
);
var EquipmentModelDetailsSchema = ModelDetailsSchema.extend({
  modelId: ModelIdSchema,
  modelDisplay: ModelNumberSchema,
  modelCanonical: ModelCanonicalSchema
});
var ArchiveStatusSchema = z56.enum(["active", "archived", "deleted"]);
var EquipmentSchemaV1 = z56.object({
  object: z56.literal("equipment"),
  schema: z56.literal(1),
  id: EquipmentIdSchema,
  active: z56.boolean(),
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema,
  refreshId: RefreshIdSchema.optional(),
  fields: EquipmentFieldsSchema.optional(),
  image: ImageSchema.optional(),
  details: EquipmentModelDetailsSchema.optional(),
  sources: z56.array(CitationDetailsSchema).optional()
});
var EquipmentImageCategorySchema = z56.enum(["model-number", "serial-number"]);
var SerialConfirmationSchema = z56.object({
  /** The serial the confirmation was recorded against (compared normalized). */
  serial: z56.string(),
  /** The manufacture year the user picked among the serial's candidate years. */
  year: z56.number().int()
});
var EquipmentImageSchema = withSingularArrayFields(
  ImageObjectSchema.extend({
    /** Codes this image documents (model + serial share one image when scanned together). */
    categories: z56.array(EquipmentImageCategorySchema).optional()
  }),
  [
    ["highlight", "highlights"],
    ["category", "categories"]
  ]
);
function imageForCategory(equipment, category) {
  return orderedImages(equipment).find((img) => (img.categories ?? []).includes(category));
}
var CODE_REGION_SLOT = {
  "model-number": 0,
  "serial-number": 1
};
var CODE_REGION_ORDER = Object.keys(CODE_REGION_SLOT).sort(
  (a, b) => CODE_REGION_SLOT[a] - CODE_REGION_SLOT[b]
);
var ABSENT_REGION = { x: 0, y: 0, width: 0, height: 0 };
function highlightForCategory(image, category) {
  const highlights = image.highlights ?? [];
  const slot = CODE_REGION_SLOT[category];
  const sharesThePhoto = (image.categories ?? []).length > 1;
  const box = sharesThePhoto ? highlights[slot] : highlights[slot] ?? highlights[0];
  return box && box.width > 0 && box.height > 0 ? box : void 0;
}
function withHighlightForCategory(image, category, box) {
  if (!box) return image;
  const documented = /* @__PURE__ */ new Set([...image.categories ?? [], category]);
  if (documented.size <= 1) return { ...image, highlights: [box] };
  const slotted = CODE_REGION_ORDER.map((c) => c === category ? box : highlightForCategory(image, c));
  while (slotted.length > 0 && !slotted[slotted.length - 1]) slotted.pop();
  return { ...image, highlights: slotted.map((b) => b ?? ABSENT_REGION) };
}
var EquipmentSchemaV2 = z56.object({
  ...EquipmentSchemaV1.omit({ image: true }).shape,
  schema: z56.literal(2),
  images: z56.array(EquipmentImageSchema).optional(),
  nickname: z56.string().optional()
});
var EquipmentSchemaV3 = z56.object({
  ...EquipmentSchemaV2.omit({ images: true }).shape,
  schema: z56.literal(3),
  /** Keyed by image id. Read via `orderedImages`/`primaryImage`, never directly. */
  images: z56.record(z56.string(), EquipmentImageSchema).optional(),
  /** Image ids in display order; first = primary. See `orderedImages` for drift tolerance. */
  imageOrder: z56.array(z56.string()).optional(),
  // Stamped at creation (createEquipmentAction); backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional(),
  /** Who added it; absent when Rock did. */
  createdBy: UserIdSchema.optional(),
  /**
   * The folder holding this equipment (system instance or user folder) — absent means root,
   * grouped at render time. A dangling id (folder deleted/inactive) also renders at root.
   */
  folderId: FolderIdSchema.optional(),
  /** User-picked manufacture year resolving an ambiguous serial decode. See `@rock/serials`. */
  serialConfirmation: SerialConfirmationSchema.optional(),
  /** Capture CTAs (model/serial) the user chose to hide on the equipment dashboard. */
  hiddenCaptureFields: z56.array(DataKeySchema).optional(),
  /**
   * The lifecycle field. `active` is its transitional mirror: still written on every write, and still
   * the field every equipment query filters on, so the two may never disagree.
   */
  status: ArchiveStatusSchema.default("active"),
  /** When it was archived. The archive shows it on the unit, and on the unit's own screen. */
  archivedAt: TimestampSchema.optional(),
  archivedBy: UserIdSchema.optional(),
  /** When the permanent removal happened, written beside `status: 'deleted'`. */
  deletedAt: TimestampSchema.optional(),
  deletedBy: UserIdSchema.optional()
});
function isEquipmentV1(val) {
  return isObjectSchema(val, "equipment", 1);
}
function isEquipmentV2(val) {
  return isObjectSchema(val, "equipment", 2);
}
function isEquipmentV3(val) {
  return isObjectSchema(val, "equipment", 3);
}
function migrateEquipmentV1ToV2(val) {
  return {
    ...val,
    images: val.image ? [val.image] : [],
    schema: 2
  };
}
function migrateEquipmentV2ToV3(val) {
  const { images, ...rest } = val;
  const v3 = val;
  const collection = Array.isArray(images) ? toImageCollection(images) : { images: v3.images, imageOrder: v3.imageOrder };
  return {
    ...rest,
    ...collection,
    schema: 3
  };
}
var EquipmentSchema = z56.preprocess(
  (val, ctx) => {
    if (isEquipmentV3(val)) {
      return Array.isArray(val.images) ? { ...val, ...toImageCollection(val.images) } : val;
    }
    if (isEquipmentV2(val)) {
      return migrateEquipmentV2ToV3(val);
    }
    if (isEquipmentV1(val)) {
      return migrateEquipmentV2ToV3(migrateEquipmentV1ToV2(val));
    }
    if (isObjectType(val, "equipment")) {
      return { ...val, schema: 3 };
    }
    ctx.addIssue({
      code: "custom",
      message: "Object is not an equipment",
      input: val
    });
    return z56.NEVER;
  },
  EquipmentSchemaV3
);
var SystemInfoDataSchema = z56.object({
  spaceId: SpaceIdSchema,
  type: InfoTypeSchema,
  info: SystemInfoSchema
});
var EquipmentImageOptionSchema = withSingularArrayFields(
  ImageObjectSchema.extend({
    object: z56.literal("equipment-image"),
    schema: z56.literal(1),
    id: z56.string()
  }),
  [["highlight", "highlights"]]
);

// src/home_contact.types.ts
import * as z57 from "zod";
var ContactSchema = z57.object({
  object: z57.literal("contact"),
  schema: z57.literal(1),
  id: ContactIdSchema,
  active: z57.boolean(),
  homeId: HomeIdSchema,
  companyId: CompanyIdSchema.optional(),
  spaceId: SpaceIdSchema,
  // Personal overrides: when set, take precedence over the referenced company's values.
  // When unset, display falls back to company.* via `contact.X ?? company?.X`.
  name: z57.string().optional(),
  phone: z57.string().optional(),
  website: z57.string().optional(),
  // Raw address text the user typed or picked. Source of truth the enrichContact trigger uses
  // to populate `address` + `geo` via Google Geocoding. Writing/updating this is offline-safe;
  // the trigger fires once Firestore syncs.
  addressText: z57.string().optional(),
  address: CompanyAddressSchema.optional(),
  geo: CompanyGeoSchema.optional(),
  label: z57.string().optional(),
  notes: z57.string().optional(),
  createdAt: TimestampSchema,
  createdBy: UserIdSchema.optional()
});

// src/user.types.ts
import * as z58 from "zod";
var UI_MODE_VALUES = ["system", "light", "dark"];
var UiModeSchema = z58.enum(UI_MODE_VALUES);
var DEFAULT_UI_MODE = "system";
var LocalePreferenceSchema = z58.object({
  languageTag: z58.string(),
  languageCode: z58.string().optional(),
  regionCode: z58.string().optional(),
  currencyCode: z58.string().optional(),
  currencySymbol: z58.string().optional(),
  decimalSeparator: z58.string().optional(),
  digitGroupingSeparator: z58.string().optional(),
  textDirection: z58.enum(["ltr", "rtl"]).optional(),
  measurementSystem: z58.enum(["metric", "us", "uk"]).optional(),
  temperatureUnit: z58.enum(["celsius", "fahrenheit"]).optional()
});
var DEFAULT_LOCALE_TAG = "en-US";
var DEFAULT_LOCALE_PREFERENCE = {
  languageTag: DEFAULT_LOCALE_TAG
};
var DEFAULT_LOCALE_PREFERENCES = [DEFAULT_LOCALE_PREFERENCE];
var CalendarPreferenceSchema = z58.object({
  calendar: z58.string().optional(),
  uses24hourClock: z58.boolean().optional(),
  firstWeekday: z58.number().optional(),
  timeZone: z58.string().optional()
});
var DEFAULT_CALENDAR_PREFERENCE = {};
var OWNERSHIP_STATUS_VALUES = ["owner-occupied", "landlord", "renter", "other"];
var OwnershipStatusSchema = z58.enum(OWNERSHIP_STATUS_VALUES);
var EXPERIENCE_LEVEL_VALUES = [
  "less-than-3-years",
  "3-10-years",
  "11-20-years",
  "more-than-20-years",
  "not-applicable"
];
var ExperienceLevelSchema = z58.enum(EXPERIENCE_LEVEL_VALUES);
var MOTIVATION_SEGMENT_VALUES = ["confidence", "security", "tooling", "other"];
var MotivationSegmentSchema = z58.enum(MOTIVATION_SEGMENT_VALUES);
var UserOnboardingSchema = z58.object({
  ownershipStatus: OwnershipStatusSchema.optional(),
  ownershipOther: z58.string().optional(),
  experienceLevel: ExperienceLevelSchema.optional(),
  motivationSegment: MotivationSegmentSchema.optional(),
  motivationOther: z58.string().optional(),
  confidenceBaseline: z58.number().min(1).max(5).optional(),
  completedAt: TimestampSchema.optional()
});
var UserSchema = z58.object({
  object: z58.literal("user"),
  schema: z58.literal(1),
  id: UserIdSchema,
  name: z58.string().optional(),
  nameUpdated: z58.boolean().optional(),
  email: z58.email().optional(),
  phoneNumber: z58.string().optional(),
  imageUrl: z58.string().url().optional(),
  imageUrlUpdated: z58.boolean().optional(),
  providerIds: z58.array(z58.string()),
  // `true` on seeded E2E accounts, `false` on real ones. Written on EVERY user, deliberately:
  // Firestore's inequality operators only match docs where the field exists, so leaving it off real
  // users would make `where('test','==',false)` return nothing and force filtering back onto the
  // client. Stamped at creation; backfilled by 20260812-mark-test-users. Optional only so docs
  // written before that backfill still parse — new writes always set it.
  test: z58.boolean().optional(),
  // Stamped on first login (buildUserFromAuth); backfilled from Firestore doc createTime for older docs.
  createdAt: TimestampSchema.optional(),
  lastLoginAt: TimestampSchema,
  isAnonymous: z58.boolean(),
  emailVerified: z58.boolean(),
  display: z58.object({
    timezone: z58.string().optional(),
    uiMode: UiModeSchema.optional(),
    locale: z58.string().optional(),
    locales: z58.array(LocalePreferenceSchema).optional(),
    calendar: CalendarPreferenceSchema.optional()
  }).optional(),
  deleted: z58.boolean().optional(),
  privacy: z58.object({
    imageDataUsageAllowed: z58.boolean().optional(),
    imageDataUsageUpdatedAt: TimestampSchema.optional(),
    dataDeletionRequestedAt: TimestampSchema.optional(),
    // Calendar day (`YYYY-MM-DD`) the account's data is slated for deletion — a deadline day.
    dataDeletionTargetDate: LocalDateSchema.optional(),
    dataDeletionRequestOptions: z58.object({
      includeDataExport: z58.boolean().optional(),
      stopProcessing: z58.boolean().optional(),
      confirmThirdPartyDeletion: z58.boolean().optional()
    }).optional()
  }).optional(),
  onboarding: UserOnboardingSchema.optional(),
  // The user-scoped setup-checklist markers (the dashboard's "Finishing Touches"). A separate
  // thing from `onboarding` above, and deliberately not nested under it: that field is written
  // whole by WelcomeScreen and would clobber these. See `setup_checklist.types.ts`.
  setupChecklist: UserSetupChecklistSchema.optional(),
  commerce: z58.object({
    // The user's preferred retailer for ordering supplies. Defaults to Amazon when unset.
    preferredSupplier: RetailerIdSchema.optional()
  }).optional()
});
var AuthEventSchema = z58.object({
  object: z58.literal("auth-event"),
  schema: z58.literal(1),
  type: z58.enum(["login", "logout"]),
  providerId: z58.string().optional(),
  timestamp: TimestampSchema
});

// src/domain_event.types.ts
var ChangeTypeSchema = z59.enum(["created", "updated", "deleted"]);
var BaseDomainEventSchema = z59.object({
  object: z59.literal("domain-event"),
  schema: z59.literal(1),
  id: DomainEventIdSchema,
  path: z59.string(),
  occurredAt: TimestampSchema
});
var BaseCreatedEventSchema = BaseDomainEventSchema.extend({
  changeType: z59.literal("created"),
  created: z59.unknown()
  // Extenders should provide the created entity schema
});
var BaseUpdatedEventSchema = BaseDomainEventSchema.extend({
  changeType: z59.literal("updated"),
  previous: z59.unknown(),
  // Extenders should provide the previous entity schema
  updated: z59.unknown(),
  // Extenders should provide the updated entity schema
  changedFields: z59.array(z59.string())
});
var BaseDeletedEventSchema = BaseDomainEventSchema.extend({
  changeType: z59.literal("deleted"),
  deleted: z59.unknown()
  // Extenders should provide the deleted entity schema
});
var HomeCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("home.created"),
  created: HomeSchema
});
var HomeUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("home.updated"),
  previous: HomeSchema,
  updated: HomeSchema
});
var HomeDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("home.deleted"),
  deleted: HomeSchema
});
var ConsiderationCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("consideration.created"),
  created: ConsiderationSchema
});
var ConsiderationUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("consideration.updated"),
  previous: ConsiderationSchema,
  updated: ConsiderationSchema
});
var ConsiderationDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("consideration.deleted"),
  deleted: ConsiderationSchema
});
var RoomCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("room.created"),
  created: RoomSchema
});
var RoomUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("room.updated"),
  previous: RoomSchema,
  updated: RoomSchema
});
var RoomDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("room.deleted"),
  deleted: RoomSchema
});
var HomeSpaceCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("homeSpace.created"),
  created: HomeSpaceSchema
});
var HomeSpaceUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("homeSpace.updated"),
  previous: HomeSpaceSchema,
  updated: HomeSpaceSchema
});
var HomeSpaceDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("homeSpace.deleted"),
  deleted: HomeSpaceSchema
});
var InfoCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("info.created"),
  created: SystemInfoSchema
});
var InfoUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("info.updated"),
  previous: SystemInfoSchema,
  updated: SystemInfoSchema
});
var InfoDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("info.deleted"),
  deleted: SystemInfoSchema
});
var EquipmentCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("equipment.created"),
  created: EquipmentSchema
});
var EquipmentUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("equipment.updated"),
  previous: EquipmentSchema,
  updated: EquipmentSchema
});
var EquipmentDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("equipment.deleted"),
  deleted: EquipmentSchema
});
var UserCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("user.created"),
  created: UserSchema
});
var UserUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("user.updated"),
  previous: UserSchema,
  updated: UserSchema
});
var UserDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("user.deleted"),
  deleted: UserSchema
});
var TaskCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("task.created"),
  created: TaskSchema
});
var TaskUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("task.updated"),
  previous: TaskSchema,
  updated: TaskSchema
});
var TaskDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("task.deleted"),
  deleted: TaskSchema
});
var HomeTaskSubscriptionCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("homeTaskSubscription.created"),
  created: HomeTaskSubscriptionSchema
});
var HomeTaskSubscriptionUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("homeTaskSubscription.updated"),
  previous: HomeTaskSubscriptionSchema,
  updated: HomeTaskSubscriptionSchema
});
var HomeTaskSubscriptionDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("homeTaskSubscription.deleted"),
  deleted: HomeTaskSubscriptionSchema
});
var ContactCreatedEventSchema = BaseCreatedEventSchema.extend({
  type: z59.literal("contact.created"),
  created: ContactSchema
});
var ContactUpdatedEventSchema = BaseUpdatedEventSchema.extend({
  type: z59.literal("contact.updated"),
  previous: ContactSchema,
  updated: ContactSchema
});
var ContactDeletedEventSchema = BaseDeletedEventSchema.extend({
  type: z59.literal("contact.deleted"),
  deleted: ContactSchema
});
var DomainEventSchema = z59.discriminatedUnion("type", [
  HomeCreatedEventSchema,
  HomeUpdatedEventSchema,
  HomeDeletedEventSchema,
  ConsiderationCreatedEventSchema,
  ConsiderationUpdatedEventSchema,
  ConsiderationDeletedEventSchema,
  RoomCreatedEventSchema,
  RoomUpdatedEventSchema,
  RoomDeletedEventSchema,
  HomeSpaceCreatedEventSchema,
  HomeSpaceUpdatedEventSchema,
  HomeSpaceDeletedEventSchema,
  InfoCreatedEventSchema,
  InfoUpdatedEventSchema,
  InfoDeletedEventSchema,
  EquipmentCreatedEventSchema,
  EquipmentUpdatedEventSchema,
  EquipmentDeletedEventSchema,
  UserCreatedEventSchema,
  UserUpdatedEventSchema,
  UserDeletedEventSchema,
  TaskCreatedEventSchema,
  TaskUpdatedEventSchema,
  TaskDeletedEventSchema,
  HomeTaskSubscriptionCreatedEventSchema,
  HomeTaskSubscriptionUpdatedEventSchema,
  HomeTaskSubscriptionDeletedEventSchema,
  ContactCreatedEventSchema,
  ContactUpdatedEventSchema,
  ContactDeletedEventSchema
]);

// src/equipment_image_search.types.ts
import * as z60 from "zod";
var EquipmentImageSearchIdSchema = z60.string().brand();
var EquipmentImageSearchBaseSchema = z60.object({
  object: z60.literal("equipment-image-search-request"),
  schema: z60.literal(1),
  id: EquipmentImageSearchIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema,
  modelNumber: ModelNumberSchema,
  // Set once at creation and never touched again — `updatedAt` is bumped on every batch of results.
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
  images: z60.array(ImageSchema).optional(),
  error: z60.string().optional()
});
var EquipmentImageSearchRequestedSchema = EquipmentImageSearchBaseSchema.extend({
  status: z60.literal("requested")
});
var EquipmentImageSearchProcessingSchema = EquipmentImageSearchBaseSchema.extend({
  status: z60.literal("processing")
});
var EquipmentImageSearchCompletedSchema = EquipmentImageSearchBaseSchema.extend({
  status: z60.literal("completed"),
  images: z60.array(ImageSchema)
});
var EquipmentImageSearchErrorSchema = EquipmentImageSearchBaseSchema.extend({
  status: z60.literal("error"),
  error: z60.string()
});
var EquipmentImageSearchRequestSchema = z60.discriminatedUnion("status", [
  EquipmentImageSearchRequestedSchema,
  EquipmentImageSearchProcessingSchema,
  EquipmentImageSearchCompletedSchema,
  EquipmentImageSearchErrorSchema
]);

// src/equipment_test_library.types.ts
import * as z61 from "zod";
var EquipmentTestLibraryEntryIdSchema = z61.string().brand();
var EquipmentTestLibrarySourceSchema = z61.enum(["own-photo", "web-sourced"]);
var EquipmentTestLibraryEntrySchema = z61.object({
  object: z61.literal("equipment-test-library-entry"),
  schema: z61.literal(1),
  id: EquipmentTestLibraryEntryIdSchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema,
  name: z61.string().min(1),
  /**
   * Free text, deliberately not an FK into the Brand catalog: the library needs to cover any
   * manufacturer, while the catalog's curation only covers the ones we've encountered.
   */
  brand: z61.string().optional(),
  modelNumber: ModelNumberSchema.optional(),
  serialNumber: SerialNumberSchema.optional(),
  productImage: ImageSchema.optional(),
  /**
   * The nameplate/rating-plate shot — the one that actually drives scan testing. Prefer photos
   * where the model AND serial are both legible: that exercises the `@rock/serials` decode +
   * confirm path too, not just model decoding. `ImageOption.highlights` can box both regions.
   */
  nameplateImage: ImageSchema.optional(),
  source: EquipmentTestLibrarySourceSchema,
  /**
   * The model is old or discontinued: still a real scan case, so the entry stays, tagged so nobody takes
   * it for a current product or expects its manual to exist. A wrong entry is deleted instead.
   */
  outdated: z61.boolean().optional(),
  notes: z61.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional()
});

// src/equipment_type_family.types.ts
import * as z62 from "zod";
var EquipmentTypeFamilyIdSchema = TypeIdSchema.brand();
var EquipmentTypeFamilySchema = z62.object({
  object: z62.literal("equipment-type-family"),
  schema: z62.literal(1),
  id: EquipmentTypeFamilyIdSchema,
  name: z62.string().min(1),
  typeIds: z62.array(EquipmentTypeIdSchema).min(2),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});

// src/equipment_type_option.types.ts
import * as z63 from "zod";
var CUSTOM_GROUP = "Custom";
var TypeConfidenceSchema = z63.enum(["high", "medium", "low"]);
var EquipmentTypeDraftSchema = z63.object({
  title: z63.string().trim().min(1),
  icon: z63.string().optional(),
  spaceId: SpaceIdSchema,
  group: z63.string(),
  subtitle: z63.string().optional(),
  /** Existing candidate to file the custom under, or a proposed slug. Absent → the title is slugified. */
  candidateId: EquipmentTypeCandidateIdSchema.optional(),
  aiClassification: z63.enum(["shared", "custom"]).optional(),
  /** The user's own words, recorded on the candidate as a sample description. */
  description: z63.string().optional()
});
var EquipmentTypeChoiceSchema = z63.discriminatedUnion("kind", [
  z63.object({
    kind: z63.literal("existing"),
    typeId: EquipmentTypeIdSchema,
    title: z63.string(),
    icon: z63.string().optional(),
    subtitle: z63.string().optional()
  }),
  z63.object({ kind: z63.literal("draft"), draft: EquipmentTypeDraftSchema })
]);
var EquipmentTypeOptionSchema = EquipmentTypeChoiceSchema.and(z63.object({ confidence: TypeConfidenceSchema }));
function typeChoiceKey(choice) {
  if (choice.kind === "existing") {
    return choice.typeId;
  }
  return `draft:${choice.draft.candidateId ?? choice.draft.title.trim().toLowerCase()}`;
}
function typeChoiceTitle(choice) {
  return choice.kind === "existing" ? choice.title : choice.draft.title;
}
function typeChoiceIcon(choice) {
  return choice.kind === "existing" ? choice.icon : choice.draft.icon;
}
function typeChoiceSubtitle(choice) {
  return choice.kind === "existing" ? choice.subtitle : choice.draft.subtitle;
}
var IdentifyEquipmentRequestSchema = z63.object({
  imageBase64: z63.string().min(1),
  spaceId: SpaceIdSchema.optional(),
  homeId: HomeIdSchema.optional()
});
var IdentifyEquipmentResponseSchema = z63.object({
  options: z63.array(EquipmentTypeOptionSchema),
  /** Index into `options` the confirm screen should start on; null when nothing is safe to preselect. */
  preselectedIndex: z63.number().int().nonnegative().nullable(),
  roomType: RoomTypeSchema.nullable(),
  brand: z63.string().nullable(),
  modelNumber: z63.string().nullable(),
  /** Whether the preselected item has a model number at all; null when nothing is preselected. */
  hasModelNumber: z63.boolean().nullable()
});
var ResolveEquipmentTypeRequestSchema = z63.object({
  userDescription: z63.string().trim().min(1),
  spaceId: SpaceIdSchema.optional(),
  homeId: HomeIdSchema.optional()
});
var ResolveEquipmentTypeResponseSchema = z63.object({
  options: z63.array(EquipmentTypeOptionSchema)
});

// src/feedback.types.ts
import * as z64 from "zod";
var FeedbackSubmissionIdSchema = z64.string().brand();
var FeedbackTypeSchema = z64.enum(["issue", "idea", "feedback", "question", "other"]);
var FeedbackEquipmentSelectionSchema = z64.object({
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema
});
var FeedbackAppInfoSchema = z64.object({
  env: z64.string(),
  version: z64.string(),
  platform: z64.string(),
  platformId: z64.string()
});
var MailerSendLogSchema = z64.object({
  status: z64.enum(["sent", "error"]),
  attemptedAt: TimestampSchema,
  subject: z64.string(),
  text: z64.string(),
  to: z64.string(),
  from: z64.string(),
  messageId: z64.string().optional(),
  statusCode: z64.number().optional(),
  responseBody: z64.any().optional(),
  responseHeaders: z64.record(z64.string(), z64.any()).optional(),
  error: z64.string().optional()
});
var FeedbackBodyIssueSchema = z64.object({
  type: z64.literal("issue").describe("Something looks broken or behaved unexpectedly."),
  summary: z64.string().describe("Short title summarizing the bug (\u22648 words)."),
  details: z64.string().describe("The user's full description of what they saw."),
  steps: z64.string().nullish().describe("Optional steps to reproduce, if the user provided any."),
  expected: z64.string().nullish().describe("Optional \u2014 what the user expected to happen.")
});
var FeedbackBodyIdeaSchema = z64.object({
  type: z64.literal("idea").describe("Wished-for capability or feature request."),
  summary: z64.string().describe("Short title summarizing the idea (\u22648 words)."),
  details: z64.string().describe("The user's full description of the idea."),
  impact: z64.string().nullish().describe("Optional \u2014 what this would help the user do.")
});
var FeedbackBodyFeedbackSchema = z64.object({
  type: z64.literal("feedback").describe("General reactions, appreciation, or thoughts."),
  message: z64.string().describe("The user's full message.")
});
var FeedbackBodyQuestionSchema = z64.object({
  type: z64.literal("question").describe("They want a human at Rock to follow up."),
  question: z64.string().describe("The user's question, in their own words.")
});
var FeedbackBodyOtherSchema = z64.object({
  type: z64.literal("other").describe("None of the other categories fit (rare)."),
  subject: z64.string().nullish().describe("Optional short subject line."),
  message: z64.string().describe("The user's full message.")
});
var FeedbackBodyRequestDataSchema = z64.object({
  type: z64.literal("requestData"),
  message: z64.string()
});
var FeedbackBodyAccountDeletionSchema = z64.object({
  type: z64.literal("accountDeletion"),
  message: z64.string(),
  includeDataExport: z64.boolean().optional(),
  confirmThirdPartyDeletion: z64.boolean().optional(),
  targetDeletionDate: LocalDateSchema.optional()
});
var FeedbackBodySchema = z64.discriminatedUnion("type", [
  FeedbackBodyIssueSchema,
  FeedbackBodyIdeaSchema,
  FeedbackBodyFeedbackSchema,
  FeedbackBodyQuestionSchema,
  FeedbackBodyOtherSchema,
  FeedbackBodyRequestDataSchema,
  FeedbackBodyAccountDeletionSchema
]);
var FeedbackSubmissionSchema = z64.object({
  object: z64.literal("feedback-submission"),
  schema: z64.literal(1),
  id: FeedbackSubmissionIdSchema,
  userId: UserIdSchema,
  homeId: HomeIdSchema,
  // Set only on chat-originated submissions (the AI's `submit-feedback` tool):
  // the conversation the feedback was filed from, so support can open the exact
  // thread the user was complaining about. Absent on form-path submissions.
  chatConversationId: ChatConversationIdSchema.optional(),
  body: FeedbackBodySchema,
  equipment: z64.string().optional(),
  contactEmail: z64.email().optional(),
  createdAt: TimestampSchema,
  app: FeedbackAppInfoSchema,
  _mailersend: MailerSendLogSchema.optional(),
  resend: z64.boolean().optional()
});

// src/folder.types.ts
import * as z65 from "zod";
var FolderSchema = z65.object({
  object: z65.literal("folder"),
  schema: z65.literal(1),
  // Random id — folders are per-instance, unlike deterministic per-type starter ids.
  id: FolderIdSchema,
  homeId: HomeIdSchema,
  // Placement — a doc FIELD, not a path segment; folders are addressed by (homeId, folderId).
  spaceId: SpaceIdSchema,
  name: z65.string().min(1),
  /** User-picked Lucide icon name; display falls back to the system type's icon, then a generic. */
  icon: z65.string().optional(),
  /** Present ⇒ this folder is a system instance of that type. Hook for rich system rendering. */
  systemTypeId: EquipmentTypeIdSchema.optional(),
  /**
   * Per-INSTANCE system-guide history: "Revisit Guided Setup" on this folder hydrates from
   * these visits. Deliberately NOT on the (per-type) starter — two systems of one type must
   * not share hydration.
   */
  guideVisitIds: z65.array(GuideVisitIdSchema).optional(),
  // Deletion = deactivate, contents included. Equipment left pointing at a dead folder (legacy
  // rows, predating the cascade) falls back to root at render time.
  active: z65.boolean(),
  /** See {@link ArchiveStatusSchema}. `active` above is its transitional mirror. */
  status: ArchiveStatusSchema.default("active"),
  /** When it was archived. Recorded for the archive to sort or age on later; nothing reads it yet. */
  archivedAt: TimestampSchema.optional(),
  archivedBy: UserIdSchema.optional(),
  /** When the permanent removal happened, written beside `status: 'deleted'`. */
  deletedAt: TimestampSchema.optional(),
  deletedBy: UserIdSchema.optional(),
  createdAt: TimestampSchema.optional(),
  /** Who made it; absent when Rock did. */
  createdBy: UserIdSchema.optional()
});

// src/guide_help.types.ts
import * as z66 from "zod";
var GuideHelpIdSchema = z66.string().brand();
var GuideHelpCacheKeySchema = z66.string().brand();
var GuideHelpOptionSchema = z66.object({
  title: z66.string(),
  subtitle: z66.string().optional()
});
var GuideHelpRequestSchema = z66.object({
  object: z66.literal("guide-help-request"),
  schema: z66.literal(1),
  id: GuideHelpIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema.optional(),
  nodeId: GuideNodeIdSchema,
  /**
   * A record of what the user was shown. Client-written, so the server builds its prompt from the
   * question instead.
   */
  options: z66.array(GuideHelpOptionSchema),
  api: z66.enum(["chatgpt", "perplexity"]).default("chatgpt"),
  stream: z66.boolean().optional(),
  /**
   * Asked from preview mode — the node may exist only in the staged preview guide. Client-set and
   * trusted: a non-admin setting it gets help on an unpublished question, which is accepted.
   */
  preview: z66.boolean().optional(),
  status: z66.enum(["requested", "in-progress", "complete", "error"]),
  response: z66.string().optional(),
  deltas: z66.array(z66.string()).optional(),
  // Set once at creation and never touched again — `updatedAt` is bumped on every streaming delta.
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
  eventId: z66.string().optional(),
  error: z66.string().optional()
});
var GuideHelpCacheSchema = z66.object({
  object: z66.literal("guide-help-cache"),
  schema: z66.literal(1),
  id: GuideHelpCacheKeySchema,
  response: z66.string(),
  updatedAt: TimestampSchema
});

// src/guide_starter.types.ts
import * as z67 from "zod";
var GuideStarterStatusSchema = z67.enum(["pending", "done"]);
var GuideStarterBaseSchema = z67.object({
  object: z67.literal("guide-starter"),
  schema: z67.literal(1),
  /** Deterministic ({@link starterIdForType} / {@link starterIdForRoom}) so re-runs upsert. */
  id: GuideStarterIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  status: GuideStarterStatusSchema,
  completedAt: TimestampSchema.optional(),
  /**
   * Set when this starter reached `done` by being DISMISSED — the user opted out of setting it
   * up — rather than by being completed. Both are `done` (a dismissal must not stay pending, or
   * a space-guide re-run resurrects the row), so this is the only thing that separates "I set
   * this up" from "I'm not doing this one": surfaces that score or celebrate progress skip a
   * dismissed starter entirely instead of giving it a checkmark. Finishing the type's GUIDE later
   * clears it (`completeGuideStarter`) — a quick-add does NOT, since
   * {@link planStartersOnEquipmentCreated} only matches `pending` starters.
   */
  dismissedAt: TimestampSchema.optional(),
  /**
   * Set beside `dismissedAt` when a retire of the starter's type dismissed it, not the homeowner.
   * Restoring the type reopens exactly these. Completion clears `dismissedAt` but not this, so it
   * counts only beside a dismissal.
   */
  typeRetiredAt: TimestampSchema.optional(),
  /**
   * Type/room-guide visit history (feeds "Revisit Guided Setup" hydration). SYSTEM instances
   * keep theirs on the Folder instead — per instance, not per type.
   */
  guideVisitIds: z67.array(GuideVisitIdSchema).optional(),
  createdAt: TimestampSchema.optional()
});
var TypeGuideStarterSchema = GuideStarterBaseSchema.extend({
  kind: z67.literal("equipment-type"),
  /** May be a system-scope type — its equipment guide's finish then creates a system folder. */
  typeId: EquipmentTypeIdSchema
});
var RoomGuideStarterSchema = GuideStarterBaseSchema.extend({
  kind: z67.literal("room"),
  roomId: RoomIdSchema,
  /**
   * Room name stamped at creation — the offline display fallback when the room doc can't be
   * read (cache miss is NOT evidence of deletion; render this with no suffix).
   */
  roomName: z67.string().optional()
});
var GuideStarterSchema = z67.discriminatedUnion("kind", [TypeGuideStarterSchema, RoomGuideStarterSchema]);
function isDismissedStarter(starter) {
  return starter.status === "done" && !!starter.dismissedAt;
}
function isCompletedStarter(starter) {
  return starter.status === "done" && !starter.dismissedAt;
}
function starterIdForType(typeId) {
  return `starter:${typeId}`;
}
function starterIdForRoom(spaceId, roomId) {
  return `starter:room:${spaceId}:${roomId}`;
}
function planStartersOnEquipmentCreated(starters, equipment, folders) {
  const folder = equipment.folderId ? folders.find((f) => f.id === equipment.folderId) : void 0;
  return starters.filter(
    (starter) => starter.kind === "equipment-type" && starter.status === "pending" && (starter.typeId === equipment.typeId || !!folder?.systemTypeId && starter.typeId === folder.systemTypeId)
  );
}

// src/guide_visit.types.ts
import * as z68 from "zod";
var GuideVisitSchema = z68.object({
  object: z68.literal("guide-visit"),
  schema: z68.literal(1),
  id: GuideVisitIdSchema,
  owner: UserIdSchema,
  guideContext: GuideContextSchema,
  flowId: z68.string(),
  startedAt: TimestampSchema,
  finishedAt: TimestampSchema,
  durationMs: z68.number(),
  guideValues: GuideValuesSchema,
  // TODO: This would be nice to have, so that we can see where people go BACK
  // rawPath: z.array(z.discriminatedUnion('type', [
  //     z.object({
  //         type: z.literal('node'),
  //         nodeId: GuideNodeIdSchema,
  //         timestamp: z.string().datetime({ offset: true }),
  //     }),
  //     z.object({
  //         type: z.literal('edge'),
  //         sourceHandleId: OutgoingHandleSchema,
  //     }),
  //     z.object({
  //         type: z.literal('back'),
  //     }),
  // ])),
  finalPath: z68.array(
    z68.discriminatedUnion("type", [
      // Edge traversal from one node to another. Default type keeps historical records
      // (written before the sub-route variant existed) parseable.
      z68.object({
        type: z68.literal("handle").default("handle"),
        sourceNodeId: GuideNodeIdSchema,
        sourceHandleId: OutgoingHandleSchema,
        targetNodeId: GuideNodeIdSchema
      }),
      // Router push to a different view of the same node (e.g. scan:9's camera sub-view).
      z68.object({
        type: z68.literal("sub-route"),
        nodeId: GuideNodeIdSchema,
        fromSubRoute: z68.string().optional(),
        toSubRoute: z68.string()
      })
    ])
  ),
  // Legacy container-model field — optional (not renamed) so every stored visit still parses;
  // nothing writes or reads it anymore, and the container schema itself is gone.
  updatedContainers: z68.array(z68.unknown()).optional(),
  updatedStarters: z68.array(GuideStarterSchema).optional(),
  updatedFolders: z68.array(FolderSchema).optional(),
  createdEquipment: z68.array(EquipmentSchema),
  createdRooms: z68.array(RoomSchema),
  updatedSystemInfo: z68.array(SystemInfoSchema)
});

// src/home_invite.types.ts
import * as z70 from "zod";

// src/home_member.types.ts
import * as z69 from "zod";
var HomeMemberRoleSchema = z69.enum(["owner", "member"]);
var HomeMemberSchema = z69.object({
  object: z69.literal("home-member"),
  schema: z69.literal(1),
  id: UserIdSchema,
  homeId: HomeIdSchema,
  userId: UserIdSchema,
  role: HomeMemberRoleSchema,
  joinedAt: TimestampSchema,
  /** The member whose invite they accepted; absent for the owner. */
  invitedBy: UserIdSchema.optional(),
  /**
   * The member's name, email and avatar as the Members list shows them, copied from their user doc:
   * members cannot read each other's user docs.
   */
  name: z69.string().optional(),
  email: z69.string().optional(),
  imageUrl: z69.string().optional()
});
function memberProfileOf(user) {
  const name = user?.name?.trim();
  return {
    ...name ? { name } : {},
    ...user?.email ? { email: user.email } : {},
    ...user?.imageUrl && isRemoteImageUrl(user.imageUrl) ? { imageUrl: user.imageUrl } : {}
  };
}
function isRemoteImageUrl(url) {
  return /^https?:\/\//.test(url);
}
function sameMemberProfile(a, b) {
  return a.name === b.name && a.email === b.email && a.imageUrl === b.imageUrl;
}
function ownerMembership(home, now, profile = {}) {
  return {
    object: "home-member",
    schema: 1,
    id: home.owner,
    homeId: home.id,
    userId: home.owner,
    role: "owner",
    joinedAt: home.createdAt ?? now,
    ...profile
  };
}

// src/home_invite.types.ts
var HOME_INVITE_TTL_DAYS = 7;
var HOME_MEMBER_CAP = 6;
function isHomeFull(home) {
  return homeMemberIds(home).length >= HOME_MEMBER_CAP;
}
var HomeInviteStatusSchema = z70.enum(["pending", "accepted", "revoked", "expired"]);
var HomeInviteEmailStatusSchema = z70.enum(["sending", "sent", "held", "failed"]);
var HomeInviteEmailHoldSchema = z70.enum(["daily-limit", "home-full", "sender-left", "home-gone"]);
var InviteEmailSchema = z70.email();
var HomeInviteSchema = z70.object({
  object: z70.literal("home-invite"),
  schema: z70.literal(1),
  /** The normalized code (upper case, no separator); also the doc id. */
  id: z70.string(),
  code: z70.string(),
  homeId: HomeIdSchema,
  role: HomeMemberRoleSchema,
  email: InviteEmailSchema.optional(),
  invitedBy: UserIdSchema,
  status: HomeInviteStatusSchema,
  createdAt: TimestampSchema,
  expiresAt: TimestampSchema,
  acceptedBy: UserIdSchema.optional(),
  acceptedAt: TimestampSchema.optional(),
  revokedBy: UserIdSchema.optional(),
  revokedAt: TimestampSchema.optional(),
  emailStatus: HomeInviteEmailStatusSchema.optional(),
  emailHold: HomeInviteEmailHoldSchema.optional()
});
function buildHomeInvite(input) {
  const { homeId, invitedBy, code, email: email4, now } = input;
  return {
    object: "home-invite",
    schema: 1,
    id: code,
    code,
    homeId,
    role: "member",
    ...email4 ? { email: foldEmail(email4) } : {},
    invitedBy,
    status: "pending",
    createdAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + HOME_INVITE_TTL_DAYS * MS_PER_DAY).toISOString()
  };
}
function foldEmail(email4) {
  return email4.trim().toLowerCase();
}
function isInviteEmail(email4) {
  return InviteEmailSchema.safeParse(foldEmail(email4)).success;
}
function homeInviteStatus(invite, now) {
  return invite.status === "pending" && invite.expiresAt <= now ? "expired" : invite.status;
}
function daysUntilInviteExpires(invite, now) {
  return Math.max(0, Math.ceil((new Date(invite.expiresAt).getTime() - now.getTime()) / MS_PER_DAY));
}
function resolveInviteCode(matches, now, joiner) {
  const taken = joiner && matches.find((invite) => invite.status === "accepted" && invite.acceptedBy === joiner);
  if (taken) return taken;
  const open = matches.filter((invite) => homeInviteStatus(invite, now) === "pending");
  if (open.length > 1) return void 0;
  if (open.length === 1) return open[0];
  return [...matches].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}
function openInviteTo(home, invites, email4, now) {
  const folded = foldEmail(email4);
  return invites.find(
    (invite) => invite.email === folded && homeInviteStatus(invite, now) === "pending" && isHomeMember(home, invite.invitedBy)
  );
}
var MS_PER_DAY = 24 * 60 * 60 * 1e3;
var InviteCodeRequestSchema = z70.object({
  /** As typed: case, dashes and spaces are forgiven. */
  code: z70.string().min(1)
});
var HomeInviteLookupSchema = z70.object({
  status: z70.enum(["pending", "accepted", "revoked", "expired", "not-found", "member", "full"]),
  homeId: HomeIdSchema.nullish(),
  homeName: z70.string().nullish(),
  /** The home's own photo (its Home space's), a tokened download URL a non-member can load. */
  homePhoto: z70.string().nullish(),
  inviterName: z70.string().nullish()
});
function homeInviteLookupMessage(status) {
  switch (status) {
    case "accepted":
      return "That invite has already been used.";
    case "revoked":
      return "That invite was taken back.";
    case "expired":
      return "That invite has expired. Ask for a new one.";
    case "member":
      return "You're already in this home.";
    case "full":
      return "That home has no room for another member.";
    default:
      return "We couldn't find an invite with that code.";
  }
}
var MyHomeInviteSchema = z70.object({
  code: z70.string(),
  homeId: HomeIdSchema,
  homeName: z70.string(),
  inviterName: z70.string().nullish()
});
var AcceptHomeInviteResponseSchema = z70.object({ homeId: HomeIdSchema });
var HomeRequestSchema = z70.object({ homeId: HomeIdSchema });
var HomeMemberRequestSchema = z70.object({ homeId: HomeIdSchema, userId: UserIdSchema });

// src/home_eval.types.ts
import * as z71 from "zod";
var HomeEvalTriggerStatusSchema = z71.enum(["pending", "fired"]);
var HomeEvalTriggerSchema = z71.object({
  id: HomeIdSchema,
  object: z71.literal("home-eval-trigger"),
  schema: z71.literal(1),
  homeId: HomeIdSchema,
  lastEventAt: TimestampSchema.optional(),
  nextRunAt: TimestampSchema.optional(),
  status: HomeEvalTriggerStatusSchema.optional(),
  lastJobId: HomeEvalJobIdSchema.optional(),
  pendingEventIds: z71.array(DomainEventIdSchema).default([])
});
var HomeEvalJobStatusSchema = z71.enum(["pending", "running", "success", "error"]);
var HomeEvalJobSchema = z71.object({
  object: z71.literal("home-eval-job"),
  schema: z71.literal(1),
  id: HomeEvalJobIdSchema,
  homeId: HomeIdSchema,
  jobStatus: HomeEvalJobStatusSchema.optional(),
  attempts: z71.number(),
  errorMessage: z71.string().optional(),
  scheduledAt: TimestampSchema.optional(),
  startedAt: TimestampSchema.optional(),
  finishedAt: TimestampSchema.optional(),
  summary: z71.object({
    evaluatedCount: z71.number(),
    appliedCount: z71.number(),
    durationMs: z71.number()
  }).optional(),
  triggerEventIds: z71.array(DomainEventIdSchema).optional()
});

// src/home_question.types.ts
import * as z72 from "zod";
var HomeQuestionStatusSchema = z72.enum(["open", "answered", "stale"]);
var HomeQuestionCommonShape = {
  object: z72.literal("home-question"),
  schema: z72.literal(1),
  id: HomeQuestionIdSchema,
  homeId: HomeIdSchema,
  status: HomeQuestionStatusSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  closedAt: TimestampSchema.optional(),
  snoozedUntil: TimestampSchema.optional(),
  snoozedBy: UserIdSchema.optional(),
  /** When the owner was told about it; a re-ask after their own edit clears it, so that one notifies too. */
  notifiedAt: TimestampSchema.optional(),
  test: z72.boolean().default(false)
};
var EquipmentBrandQuestionAskedForSchema = z72.object({
  identity: z72.string(),
  brand: z72.string().optional(),
  brandId: BrandIdSchema.optional()
});
var EquipmentBrandQuestionSchema = z72.object({
  ...HomeQuestionCommonShape,
  kind: z72.literal("equipment-brand"),
  equipmentId: EquipmentIdSchema,
  askedFor: EquipmentBrandQuestionAskedForSchema,
  options: z72.array(RefreshBrandOptionSchema)
});
var InfoQuestionSchema = z72.object({
  ...HomeQuestionCommonShape,
  kind: z72.literal("info"),
  infoTypeId: InfoTypeIdSchema,
  spaceId: SpaceIdSchema
});
var HomeQuestionSchema = z72.discriminatedUnion("kind", [EquipmentBrandQuestionSchema, InfoQuestionSchema]);
function equipmentBrandQuestionId(equipmentId) {
  return HomeQuestionIdSchema.parse(`equipment:${equipmentId}:brand`);
}
function infoQuestionId(infoTypeId) {
  return HomeQuestionIdSchema.parse(`info:${infoTypeId}`);
}

// src/learn.types.ts
import * as z73 from "zod";
var LearnIdSchema = z73.string().brand();
var LearnCacheKeySchema = z73.string().brand();
var LEARN_ATTRIBUTE_LABEL_MAX = 200;
var LEARN_ATTRIBUTE_VALUE_MAX = 500;
var LearnRequestSchema = z73.object({
  object: z73.literal("learn-request"),
  schema: z73.literal(1),
  id: LearnIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema,
  // Both go verbatim into a paid prompt and are written by the client, so they are capped: a spec label or
  // value is a short phrase. The client truncates to the same caps before writing (`LEARN_ATTRIBUTE_*_MAX`),
  // because a request that fails to parse here is simply never served — a spinner that never resolves.
  attributeLabel: z73.string().max(LEARN_ATTRIBUTE_LABEL_MAX),
  /**
   * The value the user was looking at when they asked. The Specs tab renders a row under the equipment
   * TYPE's label with a value that may come from the model's manual rather than the equipment's own
   * researched attributes, so the server can no longer count on finding the row by label in
   * `details.attributes`; with the value in hand it needs no lookup at all. Optional for requests from
   * builds that predate it, which fall back to that lookup.
   */
  attributeValue: z73.string().max(LEARN_ATTRIBUTE_VALUE_MAX).optional(),
  api: z73.enum(["chatgpt", "perplexity"]).default("chatgpt"),
  stream: z73.boolean().optional(),
  status: z73.enum(["requested", "in-progress", "complete", "error"]),
  response: z73.string().optional(),
  deltas: z73.array(z73.string()).optional(),
  updatedAt: TimestampSchema.optional(),
  eventId: z73.string().optional(),
  error: z73.string().optional()
});
var LearnResponseSchema = z73.object({
  explanation: z73.string()
});
var LearnCacheSchema = z73.object({
  object: z73.literal("learn-cache"),
  schema: z73.literal(1),
  id: LearnCacheKeySchema,
  response: z73.string(),
  updatedAt: TimestampSchema
});

// src/lesson.types.ts
import * as z74 from "zod";
var LessonStatusSchema = z74.enum(["unread", "read", "completed", "dismissed"]);
var LessonStatusOptions = LessonStatusSchema.options;
var LessonStatusLabels = {
  unread: "Unread",
  read: "Started",
  completed: "Done",
  dismissed: "Dismissed"
};
var LessonSchema = z74.object({
  object: z74.literal("lesson"),
  schema: z74.literal(1),
  id: LessonIdSchema,
  typeId: LessonTypeIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema.optional(),
  title: z74.string().min(1),
  status: LessonStatusSchema,
  /**
   * Snapshot of the type's criticality at creation, mirroring `Consideration.criticality`. On a lesson
   * this answers "what does it cost this homeowner not to know it": it orders the library and the
   * queue the week's fill draws from, and the lessons list shows it as a badge. It never pre-checks anything and never notifies.
   */
  criticality: ConsiderationCriticalitySchema.optional(),
  /**
   * The weekend (its first day, `YYYY-MM-DD`) this lesson is raised on. Same field and same fill as
   * `Consideration.reviewAt`: absent, the lesson is queued until a week's fill finds room for it
   * behind that weekend's chores, so a batch of new lessons can never crowd maintenance off a weekend.
   */
  reviewAt: LocalDateSchema.optional(),
  firstReadAt: TimestampSchema.optional(),
  lastReadAt: TimestampSchema.optional(),
  /** Who read it last. */
  readBy: UserIdSchema.optional(),
  completedAt: TimestampSchema.optional(),
  completedBy: UserIdSchema.optional(),
  dismissedAt: TimestampSchema.optional(),
  dismissedBy: UserIdSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var LESSONS_TITLE = "Important to Know";
var LessonTypeSchema = z74.object({
  object: z74.literal("lesson-type"),
  schema: z74.literal(1),
  id: LessonTypeIdSchema,
  title: z74.string().min(1),
  spaceId: SpaceIdSchema.optional(),
  overview: z74.string().optional(),
  lessonDocId: RockDocIdSchema.optional(),
  /**
   * The content's When To Show text, which the applies-to query is drafted from. It is only a
   * request: the lesson reaches nobody until an admin approves an `appliesTo` query
   * (`selectLessonTypesToGive` holds the gate).
   */
  aiWhenToShow: z74.string().optional(),
  ...AppliesToFieldsSchema.shape,
  defaultCriticality: ConsiderationCriticalitySchema.optional(),
  /** Only `approved` types are applied to homes. `retired` means the Notion page is gone. */
  documentStatus: DocumentStatusSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var APPLIES_TO_OWNER_COPY = {
  "consideration-type": { noun: "consideration", plural: "considerations", textLabel: "When To Apply" },
  "lesson-type": { noun: "lesson", plural: "lessons", textLabel: "When To Show" }
};
function appliesToOwnerCopy(type) {
  return APPLIES_TO_OWNER_COPY[type.object];
}
function whenToApplyText(type) {
  return type.object === "lesson-type" ? type.aiWhenToShow : type.aiWhenToApply;
}
function hasWhenToApplyText(type) {
  return !!whenToApplyText(type)?.trim();
}

// src/document.types.ts
import * as z76 from "zod";

// src/document_language.types.ts
import * as z75 from "zod";
var PageLanguageSchema = z75.object({
  lang: z75.string(),
  /** Detector probability for `lang` (0-1). */
  prob: z75.number(),
  /** Detector's own reliability flag for this read. */
  reliable: z75.boolean(),
  /** Count of alphabetic characters extracted from the page. */
  letters: z75.number()
});
var PageRangeSchema = z75.object({
  start: z75.number().int().min(1),
  end: z75.number().int().min(1)
});
var TrimProposalOutcomeSchema = z75.enum(["trim", "none", "declined"]);
var TrimCautionSchema = z75.enum(["english-in-middle", "uncertain-pages"]);
var DocumentTrimProposalSchema = z75.object({
  outcome: TrimProposalOutcomeSchema,
  /** The pages an English-only copy would keep (1-based, ascending, non-overlapping). Empty on `declined`. */
  keepRanges: z75.array(PageRangeSchema),
  keptPages: z75.number().int(),
  /** Why a `declined` proposal declined. */
  reason: z75.string().optional(),
  /**
   * The weakest detector probability among pages the policy classified (en/other) — the number a future
   * auto-trim threshold would gate on. 0 when nothing classified.
   */
  confidence: z75.number(),
  /** Fraction of pages too sparse/uncertain to classify (attached to neighbors by position). */
  unknownFraction: z75.number(),
  /** Empty on `declined`, and on analyses that predate cautions. */
  cautions: z75.array(TrimCautionSchema).default([])
});
var DocumentLanguageAnalysisSchema = z75.object({
  revision: z75.number().int(),
  /** Detector that produced the per-page reads (e.g. `cld3`). */
  engine: z75.string(),
  analyzedAt: TimestampSchema,
  totalPages: z75.number().int(),
  /** Index i = page i+1. */
  pages: z75.array(PageLanguageSchema),
  proposal: DocumentTrimProposalSchema
});
var DocumentTrimDecisionSchema = z75.object({
  action: z75.enum(["applied", "applied-adjusted", "rejected"]),
  /** The ranges actually applied (absent on `rejected`). */
  appliedRanges: z75.array(PageRangeSchema).optional(),
  /** The `DocumentLanguageAnalysis.revision` this decision judged. */
  analysisRevision: z75.number().int(),
  decidedAt: TimestampSchema
});
var AnalyzeDocumentLanguagesRequestSchema = z75.object({
  modelId: ModelIdSchema,
  /** The document/candidate content hash (they share ids — publishing is identity-preserving). */
  docId: ModelDocumentIdSchema
});
var AnalyzeDocumentLanguagesResponseSchema = z75.object({
  analysis: DocumentLanguageAnalysisSchema
});
var TrimTargetSchema = z75.enum(["document", "candidate"]);
var TrimSelectionSchema = z75.object({
  mode: z75.enum(["keep", "remove"]),
  /** 1-based, ascending, non-overlapping. Bounds are checked server-side against the actual PDF. */
  ranges: z75.array(PageRangeSchema).min(1)
});
var ApplyDocumentTrimRequestSchema = z75.object({
  modelId: ModelIdSchema,
  /** Content hash of the document/candidate to cut (see `TrimTargetSchema`). */
  docId: ModelDocumentIdSchema,
  target: TrimTargetSchema,
  selection: TrimSelectionSchema,
  /**
   * The language proposal is what put these ranges in front of the reviewer — they applied it as
   * offered, or opened it for adjustment and edited it. Either way the cut answers FOR the proposal,
   * which is the whole of what `DocumentTrimDecisionSchema` records (`applied` vs `applied-adjusted`
   * then follows from whether the ranges still match). False for pages typed from scratch: those
   * judged no proposal, and scoring them would corrupt the corpus.
   */
  viaProposal: z75.boolean().optional()
});
var ApplyDocumentTrimResponseSchema = z75.object({
  /** The record that was cut — unchanged from the request, since a cut never re-keys anything. */
  docId: ModelDocumentIdSchema,
  /** Pages the document now has, so the caller can say what the cut actually did. */
  keptPages: z75.number().int(),
  totalPages: z75.number().int()
});
var RestoreDocumentTrimRequestSchema = z75.object({
  modelId: ModelIdSchema,
  docId: ModelDocumentIdSchema,
  target: TrimTargetSchema
});
var RestoreDocumentTrimResponseSchema = z75.object({
  /** The record that was restored — unchanged from the request, since nothing is ever re-keyed. */
  docId: ModelDocumentIdSchema
});

// src/document.types.ts
var ManualDigestSchema = z76.object({
  summary: z76.string(),
  specifications: z76.array(ModelAttributeSchema),
  maintenance: z76.array(
    z76.object({
      task: z76.string(),
      interval: z76.string().nullish(),
      details: z76.string().nullish()
    })
  ),
  troubleshooting: z76.array(z76.object({ problem: z76.string(), solution: z76.string() })),
  consumables: z76.array(
    z76.object({
      name: z76.string(),
      partNumber: z76.string().nullish(),
      spec: z76.string().nullish()
    })
  ),
  safety: z76.array(z76.string())
});
var ManualIngestStatusSchema = z76.enum(["pending", "ingested", "failed", "unsupported"]);
var ModelManualIngestSchema = z76.object({
  status: ManualIngestStatusSchema,
  /** When `status` was recorded: claim time for `pending`, completion time for terminal states. */
  at: TimestampSchema,
  /** Disposable OpenAI Files id for the uploaded PDF — a cache, re-derivable from `storageUrl`. */
  openaiFileId: z76.string().optional()
});
var ModelDocTagSchema = z76.enum([
  // App-visible kinds (the APP_VISIBLE_TAGS set) — enum order drives display order, owner-manual first.
  "owner-manual",
  "quick-reference-guide",
  "install-guide",
  "product-data",
  "service-manual",
  "warranty",
  // Library-only kinds: confirmable (right product, honestly classified) but never served in the app.
  "parts-list",
  "catalog",
  "sell-sheet",
  "brochure",
  "other",
  // Metadata tags (the METADATA_TAGS set): flags on top of a kind, not kinds themselves. LAST in the
  // enum on purpose — the picker submits in enum order, so a metadata tag never lands in `tags[0]`
  // (the display-label slot) when a kind tag is present.
  "model-nomenclature",
  "customer-service-contact"
]);
var DOC_TAG_LABELS = {
  "owner-manual": "Owner's Manual",
  "quick-reference-guide": "Quick Reference Guide",
  "install-guide": "Installation Guide",
  "product-data": "Product Data",
  "service-manual": "Service Manual",
  warranty: "Warranty",
  "parts-list": "Parts List",
  catalog: "Catalog",
  "sell-sheet": "Sell Sheet",
  brochure: "Brochure",
  other: "Other",
  "model-nomenclature": "Model Nomenclature",
  "customer-service-contact": "Customer Service Contact"
};
var ModelDocClassificationSchema = ModelDocTagSchema;
var APP_VISIBLE_TAGS = [
  "owner-manual",
  "quick-reference-guide",
  "install-guide",
  "product-data",
  "service-manual",
  "warranty"
];
var DIGESTIBLE_TAGS = ["owner-manual"];
var METADATA_TAGS = ["model-nomenclature", "customer-service-contact"];
var CoverageRangeSchema = z76.object({
  prefix: ModelCanonicalSchema.min(2),
  from: z76.number().int().nonnegative(),
  to: z76.number().int().nonnegative(),
  /**
   * Exact digit width of the bounded number, when it is written zero-padded. Set only where the stem
   * absorbed leading DIGITS (`47001`–`47019` → stem `470`, bounds `01`–`19`): the width is then part of
   * the meaning and the numbers alone no longer carry it, so a matcher comparing numerically would let
   * the shorter `4701` in. Absent for an alphabetic stem, where a digit run of any length is the whole
   * number and variable width is correct (`KSM45`–`KSM200` must match both `KSM45` and `KSM150`).
   */
  digits: z76.number().int().positive().optional()
});
var DocumentCoverageSchema = z76.object({
  /**
   * Human-confirmed model-number prefixes, uppercase-alnum, each ≥ 2 chars — the schema enforces the
   * floor (`MIN_COVERAGE_PREFIX_LENGTH` in @rock/manuals, literal here: core can't depend on domains)
   * because a 1-char prefix would blanket an entire brand in the backfill sweep while staying
   * invisible to forward lookups (which start at 2). May be empty when `ranges` carries the rule;
   * "at least one predicate overall" is enforced at write time (`assertValidCoverageRule`), not here —
   * a parse-time floor would make already-stored documents unreadable, never catchable at write time.
   */
  prefixes: z76.array(ModelCanonicalSchema.min(2)),
  /** Human-confirmed numeric ranges (see `CoverageRangeSchema`). Absent = prefix-only rule. */
  ranges: z76.array(CoverageRangeSchema).optional(),
  /** When a human confirmed this coverage. */
  confirmedAt: TimestampSchema
});
var DocumentTrimSchema = z76.object({
  /**
   * Object path of the FULL pdf these pages came from. A PATH, not a URL: the app bucket is the
   * canonical byte store, so this stays resolvable after the docs mirror moves or drops a public copy.
   * Undoing a cut is one write — repoint storage here and drop this field.
   *
   * Pinned to the ORIGINAL across successive cuts, so undo always returns the WHOLE pdf rather than the
   * previous pass, and each pass re-cuts from the full original instead of re-encoding cut bytes.
   */
  sourcePath: z76.string(),
  /**
   * The 1-based ranges kept, always expressed against the SOURCE's page numbering — a reviewer types
   * ranges against what they can SEE, and `composeKeepRanges` in @rock/manuals maps those back here.
   */
  keepRanges: z76.array(PageRangeSchema),
  trimmedAt: TimestampSchema
});
var ModelDocumentSchema = z76.object({
  object: z76.literal("document"),
  schema: z76.literal(1),
  /**
   * Content hash of the PDF bytes we ACQUIRED (see `ModelDocumentIdSchema`) — equals the candidate id it
   * came from. It identifies the manual we found at these URLs, not the bytes currently served: a page
   * cut replaces the bytes in place and the id stays put, which is what keeps a cut from fragmenting a
   * document that several models link. Every consumer wants the acquisition identity — the worker
   * re-hashes a FETCHED pdf to recognize an already-seen candidate, and a cross-model publish of the
   * same source bytes reuses this artifact rather than duplicating it.
   */
  id: ModelDocumentIdSchema,
  /**
   * What this document is (1+). First tag drives the default display label. Each tag parses
   * independently (`.catch`): a kind added after a reader was compiled drops that ONE tag on the old
   * bundle instead of failing the whole artifact — and with it every manual the app serves from it.
   *
   * That protection covers a MULTI-tagged document. One tagged solely with a value the reader doesn't
   * know still filters to empty and fails the `.refine`, exactly as before — so shipping a new tag to
   * every surface before anything writes it alone remains the safe order.
   */
  tags: z76.array(ModelDocTagSchema.optional().catch(void 0)).transform((tags) => tags.filter((tag) => !!tag)).refine((tags) => tags.length > 0, "A document needs at least one tag"),
  /** Optional display-title override; defaults to `DOC_TAG_LABELS[tags[0]]`. */
  title: z76.string().optional(),
  /**
   * The brands this document is FOR — a stated fact about the PDF, like its tags. Plural because one
   * volume can serve a shared nameplate (Whirlpool and KitchenAid sell the same WRF555). A model's
   * `brandId` must be listed for coverage to reach it, and a direct link to a model of another brand is
   * flagged in admin. "At least one" is enforced at write time (`assertValidDocumentScope` in
   * @rock/manuals), not here, for the reason `DocumentCoverageSchema.prefixes` gives.
   */
  brandIds: z76.array(BrandIdSchema),
  /**
   * The equipment types this document is FOR. Plural because combined manufacturer volumes span
   * categories (fan coils AND furnaces). Matched through `typeAffinity`: a model of a listed type fits,
   * one whose type shares a family with a listed type fits flagged. Write-time floor as `brandIds`.
   */
  typeIds: z76.array(EquipmentTypeIdSchema),
  /**
   * Canonical object path of our copy in the app Storage bucket (`models/{modelId}/…` — paths keep the
   * tree they were staged under; for a shared artifact the path is an opaque stable key, not
   * ownership) — the source of truth `storageUrl` derives from, stored bucket-agnostically so serving
   * can move domains without a data migration. Absent only on legacy documents published before paths
   * were recorded; readers fall back to extracting the path from `storageUrl` (`documentStoragePath`
   * in `@rock/manuals`).
   */
  storagePath: z76.string().optional(),
  /**
   * The serving URL — what clients open. Denormalized from `storagePath` by the docs-mirror trigger
   * (`https://docs.rockhome.app/...` once bytes are mirrored; the staged token URL until then) and kept
   * as a field because fielded mobile builds read it directly. New code reads `storagePath`.
   */
  storageUrl: z76.string(),
  /**
   * Where the PDF came from — provenance, never a serving pointer (that's `storageUrl`). Two shapes are
   * both correct: a manufacturer/retailer page the worker fetched it from, or — when an admin UPLOADED
   * the file — OUR OWN Storage URL under the model's `offers/` prefix, which is the only record of bytes
   * that never had an external address. So "points at our host" does NOT mean "wrong": use
   * `isUploadedSource` in @rock/manuals, which keys on the `offers/` prefix, rather than the hostname.
   * A cut never rewrites this field; the pages changed, not where the document came from.
   */
  sourceUrl: z76.string().optional(),
  /** Present when these bytes are a page-level cut of a larger PDF: see `DocumentTrimSchema`. */
  trim: DocumentTrimSchema.optional(),
  digest: ManualDigestSchema.optional(),
  ingest: ModelManualIngestSchema.optional(),
  /** Present when this document covers a whole class of model numbers (see `DocumentCoverageSchema`). */
  coverage: DocumentCoverageSchema.optional(),
  /**
   * A newer revision of this document (an admin's "Replace with newer revision"). On a model that links
   * both, the newer one is current and this one is superseded, however each is linked; where
   * only this one reaches, it stays current. A chain resolves to its newest linked revision. The newer one
   * must carry this one's lead kind (`canSupersede` in @rock/manuals), or it supersedes nothing.
   */
  replacedBy: ModelDocumentIdSchema.optional(),
  replacedAt: TimestampSchema.optional(),
  /** When the document was first published (vouched for) by a human. */
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var CoveragePredicateSchema = z76.discriminatedUnion("match", [
  z76.object({ match: z76.literal("prefix"), prefix: ModelCanonicalSchema.min(2) }),
  CoverageRangeSchema.extend({ match: z76.literal("range") })
]);
var DocumentCoverageEntrySchema = z76.object({
  docId: ModelDocumentIdSchema,
  /** The covering document's `typeIds` at projection time — readers apply the type tier in code. */
  typeIds: z76.array(EquipmentTypeIdSchema).min(1),
  /** Every predicate of this document's rule that files under this bucket (several can share one). */
  predicates: z76.array(CoveragePredicateSchema).min(1)
});
var DocumentCoverageProjectionSchema = z76.object({
  object: z76.literal("document-coverage"),
  schema: z76.literal(2),
  id: DocumentCoverageKeySchema,
  brandId: BrandIdSchema,
  /** The bucket this key represents (also derivable from the id; stored for legibility). */
  bucket: ModelCanonicalSchema,
  entries: z76.array(DocumentCoverageEntrySchema),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});

// src/document_acquisition.types.ts
import * as z78 from "zod";

// src/rock_manuals_job.types.ts
import * as z77 from "zod";
var RockManualsJobStatusSchema = z77.enum([
  "pending",
  "running",
  "succeeded",
  "reviewed",
  "failed",
  "unavailable",
  "cancelled"
]);
var ManualFetchTierSchema = z77.enum(["impersonate", "browser", "off-ip", "human-browser"]);
var ManualFetchPolicySchema = z77.enum([
  "open-plain",
  "warm-impersonate-home",
  "browser-home",
  "build-host-resolver",
  "route-off-ip"
]);
var RockManualsJobSchema = z77.object({
  object: z77.literal("rock-manuals-job"),
  schema: z77.literal(1),
  id: RockManualsJobIdSchema,
  modelId: ModelIdSchema,
  // Snapshot the producer may pass for human-readable logs / a head start. The worker re-reads the
  // Model doc as the source of truth (the model number is authoritative; a passed `equipmentType` is a hint).
  modelNumber: z77.string().optional(),
  brandId: BrandIdSchema.optional(),
  spaceId: SpaceIdSchema.optional(),
  equipmentType: z77.string().optional(),
  /**
   * Discovered candidate manual URLs, accumulated across attempts (the producer may seed it; the worker
   * unions in fresh discovery each attempt). Later attempts run only their NEW broadened queries and lean
   * on this pool for the rest, so retries don't re-pay SERP for the same results.
   */
  candidateUrls: z77.array(z77.string()).optional(),
  /**
   * The `document-searches` doc this run is servicing, when the job was armed by an admin request.
   * The worker mirrors status/attempts onto it so the rock-admin buttons resolve. Absent for ambient
   * (new-model) enqueues, which have no admin waiting on them.
   */
  activeSearchId: DocumentSearchIdSchema.optional(),
  status: RockManualsJobStatusSchema,
  attempts: z77.number().default(0),
  maxAttempts: z77.number().default(5),
  /** Job is claimable when status is pending and `nextAttemptAt` <= now (backoff gate). */
  nextAttemptAt: TimestampSchema.optional(),
  /** While running, the lease deadline; an expired lease is reclaimable by another worker. */
  leaseExpiresAt: TimestampSchema.optional(),
  lockedBy: z77.string().optional(),
  lastError: z77.string().optional(),
  /**
   * When an admin cancelled the job. Set with `status: 'cancelled'`, but it is THIS field the worker's
   * `isClaimable` gate checks — so a cancel survives an in-flight attempt re-parking the status to
   * `pending` on its way out. Cleared on re-enqueue (Re-run search / offer a URL/file).
   */
  cancelledAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});

// src/document_acquisition.types.ts
var DocPublisherSchema = z78.enum(["manufacturer", "third-party", "unknown"]);
var StatedModelRangeSchema = z78.object({
  from: z78.string(),
  to: z78.string()
});
var DocumentDatesSchema = z78.object({
  /** PDF `CreationDate`. */
  createdAt: TimestampSchema.optional(),
  /** PDF `ModDate` — often a re-export of an older document, so it dates the FILE, not the edition. */
  modifiedAt: TimestampSchema.optional()
});
var CandidateVerdictSchema = z78.object({
  /** What the document IS, on the typed vocabulary (only `ModelDocTag` values are publishable tags). */
  classification: ModelDocClassificationSchema,
  /** Additional roles the same PDF covers (combined Installer+Owner volumes). */
  alsoCovers: z78.array(ModelDocClassificationSchema).optional(),
  /** Identity gate: does the doc cover the claimed product (exact code OR an evidenced identity)? */
  identityMatch: z78.enum(["yes", "no", "unsure"]),
  /** Provenance gate: who authored the document. Absent on pre-v5 verdicts. */
  publisher: DocPublisherSchema.optional(),
  confidence: z78.number(),
  reason: z78.string().optional(),
  /** Verifier's free-text read of what the doc actually is ("TEM4 series air handler installer guide"). */
  docIdentity: z78.string().optional(),
  coversModels: z78.array(z78.string()).optional(),
  /**
   * Stated model ranges, structured (see `StatedModelRangeSchema`) — what the publish form turns into
   * one-click range suggestions. `coversModels` carries the same fact as text and stays the input to
   * the identity backstop; this field exists so coverage predicates never come from string-guessing.
   * Absent on verdicts judged before the verifier emitted it.
   */
  coversRanges: z78.array(StatedModelRangeSchema).optional(),
  /**
   * Claimed identifiers that literally appear in the document, per the deterministic whole-document
   * scan (same provenance as `coversModels`, which is the LLM's extraction). The auto-confirm gate's
   * identity-strength check reads this: a literal hit is stronger evidence than range membership or a
   * family match. Absent on pre-gate verdicts and on vision-judged scans with no text layer.
   */
  literalHits: z78.array(z78.string()).optional(),
  /**
   * What the PDF says about its own dates (see `DocumentDatesSchema`). Rides on the verdict because
   * that is the channel already plumbed from the bytes to the record — and it can't go stale there:
   * candidates are content-hash keyed, so a re-verify re-derives the same dates from the same bytes.
   * Absent on verdicts recorded before this field existed, and on any document whose DocInfo omits or
   * malforms them (an unparseable date is dropped, never guessed).
   */
  documentDates: DocumentDatesSchema.optional(),
  /** Which fetch tier produced the bytes. */
  fetchTier: ManualFetchTierSchema.optional(),
  /**
   * How the verifier judged it: page-text, rendered-page vision (scanned PDFs), or `metadata` — the
   * deterministic decoy screen settled it from the PDF's DocInfo and no LLM call was made.
   */
  verifiedVia: z78.enum(["text", "vision", "metadata"]).optional(),
  /**
   * Fingerprint of the CLAIM (verifier revision + brand + type + model + identities) this verdict was
   * reached under — a retry only trusts a verdict whose fingerprint matches the current claim.
   */
  claimFingerprint: z78.string().optional()
});
var CandidateGateCheckSchema = z78.object({
  pass: z78.boolean(),
  /** Why it passed/failed ("assets.carrier.com is brand-owned", "no literal hit (range evidence only)"). */
  detail: z78.string().optional()
});
var CandidateGateSchema = z78.object({
  /** The composite result: true only when every check passed. */
  pass: z78.boolean(),
  /** `GATE_REVISION` the checks ran under — results from older revisions aren't comparable. */
  gateRevision: z78.number(),
  evaluatedAt: TimestampSchema,
  checks: z78.object({
    /** The verdict's provenance gate: `publisher === 'manufacturer'` (absent/unknown/third-party fail). */
    publisher: CandidateGateCheckSchema,
    /** Some source or linking URL is brand-owned, trusted for the brand, or on the curated trusted-host list. */
    domainTier: CandidateGateCheckSchema,
    /** The claimed model appears literally (scan hit / exact covers-models entry), not just via range/family. */
    identityStrength: CandidateGateCheckSchema,
    /** Verifier confidence clears the auto-confirm bar (stricter than the accept bar). */
    confidence: CandidateGateCheckSchema
  })
});
var CandidateDispositionSchema = z78.enum(["published", "not-relevant"]);
var DiscoveryAiOverviewSchema = z78.object({
  /** The overview's prose, flattened and capped by the producer. */
  text: z78.string(),
  references: z78.array(
    z78.object({
      link: z78.string(),
      title: z78.string().optional(),
      source: z78.string().optional()
    })
  )
});
var DiscoverySearchStepSchema = z78.object({
  kind: z78.literal("search"),
  engine: z78.literal("google"),
  query: z78.string(),
  /** Whether the `cr=countryUS` restrict was applied (see the US-first discovery policy). */
  restrictToUs: z78.boolean().optional(),
  resultCount: z78.number(),
  aiOverview: DiscoveryAiOverviewSchema.optional()
});
var DiscoveryResultStepSchema = z78.object({
  kind: z78.literal("result"),
  position: z78.number(),
  url: z78.string(),
  title: z78.string().optional(),
  snippet: z78.string().optional()
});
var DiscoveryPageStepSchema = z78.object({
  kind: z78.literal("page"),
  url: z78.string(),
  title: z78.string().optional(),
  /** Whether the page's text literally contained the claimed model (the identity evidence gate). */
  mentionsModel: z78.boolean().optional(),
  /**
   * The model designation the page itself states, when it differs from the claim — e.g. a PDP listing
   * the family code with the claimed model's trailing digits absent. EVIDENCE of family identity, where
   * the derived prefix is only a guess.
   */
  observedModel: z78.string().optional(),
  /** Storage path of a captured screenshot of this page, once the capture flow exists. */
  screenshotPath: z78.string().optional()
});
var DiscoveryLinkStepSchema = z78.object({
  kind: z78.literal("link"),
  href: z78.string(),
  anchorText: z78.string().optional(),
  /** The resolver's relevance score at follow time (see `resolveManualLinks`). */
  score: z78.number().optional()
});
var DiscoveryDocumentStepSchema = z78.object({
  kind: z78.literal("document"),
  url: z78.string(),
  byteSize: z78.number(),
  /** Content hash of the fetched bytes — the candidate id these bytes merge into. */
  contentHash: z78.string()
});
var DiscoveryStepSchema = z78.discriminatedUnion("kind", [
  DiscoverySearchStepSchema,
  DiscoveryResultStepSchema,
  DiscoveryPageStepSchema,
  DiscoveryLinkStepSchema,
  DiscoveryDocumentStepSchema
]);
var DiscoveryTrailSchema = z78.array(DiscoveryStepSchema);
var CandidateTrailSchema = z78.object({
  steps: DiscoveryTrailSchema,
  at: TimestampSchema
});
var ModelDocumentCandidateSchema = z78.object({
  object: z78.literal("model-document-candidate"),
  schema: z78.literal(1),
  id: ModelDocumentIdSchema,
  modelId: ModelIdSchema,
  /** Every URL these exact bytes were fetched from (grows on hash-dedup merges). */
  urls: z78.array(z78.string()).min(1),
  /**
   * Pages the worker discovered these bytes' URLs ON (the HTML-resolution hop's parent pages).
   * Provenance for the auto-confirm gate's domain-tier check: OEM sites commonly serve manual bytes
   * from shared CDNs, and the brand's own page LINKING the PDF is the attestation — credited
   * per-document, without the CDN host ever becoming trusted. Grows on merges like `urls`.
   */
  linkedFrom: z78.array(z78.string()).optional(),
  /**
   * The discovery paths that reached these bytes ("Google {query} → result #2 → the Home Depot page →
   * the Owner's Manual link → this PDF") — the provenance a reviewer judges from, rendered in
   * rock-admin. Newest first, deduped + capped by `mergeTrails` in @rock/manuals. Absent on candidates
   * recorded before trails existed and on paths with no recorded lineage (offer pins, source refreshes).
   */
  trails: z78.array(CandidateTrailSchema).optional(),
  /**
   * Our staged Storage copy (`models/{modelId}/candidates/{hash}.pdf`) — previewable, publishable.
   * Absent on URL-only candidates: migrated human vetoes (pre-documents "disputed" entries had no
   * bytes) and `oversize` records. Such candidates can't be previewed or published from staging.
   */
  stagedStorageUrl: z78.string().optional(),
  /**
   * Object path of the staged copy (`models/{modelId}/candidates/{hash}.pdf`) — the bucket-agnostic
   * source of truth `stagedStorageUrl` derives from. Absent on candidates staged before paths were
   * recorded (and on URL-only candidates); readers fall back to extracting it from the URL.
   */
  stagedStoragePath: z78.string().optional(),
  byteSize: z78.number().optional(),
  /**
   * The download exceeded the staging size cap, so only the URL is recorded (no bytes, no verify).
   * Permanent for these bytes — the worker never re-downloads the URL. The admin remedy is opening the
   * source URL and, if it's the right document, downloading it and using Offer file, which waives the
   * cap up to the sanity ceiling (`MANUALS_OVERSIZE_MAX_BYTES`) exactly like "Bring it in". A doc past
   * the ingest cap still serves in the app — only its digest degrades to `unsupported`.
   */
  oversize: z78.literal(true).optional(),
  verdict: CandidateVerdictSchema,
  /**
   * Shadow auto-confirm gate result for the CURRENT verdict + URL set (admin-only visibility +
   * calibration data; see `CandidateGateSchema`). Refreshed alongside the verdict on re-observations;
   * absent on pre-gate candidates and URL-only records (oversize — nothing was verified).
   */
  gate: CandidateGateSchema.optional(),
  disposition: CandidateDispositionSchema.optional(),
  /** Set while `disposition === 'published'`; equals `id` (publishing is identity-preserving). */
  publishedDocId: ModelDocumentIdSchema.optional(),
  /**
   * Per-page language analysis of the staged bytes + the English-only trim proposal it implies —
   * written by rock-functions (auto on candidate creation; on demand from rock-admin). Keyed to the
   * bytes by construction (the candidate IS the bytes), so it never goes stale on merges.
   */
  languageAnalysis: DocumentLanguageAnalysisSchema.optional(),
  /**
   * What a human did with the trim proposal (applied / adjusted / rejected) — recorded so proposal
   * accuracy and a future auto-trim confidence threshold can be measured from stored data.
   */
  trimDecision: DocumentTrimDecisionSchema.optional(),
  /**
   * Present when an admin cut pages out of the staged bytes before publishing (see
   * `DocumentTrimSchema`). The cut is applied IN PLACE — this record keeps its id, `urls`, `verdict`,
   * `gate` and `disposition`, because it is still the acquisition it always was — and rides onto the
   * document this candidate publishes as, so the cut survives the publish.
   */
  trim: DocumentTrimSchema.optional(),
  /** When these bytes were first fetched + verified. */
  triedAt: TimestampSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var DocumentSearchStatusSchema = z78.enum(["requested", "running", "succeeded", "gave-up"]);
var DocumentSearchOfferOutcomeSchema = z78.enum(["candidate", "none"]);
var DocumentSearchOriginSchema = z78.enum(["revisit"]);
var DocumentSearchSchema = z78.object({
  object: z78.literal("document-search"),
  schema: z78.literal(1),
  id: DocumentSearchIdSchema,
  modelId: ModelIdSchema,
  requestedAt: TimestampSchema,
  /** Admin-pasted candidate link (BYOM), pinned before discovery. */
  offeredUrl: z78.string().optional(),
  /**
   * The admin explicitly chose to bring an oversize document in ("Bring it in" on an oversize
   * candidate): the staging size cap is waived for the OFFER pin — up to the worker's sanity ceiling —
   * so the bytes get staged, verified, and become a normal reviewable candidate.
   */
  allowOversize: z78.literal(true).optional(),
  /**
   * The offer is a newer revision of this document, filed from its page: the candidate it yields reviews
   * as that revision, and publishing it marks the old document `replacedBy` it.
   */
  replaces: ModelDocumentIdSchema.optional(),
  /** System-filed request (absent = an admin asked). See DocumentSearchOriginSchema. */
  origin: DocumentSearchOriginSchema.optional().catch(void 0),
  status: DocumentSearchStatusSchema,
  /** What the offer chain itself yielded, judged as soon as the worker has tried the offered URL. */
  offerOutcome: DocumentSearchOfferOutcomeSchema.optional(),
  /** Worker detail behind `offerOutcome` ("accepted abc123…", "page had no document links"). */
  offerNote: z78.string().optional(),
  /** Worker attempts consumed by this search (mirrors the queue job while it runs). */
  attempts: z78.number().default(0),
  lastError: z78.string().optional(),
  finishedAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});

// src/model_number_help.types.ts
import * as z79 from "zod";
var ModelNumberHelpIdSchema = z79.string().brand();
var ModelNumberHelpCacheKeySchema = z79.string().brand();
var ModelNumberHelpRequestSchema = z79.object({
  object: z79.literal("model-number-help-request"),
  schema: z79.literal(1),
  id: ModelNumberHelpIdSchema,
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  typeId: EquipmentTypeIdSchema,
  api: z79.enum(["chatgpt", "perplexity"]).default("chatgpt"),
  stream: z79.boolean().optional(),
  /**
   * Asked from preview mode — the type may exist only as a staged draft. Client-set and trusted: a
   * non-admin setting it gets help on an unpublished type, which is accepted.
   */
  preview: z79.boolean().optional(),
  status: z79.enum(["requested", "in-progress", "complete", "error"]),
  response: z79.string().optional(),
  deltas: z79.array(z79.string()).optional(),
  // Set once at creation and never touched again — `updatedAt` is bumped on every streaming delta.
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
  eventId: z79.string().optional(),
  error: z79.string().optional()
});
var ModelNumberHelpCacheSchema = z79.object({
  object: z79.literal("model-number-help-cache"),
  schema: z79.literal(1),
  id: ModelNumberHelpCacheKeySchema,
  response: z79.string(),
  updatedAt: TimestampSchema
});

// src/notification.types.ts
import * as z80 from "zod";
var NotificationTypeSchema = z80.enum([
  "welcome",
  "push-prompt",
  "model-manual-added",
  "weekend-checklist",
  "weekend-start",
  "new-consideration",
  "image-enhanced",
  "plan-ready",
  "equipment-recall",
  "equipment-brand-needed",
  "home-member-joined"
]);
function isNotificationType(value) {
  return NotificationTypeSchema.safeParse(value).success;
}
var WelcomeDataSchema = z80.object({});
var PushPromptDataSchema = z80.object({});
var ModelManualDataSchema = z80.object({
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema.nullish(),
  // Human-readable model name (e.g. "Bosch SHEM63W55N"), used in copy across channels.
  modelName: z80.string(),
  // Shorter model label (e.g. "SHEM63W55N") for terse renderings.
  modelLabel: z80.string(),
  // Brand label (e.g. "Bosch") for natural-language copy; absent when the model has no known brand.
  brandLabel: z80.string().nullish(),
  // Equipment-type label (e.g. "Dishwasher") for natural-language copy.
  typeLabel: z80.string(),
  equipmentCount: z80.number().int().nonnegative()
});
var EquipmentRecallDataSchema = z80.object({
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema,
  recallId: RecallIdSchema,
  verdict: z80.enum(["affected", "possibly-affected"]),
  recallTitle: z80.string(),
  hazard: z80.string().nullish(),
  remedy: z80.string().nullish(),
  /** An admin's guidance beside the notice (`Recall.additionalGuidance`). */
  additionalGuidance: z80.string().nullish(),
  brandLabel: z80.string().nullish(),
  /** The unit's model number as the homeowner sees it, named beside the unit in the copy. */
  modelNumber: z80.string().nullish(),
  // Equipment-type label (e.g. "Water heater") for natural-language copy.
  typeLabel: z80.string()
});
var EquipmentBrandNeededDataSchema = z80.object({
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema,
  /** Equipment-type label (e.g. "Dishwasher") for natural-language copy. */
  typeLabel: z80.string(),
  /** What names the product, as the homeowner typed it: a model number, or the name it was sold under. */
  subject: z80.object({ kind: z80.enum(["model-number", "product-name"]), display: z80.string() }),
  /** The brands to pick from, as they display. */
  brandNames: z80.array(z80.string()).min(2)
});
var WeekendChecklistTaskItemSchema = z80.object({
  title: z80.string(),
  overdueLabel: z80.string().nullish()
});
var WeekendChecklistReviewItemSchema = z80.object({
  title: z80.string(),
  /** Set on a `consider` row. */
  considerationId: z80.string().optional(),
  /** Set on a `learn` row. */
  lessonId: z80.string().optional(),
  kind: z80.enum(["consider", "learn"])
});
var WeekendChecklistDataSchema = z80.object({
  homeId: HomeIdSchema,
  userName: z80.string().nullish(),
  homeName: z80.string().nullish(),
  // True when the recipient owns more than one active home. The email channel qualifies the subject
  // with the home name only then ("…Checklist for {home}"); a single-home user keeps the cleaner
  // "…Checklist". The body uses `homeName` regardless.
  multipleHomes: z80.boolean().nullish(),
  weekendDateLabel: z80.string().nullish(),
  nextWeekendDateLabel: z80.string().nullish(),
  thisWeekend: z80.array(WeekendChecklistTaskItemSchema),
  nextWeekend: z80.array(z80.object({ title: z80.string() })),
  // Scheduled suggestion + lesson rows (Suggested Reminders / Important to Know, by `kind`) whose
  // `reviewAt` falls on each weekend.
  // Defaulted so bus docs written before this field existed still parse.
  thisWeekendReviews: z80.array(WeekendChecklistReviewItemSchema).default([]),
  nextWeekendReviews: z80.array(WeekendChecklistReviewItemSchema).default([])
});
var WeekendSectionCountsSchema = z80.object({
  actions: z80.number().int().nonnegative(),
  recommendations: z80.number().int().nonnegative(),
  lessons: z80.number().int().nonnegative()
});
var WeekendStartDataSchema = z80.object({
  homeId: HomeIdSchema,
  homeName: z80.string().nullish(),
  multipleHomes: z80.boolean().nullish(),
  remainingCount: z80.number().int().positive(),
  sectionCounts: WeekendSectionCountsSchema.nullish(),
  firstItemTitle: z80.string()
});
var NewConsiderationDataSchema = z80.object({
  homeId: HomeIdSchema,
  spaceId: SpaceIdSchema.nullish(),
  equipmentId: EquipmentIdSchema.nullish(),
  considerationId: z80.string(),
  considerationTitle: z80.string(),
  considerationDescription: z80.string().nullish(),
  criticality: ConsiderationCriticalitySchema.nullish(),
  homeName: z80.string().nullish(),
  homeLocation: z80.string().nullish(),
  equipmentNames: z80.string().nullish(),
  equipmentIcon: z80.string().nullish(),
  equipmentImageUrl: z80.string().nullish()
});
var ImageEnhancedDataSchema = z80.object({
  homeId: HomeIdSchema,
  roomId: RoomIdSchema,
  roomName: z80.string(),
  candidateImageUrl: z80.string()
});
var PlanReadyDataSchema = z80.object({
  homeId: HomeIdSchema,
  homeName: z80.string().nullish(),
  suggestionCount: z80.number()
});
var HomeMemberJoinedDataSchema = z80.object({
  homeId: HomeIdSchema,
  homeName: z80.string().nullish(),
  memberName: z80.string().nullish()
});
var ChannelOutcomeSchema = z80.object({
  sent: z80.boolean(),
  reason: z80.string().nullish(),
  error: z80.string().nullish(),
  decidedAt: TimestampSchema
});
var NotificationImageIconSchema = z80.object({
  type: z80.literal("icon"),
  icon: z80.string()
});
var NotificationImageUrlSchema = z80.object({
  type: z80.literal("url"),
  url: z80.string()
});
var NotificationImageEquipmentSchema = z80.object({
  type: z80.literal("equipment"),
  // The home the equipment lives in. Notifications are a cross-home inbox, so the app must resolve
  // the equipment doc against THIS home, not the active one. Nullish for docs written before this
  // field existed — readers fall back to the notification's top-level `homeId` (same home).
  homeId: HomeIdSchema.nullish(),
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema,
  /** A mark over the thumbnail naming what the notification is about: `recall` is the yellow shield. */
  badge: z80.enum(["recall"]).nullish()
});
var NotificationImageSchema = z80.discriminatedUnion("type", [
  NotificationImageIconSchema,
  NotificationImageUrlSchema,
  NotificationImageEquipmentSchema
]);
var NotificationActionSchema = z80.discriminatedUnion("type", [
  z80.object({ type: z80.literal("enable-push") }),
  z80.object({ type: z80.literal("navigate"), label: z80.string(), path: z80.string() })
]);
var UserNotificationBaseSchema = z80.object({
  object: z80.literal("user-notification"),
  schema: z80.literal(1),
  id: UserNotificationIdSchema,
  userId: UserIdSchema,
  createdAt: TimestampSchema,
  // Generic top-level pointer kept only for routing/auditing convenience; the authoritative,
  // type-specific pointers live in `data`. Absent for account-level notifications.
  homeId: HomeIdSchema.nullish(),
  // Filled in by each channel listener when it runs; absent until then.
  email: ChannelOutcomeSchema.nullish(),
  push: ChannelOutcomeSchema.nullish(),
  app: ChannelOutcomeSchema.nullish()
});
var UserNotificationSchema = z80.discriminatedUnion("type", [
  UserNotificationBaseSchema.extend({ type: z80.literal("welcome"), data: WelcomeDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("push-prompt"), data: PushPromptDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("model-manual-added"), data: ModelManualDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("weekend-checklist"), data: WeekendChecklistDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("weekend-start"), data: WeekendStartDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("new-consideration"), data: NewConsiderationDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("image-enhanced"), data: ImageEnhancedDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("plan-ready"), data: PlanReadyDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("home-member-joined"), data: HomeMemberJoinedDataSchema }),
  UserNotificationBaseSchema.extend({ type: z80.literal("equipment-recall"), data: EquipmentRecallDataSchema }),
  UserNotificationBaseSchema.extend({
    type: z80.literal("equipment-brand-needed"),
    data: EquipmentBrandNeededDataSchema
  })
]);
var InAppNotificationSchema = z80.object({
  object: z80.literal("in-app-notification"),
  schema: z80.literal(1),
  id: NotificationIdSchema,
  // Links back to the intake bus doc. ABSENT for direct writes (rock-admin test form).
  userNotificationId: UserNotificationIdSchema.nullish(),
  // For icon fallback / analytics — NOT required to render (display fields are pre-rendered).
  type: NotificationTypeSchema,
  createdAt: TimestampSchema,
  // Surfaced by the in-app builder for routing/labeling. The home this notification is *about*,
  // when it's home-specific; absent for account-level notifications (welcome, push prompt).
  // Notifications are stored under the user, not the home; this pointer is what lets a tap
  // deep-link into the right home (switching the active home if needed) and lets the UI show
  // which home a notification belongs to.
  homeId: HomeIdSchema.nullish(),
  spaceId: SpaceIdSchema.nullish(),
  // Server-rendered presentation.
  title: z80.string().max(100),
  summary: z80.string().max(250),
  content: z80.string(),
  image: NotificationImageSchema.nullish(),
  // `.catch(null)` so a future action *kind* an older app build doesn't recognize degrades to "no
  // action" instead of failing the whole inbox doc on parse — new `navigate` destinations need no
  // release, but a brand-new kind does, and shouldn't break inboxes that predate it.
  action: NotificationActionSchema.nullish().catch(null),
  // App-managed interaction state.
  read: z80.boolean(),
  readAt: TimestampSchema.nullish(),
  archived: z80.boolean(),
  archivedAt: TimestampSchema.nullish()
});
var EmailChannelConfigSchema = z80.object({
  suppressed: z80.boolean().nullish(),
  firstSentAt: TimestampSchema.nullish()
});
var PushChannelConfigSchema = z80.object({
  suppressed: z80.boolean().nullish()
});
var AppChannelConfigSchema = z80.object({
  suppressed: z80.boolean().nullish()
});
var NotificationChannelConfigSchema = z80.object({
  email: EmailChannelConfigSchema.nullish(),
  push: PushChannelConfigSchema.nullish(),
  app: AppChannelConfigSchema.nullish()
});
var NotificationChannelDefaultsSchema = z80.object({
  email: z80.object({ suppressed: z80.boolean() }),
  push: z80.object({ suppressed: z80.boolean() }),
  app: z80.object({ suppressed: z80.boolean() })
});
var NotificationConfigSchema = z80.object({
  object: z80.literal("notification-config"),
  schema: z80.literal(1),
  id: NotificationTypeSchema,
  userId: UserIdSchema,
  email: EmailChannelConfigSchema.nullish(),
  push: PushChannelConfigSchema.nullish(),
  app: AppChannelConfigSchema.nullish(),
  // When this notification type was first emitted onto the bus for this user — doc-level (the bus event
  // is channel-agnostic), distinct from the per-channel `sent` outcomes. Its presence gates one-time
  // types (welcome, push-prompt) to a single emission ever; the scheduled weekend types are timed per
  // home (`WeekendFill`) and use `email.firstSentAt` instead, leaving this unset. Stamped on the
  // per-type config doc, where send-state already lives, rather than on the user doc.
  firstNotifiedAt: TimestampSchema.nullish(),
  updatedAt: TimestampSchema
});
var NotificationConfigDefaultsSchema = z80.object({
  object: z80.literal("notification-config-defaults"),
  schema: z80.literal(1),
  id: z80.literal("notification-config"),
  defaults: z80.record(z80.string(), z80.unknown()).transform((raw) => Object.fromEntries(Object.entries(raw).filter(([type]) => isNotificationType(type)))).pipe(z80.partialRecord(NotificationTypeSchema, NotificationChannelDefaultsSchema)),
  updatedAt: TimestampSchema
});
var NOTIFICATION_CONFIG_DEFAULTS = {
  welcome: { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } },
  "push-prompt": { email: { suppressed: true }, push: { suppressed: true }, app: { suppressed: false } },
  "model-manual-added": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } },
  "weekend-checklist": { email: { suppressed: false }, push: { suppressed: true }, app: { suppressed: true } },
  // Push-only. Safe without the in-app channel because the type declares a `target`, so the tap
  // never falls back to an inbox item that was never materialized.
  "weekend-start": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: true } },
  "new-consideration": { email: { suppressed: false }, push: { suppressed: false }, app: { suppressed: false } },
  // No email (the value is the before/after review in-app); push + in-app on so the deep link lands.
  "image-enhanced": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } },
  // The one onboarding touch — push brings them back to their plan; in-app covers push-denied users
  // on next open. Email support is a planned follow-up.
  "plan-ready": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } },
  // A safety notice goes out on every channel, immediately (Decision 2) — home status does not gate it.
  "equipment-recall": { email: { suppressed: false }, push: { suppressed: false }, app: { suppressed: false } },
  // One question, answered with a tap on the equipment screen: push + in-app land there; no email.
  "equipment-brand-needed": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } },
  // A household event worth a tap, not a letter: push + in-app only.
  "home-member-joined": { email: { suppressed: true }, push: { suppressed: false }, app: { suppressed: false } }
};
var PushReceiptStatusSchema = z80.enum(["pending", "ok", "error"]);
var PushReceiptSchema = z80.object({
  object: z80.literal("push-receipt"),
  schema: z80.literal(1),
  userId: UserIdSchema,
  userNotificationId: UserNotificationIdSchema,
  ticketId: z80.string(),
  deviceId: DeviceIdSchema,
  expoPushToken: z80.string(),
  status: PushReceiptStatusSchema,
  errorCode: z80.string().nullish(),
  errorDetails: z80.record(z80.string(), z80.any()).nullish(),
  createdAt: TimestampSchema,
  checkedAt: TimestampSchema.nullish()
});
var EmailReceiptSchema = z80.object({
  object: z80.literal("email-receipt"),
  schema: z80.literal(1),
  userId: UserIdSchema,
  userNotificationId: UserNotificationIdSchema,
  messageId: z80.string(),
  statusCode: z80.number().nullish(),
  createdAt: TimestampSchema
});

// src/notion_webhook_event.types.ts
import * as z81 from "zod";
var NotionWebhookEventSchema = z81.object({
  object: z81.literal("notion-webhook-event"),
  schema: z81.literal(1),
  id: NotionWebhookEventIdSchema,
  type: z81.string(),
  receivedAt: TimestampSchema,
  payload: z81.record(z81.string(), z81.unknown())
});

// src/plan_review.types.ts
import * as z82 from "zod";
var PlanReviewStatusSchema = z82.enum(["requested", "running", "complete", "error"]);
var PlanReviewErrorCodeSchema = z82.enum(["parse", "failed"]);
var PlanReviewSpaceStatusSchema = z82.enum(["checked", "not-set-up", "none"]);
var PlanReviewSpaceProgressSchema = z82.object({
  total: z82.number(),
  done: z82.number(),
  found: z82.number(),
  status: PlanReviewSpaceStatusSchema
});
var PLAN_REVIEW_HOME_SPACE_KEY = "home";
var PlanReviewProgressSchema = z82.object({
  consideredTotal: z82.number(),
  consideredDone: z82.number(),
  aiEvaluated: z82.number(),
  found: z82.number(),
  // Keyed by PlanReviewSpaceKey — a SpaceId, or `'home'` for types with no space.
  spaces: z82.record(z82.string(), PlanReviewSpaceProgressSchema).optional()
});
var PlanReviewSummarySchema = z82.object({
  consideredCount: z82.number(),
  evaluatedCount: z82.number(),
  appliedCount: z82.number(),
  durationMs: z82.number()
});
var PlanReviewSchema = z82.object({
  object: z82.literal("plan-review"),
  schema: z82.literal(1),
  id: PlanReviewIdSchema,
  homeId: HomeIdSchema,
  // The requester. Must equal the home owner — the server rejects a mismatch (forgery guard,
  // same posture as enhanceRoomImage).
  userId: UserIdSchema,
  status: PlanReviewStatusSchema,
  // 1 on the first submit of a home; bumped on each re-review.
  attempt: z82.number().int().min(1),
  requestedAt: TimestampSchema,
  // Stamped by the server when it claims the doc.
  startedAt: TimestampSchema.optional(),
  updatedAt: TimestampSchema,
  completedAt: TimestampSchema.optional(),
  // The Firebase event that claimed this review — dedupes a redelivered trigger.
  eventId: z82.string().optional(),
  error: z82.string().optional(),
  errorCode: PlanReviewErrorCodeSchema.optional(),
  // Heartbeat written by the foregrounded review screen so the server can skip the "your plan
  // is ready" push when the user is already watching.
  clientSeenAt: TimestampSchema.optional(),
  // Stamped by the trigger, on the SERVER clock, whenever the client's `clientSeenAt` heartbeat
  // changes — the notification rule compares server time to server time, so device clock skew
  // can't fake or hide a watching user.
  serverSeenAt: TimestampSchema.optional(),
  progress: PlanReviewProgressSchema.optional(),
  summary: PlanReviewSummarySchema.optional()
});

// src/recall.types.ts
import * as z83 from "zod";
var RecallSourceSchema = z83.enum(["cpsc", "health-canada", "manufacturer"]);
function recallKey(source, sourceId) {
  return `${source}:${sourceId}`;
}
var RecallKeySchema = z83.object({
  object: z83.literal("recall-key"),
  schema: z83.literal(1),
  id: z83.string(),
  source: RecallSourceSchema,
  sourceId: z83.string(),
  recallId: RecallIdSchema,
  createdAt: TimestampSchema
});
var ExtractedRecallModelRuleKindSchema = z83.enum(["exact", "prefix", "range"]);
var RecallModelRuleKindSchema = z83.enum([...ExtractedRecallModelRuleKindSchema.options, "any"]);
var RecallModelRuleSchema = z83.object({
  kind: RecallModelRuleKindSchema,
  value: z83.string(),
  note: z83.string().nullable()
});
var ExtractedRecallModelRuleSchema = RecallModelRuleSchema.extend({
  kind: ExtractedRecallModelRuleKindSchema,
  value: z83.string().describe("Exact model/catalog number as printed; a prefix; or 'START..END' for a stated range.")
});
var RecallSerialRuleKindSchema = z83.enum(["prefix", "range", "exact"]);
var RecallSerialRuleSchema = z83.object({
  appliesToModels: z83.array(z83.string()).describe("Model values this rule is tied to; empty = all listed models."),
  kind: RecallSerialRuleKindSchema,
  value: z83.string().describe("A prefix; 'START..END' for ONE range (X placeholders allowed); or one exact serial."),
  note: z83.string().nullable()
});
var RecallScopeSchema = z83.enum([
  "all-units-of-listed-models",
  "listed-models-with-conditions",
  "product-line-no-model-numbers",
  "unclear"
]);
var RecallCheckKindSchema = z83.enum(["serial", "batch", "date", "upc", "other"]);
var RecallCheckSchema = z83.object({
  kind: RecallCheckKindSchema.describe(
    "serial = a serial number condition; batch = a batch, lot or production number; date = a manufacture date or date code; upc = a UPC the recall is limited to; other = any other condition that narrows the recall."
  ),
  text: z83.string().describe(
    "The condition in the notice's own words, short and complete: 'Batch numbers 1535 through 1545 and 1200000 through 1202080'."
  ),
  appliesToModels: z83.array(z83.string()).describe("modelRules values this check is tied to, copied exactly; empty = all listed models.")
});
var RecallExtractionSchema = z83.object({
  brands: z83.array(z83.string()).describe("Brand names printed on the product, not the recalling firm unless it is the brand."),
  scope: RecallScopeSchema,
  modelRules: z83.array(ExtractedRecallModelRuleSchema),
  serialRules: z83.array(RecallSerialRuleSchema).describe("Only numbers the notice calls serial numbers; batch, lot and date codes are checks, not serials."),
  checks: z83.array(RecallCheckSchema),
  upcs: z83.array(z83.string()),
  whereToFindIdentifiers: z83.string(),
  confidence: z83.number()
});
var RecallRuleSourceSchema = z83.enum(["both", "sol", "sonnet", "admin"]);
var MergedRecallModelRuleSchema = RecallModelRuleSchema.extend({
  source: RecallRuleSourceSchema,
  verified: z83.boolean(),
  accepted: z83.boolean().optional()
});
var MergedRecallSerialRuleSchema = RecallSerialRuleSchema.extend({
  source: RecallRuleSourceSchema,
  verified: z83.boolean(),
  accepted: z83.boolean().optional()
});
var MergedRecallCheckSchema = RecallCheckSchema.extend({ source: RecallRuleSourceSchema });
var SourcedValueSchema = z83.object({ value: z83.string(), source: RecallRuleSourceSchema });
var MergedRecallRulesSchema = z83.object({
  brands: z83.array(SourcedValueSchema),
  scope: RecallScopeSchema,
  /** The extractors disagreed on scope; the broader one is kept. */
  scopeDiffers: z83.boolean().optional(),
  modelRules: z83.array(MergedRecallModelRuleSchema),
  serialRules: z83.array(MergedRecallSerialRuleSchema),
  checks: z83.array(MergedRecallCheckSchema),
  /** Set once an admin has edited the checks; a re-process then keeps them instead of the extractors'. */
  checksAdminEdited: z83.boolean().optional(),
  upcs: z83.array(SourcedValueSchema),
  whereToFindIdentifiers: z83.string(),
  confidence: z83.number(),
  suppressed: z83.array(z83.string())
});
var RecallVerificationSchema = z83.object({
  /** Extracted identifiers (folded) that appear nowhere in the notice text. */
  unverified: z83.array(z83.string()),
  /** Identifier-like tokens in the notice that no rule covers — the "did the model miss one?" list. */
  harvestedNotExtracted: z83.array(z83.string())
});
var RecallClassifierResultSchema = z83.object({
  productKind: z83.string(),
  headline: z83.string().optional(),
  typeIds: z83.array(EquipmentTypeIdSchema),
  topicIds: z83.array(RecallTopicIdSchema).default([])
});
var RecallClassificationSchema = z83.object({
  /** Admin-editable slug naming the product in the model's own taxonomy (`pressure-washer`). */
  productKind: z83.string(),
  /**
   * The recall in a few words, product first, then the danger ("Sauna360 infrared saunas can catch fire"): what
   * a homeowner reads in a list to tell whether it might be theirs. Absent on recalls classified before it.
   */
  headline: z83.string().optional(),
  /** Admin-editable; defaults to the union of both classifiers. Never a group string. */
  typeIds: z83.array(EquipmentTypeIdSchema),
  /**
   * The FYI topics the recall reaches homes through, once approved. Admin-editable; defaults to the union of
   * both classifiers, which an admin approves in bulk.
   */
  topicIds: z83.array(RecallTopicIdSchema).default([]),
  /** Each classifier's own answer, verbatim, keyed by llm id. */
  byClassifier: z83.record(z83.string(), RecallClassifierResultSchema),
  classifyVersion: z83.string(),
  /** Hash of the sorted published catalog type ids the classification ran against. */
  catalogVersion: z83.string(),
  at: TimestampSchema,
  /** Set once an admin has edited `typeIds` or `productKind`; a reclassify then leaves them alone. */
  adminEdited: z83.boolean().optional(),
  /** Set once an admin has edited `topicIds`; a reclassify then leaves them alone. */
  topicsAdminEdited: z83.boolean().optional(),
  /** Set once an admin has written `headline`; a reclassify then leaves it alone. */
  headlineAdminEdited: z83.boolean().optional(),
  /**
   * Types an admin removed: no reclassify types the recall with one again, even after a return to the
   * untyped backlog clears `adminEdited`.
   */
  ruledOutTypeIds: z83.array(EquipmentTypeIdSchema).optional(),
  /** Topics an admin took off the recall: no reclassify files it under one again. */
  ruledOutTopicIds: z83.array(RecallTopicIdSchema).optional(),
  /**
   * What the classifiers now propose that the recall does not carry: a live or admin-edited recall keeps its
   * lists through a reclassify, so a type or topic the catalog gained since waits here for an admin to take
   * up or rule out. `hasSuggestions` is the query flag for either list.
   */
  suggestedTypeIds: z83.array(EquipmentTypeIdSchema).optional(),
  suggestedTopicIds: z83.array(RecallTopicIdSchema).optional(),
  hasSuggestions: z83.boolean().optional()
});
var RecallStatusSchema = z83.enum(["new", "classified", "draft", "approved", "rejected"]);
var RECALL_STATUSES = RecallStatusSchema.options;
var RecallStageSchema = z83.enum(["classify", "process"]);
var RecallAiCallSchema = z83.object({
  stage: RecallStageSchema,
  llm: z83.string(),
  version: z83.string(),
  at: TimestampSchema,
  ms: z83.number(),
  inputTokens: z83.number(),
  outputTokens: z83.number(),
  costUsd: z83.number(),
  error: z83.string().optional()
});
var RecallDryRunSchema = z83.object({
  modelIds: z83.array(ModelIdSchema),
  /** Units counted, those with no catalog model (reached by brand and type) included. */
  unitCount: z83.number(),
  /** Of `unitCount`, the units with no catalog model. */
  unmodeledUnitCount: z83.number().optional(),
  homeCount: z83.number(),
  at: TimestampSchema,
  rulesVersion: z83.string()
});
var RecallPageUnavailableSchema = z83.object({ at: TimestampSchema, reason: z83.string() });
var RecallImageSchema = z83.object({
  /** Our Storage copy once rehosted (`rehostRecallImages`); the source's URL until then. */
  url: z83.string(),
  caption: z83.string().optional(),
  /** The source's URL, kept once `url` is our copy. */
  sourceUrl: z83.string().optional()
});
var RecallRejectReasonSchema = z83.enum(["not-ours", "duplicate", "wrong-rules", "other"]);
var RecallSchema = z83.object({
  object: z83.literal("recall"),
  schema: z83.literal(1),
  /** Opaque (`createId()`), never derived from the source. */
  id: RecallIdSchema,
  source: RecallSourceSchema,
  /** The id at the source, unprefixed (`22159`), scoped by `source`. */
  sourceId: z83.string(),
  /** The notice page at the source (tables intact) — linked from admin and the app. */
  sourceUrl: z83.string(),
  publishedAt: LocalDateSchema,
  lastPublishedAt: LocalDateSchema,
  title: z83.string(),
  hazard: z83.string().optional(),
  remedy: z83.string().optional(),
  consumerContact: z83.string().optional(),
  /** Where and when the recalled units were sold, at what price, as the source states it. */
  soldAt: z83.string().optional(),
  images: z83.array(RecallImageSchema).optional(),
  /**
   * The notice as the LLMs read it — ONE shared `formatNotice()` (title, date, products, manufacturers,
   * description, the source page's tables) used by both stages, the verbatim guard, admin highlighting and
   * every re-process. The classifiers read `hazard` beside it.
   */
  noticeText: z83.string(),
  /** sha256 of `noticeText`; a changed hash is the only trigger for re-processing a known recall. */
  noticeHash: z83.string(),
  /**
   * Set while the notice is built without the source's web page because the page could not be read, so
   * tables the source's data flattens or drops may be missing. A re-process reads the page again, and a
   * notice built with the page clears it.
   */
  pageUnavailable: RecallPageUnavailableSchema.optional(),
  /** The recalling firms and importers as the notice names them — resolved to brands beside `rules.brands`. */
  manufacturers: z83.array(z83.string()),
  /** The source record minus what is stored elsewhere (`Images`, `Description`). */
  raw: z83.record(z83.string(), z83.unknown()),
  status: RecallStatusSchema,
  /** Denormalized `classification.typeIds.length > 0`, so the untyped view is one equality filter. */
  typed: z83.boolean(),
  classification: RecallClassificationSchema.optional(),
  /** Each extractor's answer, verbatim, keyed by llm id — the AI Results tab. */
  extractions: z83.record(z83.string(), RecallExtractionSchema).optional(),
  /** The ACTIVE rules: what matches once approved. */
  rules: MergedRecallRulesSchema.optional(),
  /** A re-process of an approved recall lands here until re-approved; matching continues on `rules`. */
  draftRules: MergedRecallRulesSchema.optional(),
  /** The brands the re-process resolved, promoted with `draftRules`; `brandIds` stays the live gate. */
  draftBrandIds: z83.array(BrandIdSchema).optional(),
  verification: RecallVerificationSchema.optional(),
  /** Resolved through the brand catalog from `rules.brands` and the recalling firms. */
  brandIds: z83.array(BrandIdSchema),
  /**
   * The `brand-requests` entries processing queued for names the catalog lacked. When one resolves to a
   * brand, the `handleBrandRequest` trigger unions that brand into the gate (`setBrandIds`).
   */
  brandRequestIds: z83.array(BrandRequestIdSchema).optional(),
  /** `brandIds` while approved, else empty — so one array-contains query finds the live recalls of a brand. */
  activeBrandIds: z83.array(BrandIdSchema),
  /** `classification.topicIds` while approved, else empty — so one query finds the live recalls of a topic. */
  activeTopicIds: z83.array(RecallTopicIdSchema).default([]),
  dryRun: RecallDryRunSchema.optional(),
  /**
   * Admin-written guidance shown to the homeowner beside the notice: what the source leaves out, such as a
   * manufacturer's own exclusions. Plain text; links and phone numbers are made tappable where shown.
   */
  additionalGuidance: z83.string().optional(),
  /** Admin-set link from a reannouncement to its predecessor (dedupes notifications). */
  supersedesRecallId: RecallIdSchema.optional(),
  aiCalls: z83.array(RecallAiCallSchema),
  classifyVersion: z83.string().optional(),
  rulesVersion: z83.string().optional(),
  classifiedAt: TimestampSchema.optional(),
  processedAt: TimestampSchema.optional(),
  reviewedBy: UserIdSchema.optional(),
  reviewedAt: TimestampSchema.optional(),
  approvedAt: TimestampSchema.optional(),
  rejectReason: RecallRejectReasonSchema.optional(),
  rejectNote: z83.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var RecallMatchStrengthSchema = z83.enum(["strong", "weak"]);
var RecallPrefixMatchSchema = z83.object({
  listed: z83.string(),
  model: z83.string()
});
var RecallMatchSchema = z83.object({
  object: z83.literal("recall-match"),
  schema: z83.literal(1),
  id: ModelIdSchema,
  recallId: RecallIdSchema,
  modelId: ModelIdSchema,
  brandId: BrandIdSchema,
  rule: RecallModelRuleSchema,
  strength: RecallMatchStrengthSchema,
  prefixMatch: RecallPrefixMatchSchema.optional(),
  /** The model's catalog type is not in the recall's type list (strong matches only; an admin chip). */
  typeMismatch: z83.boolean().optional(),
  /** The model's type is reached through a family with a listed one, at either strength (an admin chip). */
  typeFamily: z83.boolean().optional(),
  at: TimestampSchema,
  rulesVersion: z83.string()
});
var RecallVerdictValueSchema = z83.enum(["affected", "possibly-affected"]);
var RecallNeedSchema = z83.enum(["serial", "type"]);
var RecallEvidenceSchema = z83.object({
  rule: RecallModelRuleSchema,
  strength: RecallMatchStrengthSchema,
  prefixMatch: RecallPrefixMatchSchema.optional(),
  /** The unit's catalog type is not in the recall's type list; the owner is asked the type. */
  typeMismatch: z83.boolean().optional(),
  /** With `typeMismatch`: the unit's type shares a family with a listed one. */
  typeFamily: z83.boolean().optional(),
  serialRule: RecallSerialRuleSchema.optional()
});
var RecallDisplaySchema = z83.object({
  title: z83.string(),
  /** The classifier's short headline, shown in lists in place of the notice's long title when present. */
  headline: z83.string().optional(),
  hazard: z83.string().optional(),
  remedy: z83.string().optional(),
  whereToFind: z83.string().optional(),
  sourceUrl: z83.string(),
  contact: z83.string().optional(),
  /** The checks that bind this unit, in the notice's words, after a line of our own for a `prefixMatch`. */
  checks: z83.array(z83.string()).default([]),
  /** The notice's own description of the recalled product, verbatim. */
  description: z83.string().optional(),
  soldAt: z83.string().optional(),
  additionalGuidance: z83.string().optional(),
  /** The notice's photos, in its order: the product, its packaging, where its labels are. */
  images: z83.array(RecallImageSchema).default([])
});
var RecallResolutionOutcomeSchema = z83.enum(["handled", "not-my-unit", "dismissed"]);
var RecallWithdrawnReasonSchema = z83.enum(["rejected", "rules-narrowed", "unit-changed", "home-changed"]);
var EquipmentRecallVerdictSchema = z83.object({
  object: z83.literal("recall-verdict"),
  schema: z83.literal(1),
  scope: z83.literal("equipment"),
  id: RecallVerdictIdSchema,
  recallId: RecallIdSchema,
  /** The catalog model the unit matched through; absent for a unit with no model, reached by its brand and type. */
  modelId: ModelIdSchema.optional(),
  homeId: HomeIdSchema,
  equipmentId: EquipmentIdSchema,
  verdict: RecallVerdictValueSchema,
  needs: z83.array(RecallNeedSchema),
  evidence: RecallEvidenceSchema,
  display: RecallDisplaySchema,
  resolution: z83.object({
    outcome: RecallResolutionOutcomeSchema,
    at: TimestampSchema,
    /** Who answered. */
    by: UserIdSchema.optional(),
    note: z83.string().optional()
  }).optional(),
  /** The verdict no longer stands; the equipment screen shows a muted "no longer applies" state. */
  withdrawn: z83.object({
    at: TimestampSchema,
    reason: RecallWithdrawnReasonSchema
  }).optional(),
  /** The homeowner answered the type question; the evaluation then treats the unit as the recall's kind. */
  typeConfirmedAt: TimestampSchema.optional(),
  /**
   * The homeowner said the recall applies to their unit ("Yes, this applies to me"): they follow the
   * notice's instructions next, then mark it done (`resolution: handled`).
   */
  acceptedAt: TimestampSchema.optional(),
  acceptedBy: UserIdSchema.optional(),
  /** The home was told — claimed atomically by the one pass that sends, so overlapping passes never send twice. */
  notifiedAt: TimestampSchema.optional(),
  notifiedVerdict: RecallVerdictValueSchema.optional(),
  /**
   * Written on purpose without a notification (the dev rebuild after a mirror, a reset window, a
   * reannouncement whose predecessor already told this home) — no later pass sends it either.
   */
  silencedAt: TimestampSchema.optional(),
  /** The unit belongs to a seeded E2E home. Always written (true or false) so a count can filter on it. */
  test: z83.boolean().default(false),
  rulesVersion: z83.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var HomeRecallVerdictSchema = EquipmentRecallVerdictSchema.omit({
  scope: true,
  modelId: true,
  equipmentId: true,
  verdict: true,
  needs: true,
  evidence: true,
  typeConfirmedAt: true,
  notifiedVerdict: true
}).extend({
  scope: z83.literal("home"),
  verdict: z83.literal("fyi"),
  /** The recall's topics, so the app can group its FYIs and a home's evaluation can see one it no longer follows. */
  topicIds: z83.array(RecallTopicIdSchema).default([])
});
var RecallVerdictSchema = z83.discriminatedUnion("scope", [
  EquipmentRecallVerdictSchema,
  HomeRecallVerdictSchema
]);
function isEquipmentRecallVerdict(verdict) {
  return verdict.scope === "equipment";
}
function isHomeRecallVerdict(verdict) {
  return verdict.scope === "home";
}
function recallVerdictId(key) {
  return RecallVerdictIdSchema.parse(
    key.equipmentId ? `${key.recallId}:equipment:${key.equipmentId}` : `${key.recallId}:home`
  );
}
function isVisibleRecallVerdict(verdict) {
  return !verdict.withdrawn || !!verdict.notifiedAt;
}
function isQuietFyi(verdict) {
  return verdict.verdict === "fyi" && !verdict.acceptedAt;
}
function isOpenRecallVerdict(verdict) {
  return !verdict.withdrawn && !verdict.resolution;
}
var RecallSyncOutcomeSchema = z83.enum(["ok", "skipped", "error"]);
var RecallTopicStatsSchema = z83.object({
  recentCount: z83.number(),
  lookbackCount: z83.number(),
  newest: z83.array(
    z83.object({
      recallId: RecallIdSchema,
      title: z83.string(),
      headline: z83.string().optional(),
      hazard: z83.string().optional(),
      publishedAt: LocalDateSchema
    })
  )
});
var RECALLS_STATE_ID = "recalls-state";
var RecallsStateSchema = z83.object({
  object: z83.literal("recalls-state"),
  schema: z83.literal(1),
  id: z83.literal(RECALLS_STATE_ID),
  /** Bumped on every approval or withdrawal; `Model.recallsCheckedEpoch` says which epoch a model saw. */
  recallsEpoch: z83.number(),
  lastSyncAt: TimestampSchema.optional(),
  lastSyncCount: z83.number().optional(),
  lastSyncOutcome: RecallSyncOutcomeSchema.optional(),
  /**
   * Untyped recalls per `productKind`, every status but rejected — the catalog-gap list, recomputed by the
   * sync and the jobs.
   */
  untypedKindCounts: z83.record(z83.string(), z83.number()).optional(),
  /** When the catalog stats below were last recomputed, `untypedKindCounts` included. */
  untypedKindCountsAt: TimestampSchema.optional(),
  /** Every recall synced from the source, whatever its status: the count the app's Recall Center shows. */
  totalRecallCount: z83.number().optional(),
  /** Each topic's live recalls, recomputed with `untypedKindCounts`: the app's topic rates and previews. */
  topicStats: z83.record(RecallTopicIdSchema, RecallTopicStatsSchema).optional(),
  updatedAt: TimestampSchema
});
var RecallJobRequestSchema = z83.discriminatedUnion("kind", [
  z83.object({ kind: z83.literal("process"), recallIds: z83.array(RecallIdSchema) }),
  z83.object({ kind: z83.literal("process-kind"), productKind: z83.string(), limit: z83.number() }),
  /** Every stale recall, or the named recalls when `recallIds` is given (the details page's Reclassify). */
  z83.object({ kind: z83.literal("reclassify"), recallIds: z83.array(RecallIdSchema).optional() }),
  /**
   * Re-apply every approved recall's rules (matches and verdicts rebuilt against the catalog as it stands).
   * `notify: false` writes verdicts silently — the reset-dev mirror's rebuild, which must never message a
   * dev home.
   */
  z83.object({ kind: z83.literal("sweep"), notify: z83.boolean() }),
  /** Recompute the Impact Preview (`Recall.dryRun`) of the named recalls from their current rules; no AI. */
  z83.object({ kind: z83.literal("impact-preview"), recallIds: z83.array(RecallIdSchema) })
]);
var RecallJobStatusSchema = z83.enum(["requested", "running", "complete", "error"]);
var RecallJobProgressSchema = z83.object({
  total: z83.number(),
  done: z83.number(),
  failed: z83.number(),
  costUsd: z83.number()
});
var RecallJobSchema = z83.object({
  object: z83.literal("recall-job"),
  schema: z83.literal(1),
  id: RecallJobIdSchema,
  request: RecallJobRequestSchema,
  requestedBy: UserIdSchema,
  status: RecallJobStatusSchema,
  progress: RecallJobProgressSchema.optional(),
  /** Recall ids already handled; a long job hands itself back to `requested` and continues past these. */
  cursor: z83.array(RecallIdSchema).optional(),
  eventId: z83.string().optional(),
  error: z83.string().optional(),
  startedAt: TimestampSchema.optional(),
  completedAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema
});
var RECALL_PROCESS_KIND_LIMIT = 50;

// src/recently_viewed.types.ts
import * as z84 from "zod";
var RecentlyViewedSchema = z84.object({
  object: z84.literal("recently-viewed"),
  schema: z84.literal(1),
  id: z84.string().brand(),
  spaceId: SpaceIdSchema,
  equipmentId: EquipmentIdSchema,
  viewedAt: TimestampSchema
});

// src/rock_docs.types.ts
function rockDocRefKey(ref) {
  return `${ref.kind}:${ref.id}`;
}
function collectRockDocRefs(blocks) {
  const byKey = /* @__PURE__ */ new Map();
  for (const text of blockTexts(blocks)) {
    for (const span of text) {
      if (span.ref) byKey.set(rockDocRefKey(span.ref), span.ref);
    }
  }
  return [...byKey.values()];
}
function blockTexts(blocks) {
  return blocks.flatMap((block) => {
    switch (block.type) {
      case "divider":
        return [];
      case "image":
        return [block.caption ?? []];
      case "code":
        return [block.text, block.caption ?? []];
      case "toggle":
        return [block.summary, ...blockTexts(block.children)];
      default:
        return [block.text, ...blockTexts(block.children)];
    }
  });
}

// src/room_enhancement.types.ts
import * as z85 from "zod";
var RoomEnhancementIdSchema = z85.string().brand();
var RoomEnhancementStatusSchema = z85.enum(["pending", "ready", "failed"]);
var RoomEnhancementSchema = z85.object({
  object: z85.literal("room-enhancement"),
  schema: z85.literal(1),
  id: RoomEnhancementIdSchema,
  homeId: HomeIdSchema,
  roomId: RoomIdSchema,
  // The requester — drives the notification target.
  userId: UserIdSchema,
  status: RoomEnhancementStatusSchema,
  // The room photo snapshotted at request time, so before/after stays stable if room.image changes.
  sourceImage: z85.string(),
  // Source pixel dimensions, used to pin the output aspect ratio (the model rotates without it).
  width: z85.number().optional(),
  height: z85.number().optional(),
  // Optional secondary user instruction, appended after the default prompt (guardrails stay leading).
  guidance: z85.string().optional(),
  // The enhanced result download URL, set on `ready`.
  candidateImage: z85.string().optional(),
  error: z85.string().optional(),
  requestedAt: TimestampSchema,
  completedAt: TimestampSchema.optional()
});

// src/task_generation_job.types.ts
import * as z86 from "zod";
var TaskGenerationJobRunStatusSchema = z86.enum(["pending", "running", "success", "error"]);
var TaskGenerationJobRunSchema = z86.object({
  object: z86.literal("task-generation-job-run"),
  schema: z86.literal(1),
  id: TaskGenerationJobRunIdSchema,
  requestedAt: TimestampSchema,
  status: TaskGenerationJobRunStatusSchema.optional(),
  finishedAt: TimestampSchema.optional(),
  errorMessage: z86.string().optional(),
  subscriptionCount: z86.number().optional(),
  taskCount: z86.number().optional()
});

// src/user_limits.types.ts
import * as z87 from "zod";
var UserLimitKeySchema = z87.enum([
  "enhance",
  "chat",
  "equipment-refresh",
  "invite-lookups",
  "home-invites",
  "brand-research",
  "other-paid-calls",
  "place-lookups"
]);
var DailyCounterSchema = z87.object({
  day: LocalDateSchema,
  count: z87.number()
});
var UserLimitsSchema = z87.object({
  object: z87.literal("user-limits"),
  schema: z87.literal(1),
  id: UserIdSchema,
  // Daily AI photo-enhancement counter (enforced by the enhanceRoomImage trigger).
  enhance: DailyCounterSchema.optional(),
  // Daily AI chat-message counter (enforced server-side where a chat turn runs).
  chat: DailyCounterSchema.optional(),
  // Daily counter for EXPLICIT equipment refreshes — the user pressing "Refresh Model Specs", the one
  // paid action a person can repeat at will on a single unit. Adding or editing equipment also
  // refreshes; that counts against `other-paid-calls` alone.
  "equipment-refresh": DailyCounterSchema.optional(),
  // Daily counter for invite-code lookups and joins, so the code space cannot be walked.
  "invite-lookups": DailyCounterSchema.optional(),
  // Daily counter for invite emails sent from Rock's domain; an invite shared by its code is not counted.
  "home-invites": DailyCounterSchema.optional(),
  // Daily counter for an admin's AI research runs on brand requests (two web-searching model calls each).
  "brand-research": DailyCounterSchema.optional(),
  // Daily counter for every paid call a person's own data sets off that has no key of its own above
  // (model research, AI answers, image and property searches, company resolution), charged to whoever
  // wrote the request. Cached answers are not counted, except a model refresh, which counts every run:
  // the model a new number mints is what arms the paid manual hunt. The backstop against a scripted
  // account, not a budget: a feature that needs its own number gets its own key and leaves this one.
  "other-paid-calls": DailyCounterSchema.optional(),
  // Daily counter for Google Places calls (address and business autocomplete, contact geocoding, map and
  // photo images). Resolving a picked business is a decision, so it counts as `other-paid-calls`.
  // Separate from `other-paid-calls` because autocomplete charges per keystroke burst, not per decision.
  "place-lookups": DailyCounterSchema.optional(),
  updatedAt: TimestampSchema
});
var UserLimitsConfigSchema = z87.object({
  object: z87.literal("user-limits-config"),
  schema: z87.literal(1),
  id: z87.literal("user-limits-config"),
  enhance: z87.number().optional(),
  chat: z87.number().optional(),
  "equipment-refresh": z87.number().optional(),
  "invite-lookups": z87.number().optional(),
  "home-invites": z87.number().optional(),
  "brand-research": z87.number().optional(),
  "other-paid-calls": z87.number().optional(),
  "place-lookups": z87.number().optional(),
  updatedAt: TimestampSchema
});
var USER_LIMIT_DEFAULTS = {
  enhance: 20,
  chat: 100,
  // Each explicit refresh buys a Perplexity pass (and, for a product with no model number, an OpenAI
  // web search). Far above normal use — this is the backstop against a stuck retry, not a budget.
  "equipment-refresh": 20,
  // Far above a household's real use (one or two codes, ever); the backstop against guessing codes.
  "invite-lookups": 20,
  // An invite email goes to an address of the member's choosing from Rock's domain; this keeps that from
  // becoming a bulk channel. Past it, the invite is still made but not mailed.
  "home-invites": 10,
  // About 5¢ a run, started per request or in bulk from the queue; room for a day's backlog, not a budget.
  "brand-research": 100,
  // Sized to stop abuse, never a power user's first day: a whole-home walkthrough can take a photo
  // identify, a type lookup, an image search and a refresh or two per unit, and a script still reaches
  // this in minutes. Over it, the request ends in an error and nothing paid runs.
  "other-paid-calls": 250,
  // Autocomplete fires once per debounced keystroke burst, so one address entry is a handful of calls.
  "place-lookups": 500
};
function resolveUserLimit(config, key) {
  return config?.[key] ?? USER_LIMIT_DEFAULTS[key];
}
var chatDailyLimitReason = (limit) => `You've reached today's limit of ${limit} messages. Try again tomorrow.`;
var enhanceDailyLimitReason = (limit) => `You've reached today's limit of ${limit} enhancements. Try again tomorrow.`;
var equipmentRefreshDailyLimitReason = (limit) => `You've reached today's limit of ${limit} refreshes. Try again tomorrow.`;
var inviteLookupDailyLimitReason = (limit) => `You've tried ${limit} invite codes today. Try again tomorrow.`;
var otherPaidCallsDailyLimitReason = (limit) => `You've reached today's limit of ${limit} requests. Try again tomorrow.`;
var placeLookupDailyLimitReason = (limit) => `You've reached today's limit of ${limit} address and business searches. Try again tomorrow.`;

// src/weekend_fill.types.ts
import * as z88 from "zod";
var WeekendFillSchema = z88.object({
  object: z88.literal("weekend-fill"),
  schema: z88.literal(1),
  id: HomeIdSchema,
  homeId: HomeIdSchema,
  lastFilledWeekend: LocalDateSchema.optional(),
  nextFillAt: TimestampSchema,
  // The weekend email, the day before the home's weekend starts.
  nextChecklistSendAt: TimestampSchema.optional(),
  // The weekend-start push, on the first day of the home's weekend.
  nextStartSendAt: TimestampSchema.optional(),
  updatedAt: TimestampSchema
});
var WEEKEND_MOMENTS = ["nextFillAt", "nextChecklistSendAt", "nextStartSendAt"];
export {
  APPLIES_TO_OWNER_COPY,
  APP_STATUS_DOC_SCHEMAS,
  APP_VISIBLE_TAGS,
  AcceptHomeInviteResponseSchema,
  AddModelRequestSchema,
  AddModelResponseSchema,
  AddressLookupDataSchema,
  AnalyzeDocumentLanguagesRequestSchema,
  AnalyzeDocumentLanguagesResponseSchema,
  AnyGuideItemSchema,
  AppChannelConfigSchema,
  AppEdgeDataSchema,
  AppEquipmentConfigSchema,
  AppFlowObjectSchema,
  AppFlowParamsSchema,
  AppNodeDataSchema,
  AppStatusAlertSchema,
  AppStatusDocKindSchema,
  AppStatusEasUpdateSchema,
  AppStatusMaintenanceSchema,
  AppStatusRequiredUpgradeSchema,
  AppStatusSeveritySchema,
  AppStatusStoreUrlsSchema,
  AppTaskScreenConfigSchema,
  AppTaskScreenSectionContactSchema,
  AppTaskScreenSectionHistorySchema,
  AppTaskScreenSectionInstructionsSchema,
  AppTaskScreenSectionNotesSchema,
  AppTaskScreenSectionPicturesSchema,
  AppTaskScreenSectionSchema,
  AppTaskScreenSectionSuppliesSchema,
  AppliesToDraftResultIdSchema,
  AppliesToDraftResultSchema,
  AppliesToDraftSchema,
  AppliesToFieldsSchema,
  ApplyDocumentTrimRequestSchema,
  ApplyDocumentTrimResponseSchema,
  ArchiveStatusSchema,
  AuthEventSchema,
  AuthProfileSchema,
  BRANDFETCH_SOURCE_ID,
  BRAND_MERGE_STAGES,
  BRAND_MERGE_TOUCHED_CAP,
  BRAND_REQUEST_SIGHTING_CAP,
  BRAND_RESEARCH_ABANDONED_AFTER_MS,
  BlockIdSchema,
  BoundingBoxSchema,
  BrandAliasKeySchema,
  BrandAliasSchema,
  BrandIdSchema,
  BrandLabelKindSchema,
  BrandLineSchema,
  BrandLogoImageSchema,
  BrandMergeIdSchema,
  BrandMergePreviewSchema,
  BrandMergeSchema,
  BrandMergeStageResultSchema,
  BrandMergeStageSchema,
  BrandMergeTouchedSchema,
  BrandNameSchema,
  BrandRefSchema,
  BrandRequestIdSchema,
  BrandRequestParkSchema,
  BrandRequestResolutionKindSchema,
  BrandRequestResolutionSchema,
  BrandRequestSchema,
  BrandRequestSightingSchema,
  BrandRequestSourceSchema,
  BrandRequestStatusSchema,
  BrandRequestSuggestionSchema,
  BrandResearchAnswerSchema,
  BrandResearchCallSchema,
  BrandResearchDecisionSchema,
  BrandResearchIdSchema,
  BrandResearchProposalSchema,
  BrandResearchSchema,
  BrandResearchStatusSchema,
  BrandReviewStatusSchema,
  BrandSchema,
  BrandSearchEntrySchema,
  BrandSearchIndexSchema,
  BrandSourceSchema,
  BrandfetchCandidateSchema,
  BrandfetchInfoSchema,
  BrandfetchLogoFormatSchema,
  BrandfetchLogoSchema,
  BrandfetchPayloadSchema,
  CHAT_CONVERSATION_READONLY_AFTER_DAYS,
  CHAT_CONVERSATION_READONLY_REASON,
  CHAT_MODELS,
  CONSIDERATIONS_TITLE,
  CONSULT_MANUAL_TOOL_NAME,
  CUSTOM_GROUP,
  CUSTOM_ID_PREFIX,
  CalendarPreferenceSchema,
  CandidateDispositionSchema,
  CandidateGateCheckSchema,
  CandidateGateSchema,
  CandidateTrailSchema,
  CandidateVerdictSchema,
  ChangeTypeSchema,
  ChannelOutcomeSchema,
  ChatConversationIdSchema,
  ChatConversationSchema,
  ChatIntentSchema,
  ChatMessageIdSchema,
  ChatMessageRoleSchema,
  ChatMessageSchema,
  ChatModelSchema,
  ChatToolCallSchema,
  ChatToolResultSchema,
  ChatTurnClientAppSchema,
  ChatTurnRequestSchema,
  ChatTurnResponseSchema,
  ChatWarmupRequestSchema,
  CitationDetailsSchema,
  CodeWordSchema,
  CompanyAddressSchema,
  CompanyBrandSchema,
  CompanyBusinessStatusSchema,
  CompanyGeoSchema,
  CompanyIdSchema,
  CompanySchema,
  ConsiderationCriticalityLabels,
  ConsiderationCriticalityOptions,
  ConsiderationCriticalitySchema,
  ConsiderationDeclineReasonSchema,
  ConsiderationIdSchema,
  ConsiderationSchema,
  ConsiderationSourceTypeSchema,
  ConsiderationStatusLabels,
  ConsiderationStatusOptions,
  ConsiderationStatusSchema,
  ConsiderationTypeIdSchema,
  ConsiderationTypeSchema,
  ConsultManualAnswerSchema,
  ContactIdSchema,
  ContactSchema,
  CoreFieldSchema,
  CoveragePredicateSchema,
  CoverageRangeSchema,
  CreateEquipmentDataSchema,
  CustomEquipmentTypeSchema,
  CustomTaskTypeSchema,
  DEFAULT_CALENDAR_PREFERENCE,
  DEFAULT_EQUIPMENT_SPACE_ID,
  DEFAULT_GUIDE_INTRO,
  DEFAULT_GUIDE_WRAP_UP,
  DEFAULT_LOCALE_PREFERENCE,
  DEFAULT_LOCALE_PREFERENCES,
  DEFAULT_LOCALE_TAG,
  DEFAULT_RETAILER,
  DEFAULT_UI_MODE,
  DIGESTIBLE_TAGS,
  DOC_TAG_LABELS,
  DailyCounterSchema,
  DashboardSpaceIdSchema,
  DashboardSpaceLabels,
  DataKeyDataSchema,
  DataKeySchema,
  DeviceIdSchema,
  DevicePlatformSchema,
  DeviceSchema,
  DimensionsSubTypeSchema,
  DiscoveryAiOverviewSchema,
  DiscoveryDocumentStepSchema,
  DiscoveryLinkStepSchema,
  DiscoveryPageStepSchema,
  DiscoveryResultStepSchema,
  DiscoverySearchStepSchema,
  DiscoveryStepSchema,
  DiscoveryTrailSchema,
  DisplayableEquipmentTypeSchema,
  DocPublisherSchema,
  DocumentCoverageEntrySchema,
  DocumentCoverageKeySchema,
  DocumentCoverageProjectionSchema,
  DocumentCoverageSchema,
  DocumentDatesSchema,
  DocumentLanguageAnalysisSchema,
  DocumentSearchIdSchema,
  DocumentSearchOfferOutcomeSchema,
  DocumentSearchOriginSchema,
  DocumentSearchSchema,
  DocumentSearchStatusSchema,
  DocumentStatusSchema,
  DocumentTrimDecisionSchema,
  DocumentTrimProposalSchema,
  DocumentTrimSchema,
  DomainEventIdSchema,
  DomainEventSchema,
  EQUIPMENT_CORE_FIELD_KEYS,
  EmailChannelConfigSchema,
  EmailReceiptSchema,
  EnrichBrandRequestSchema,
  EnrichBrandResponseSchema,
  EntryDataSchema,
  EquipmentAttributeDataSchema,
  EquipmentBrandNeededDataSchema,
  EquipmentBrandQuestionAskedForSchema,
  EquipmentBrandQuestionSchema,
  EquipmentFieldsSchema,
  EquipmentGuideContextSchema,
  EquipmentIdSchema,
  EquipmentImageCategorySchema,
  EquipmentImageOptionSchema,
  EquipmentImageSchema,
  EquipmentImageSearchCompletedSchema,
  EquipmentImageSearchErrorSchema,
  EquipmentImageSearchIdSchema,
  EquipmentImageSearchProcessingSchema,
  EquipmentImageSearchRequestSchema,
  EquipmentImageSearchRequestedSchema,
  EquipmentListDataSchema,
  EquipmentModelDetailsSchema,
  EquipmentNodeDataSchema,
  EquipmentRecallDataSchema,
  EquipmentRecallVerdictSchema,
  EquipmentSchema,
  EquipmentSpecSchema,
  EquipmentTestLibraryEntryIdSchema,
  EquipmentTestLibraryEntrySchema,
  EquipmentTestLibrarySourceSchema,
  EquipmentTypeCandidateIdSchema,
  EquipmentTypeCandidateSchema,
  EquipmentTypeChoiceSchema,
  EquipmentTypeDraftSchema,
  EquipmentTypeFamilyIdSchema,
  EquipmentTypeFamilySchema,
  EquipmentTypeIdOrContextSchema,
  EquipmentTypeIdSchema,
  EquipmentTypeOptionSchema,
  EquipmentTypeSchema,
  EquipmentTypeSchemaV2,
  EquipmentTypeScopeSchema,
  ExitDataSchema,
  ExperienceLevelSchema,
  ExtractedRecallModelRuleKindSchema,
  ExtractedRecallModelRuleSchema,
  FactSourceSchema,
  FeedbackBodyAccountDeletionSchema,
  FeedbackBodyFeedbackSchema,
  FeedbackBodyIdeaSchema,
  FeedbackBodyIssueSchema,
  FeedbackBodyOtherSchema,
  FeedbackBodyQuestionSchema,
  FeedbackBodyRequestDataSchema,
  FeedbackBodySchema,
  FeedbackEquipmentSelectionSchema,
  FeedbackSubmissionIdSchema,
  FeedbackSubmissionSchema,
  FeedbackTypeSchema,
  FieldDataTypeSchema,
  FieldMixinSchema,
  FolderIdSchema,
  FolderSchema,
  GuideConsiderationComponentSchema,
  GuideConsiderationItemSchema,
  GuideContextSchema,
  GuideEquipmentComponentSchema,
  GuideEquipmentItemSchema,
  GuideHelpCacheKeySchema,
  GuideHelpCacheSchema,
  GuideHelpIdSchema,
  GuideHelpOptionSchema,
  GuideHelpRequestSchema,
  GuideInfoComponentSchema,
  GuideInfoItemSchema,
  GuideIntroSchema,
  GuideNodeIdSchema,
  GuideSchema,
  GuideStarterIdSchema,
  GuideStarterSchema,
  GuideStarterStatusSchema,
  GuideValueSchema,
  GuideValuesSchema,
  GuideVisitIdSchema,
  GuideVisitSchema,
  GuideWrapUpSchema,
  HOME_INVITE_TTL_DAYS,
  HOME_MEMBER_CAP,
  HOME_SPACE_ID,
  HelpTypeSchema,
  HomeContextAttributeSchema,
  HomeContextEquipmentSchema,
  HomeContextRoomSchema,
  HomeContextSubscriptionSchema,
  HomeContextSummarySchema,
  HomeContextSystemInfoBlockSchema,
  HomeEvalAiResponseSchema,
  HomeEvalFactsSchema,
  HomeEvalJobIdSchema,
  HomeEvalJobSchema,
  HomeEvalJobStatusSchema,
  HomeEvalResultIdSchema,
  HomeEvalResultSchema,
  HomeEvalResultV1Schema,
  HomeEvalResultV2Schema,
  HomeEvalTriggerSchema,
  HomeEvalTriggerStatusSchema,
  HomeIdSchema,
  HomeInviteEmailHoldSchema,
  HomeInviteEmailStatusSchema,
  HomeInviteLookupSchema,
  HomeInviteSchema,
  HomeInviteStatusSchema,
  HomeLifecycleSchema,
  HomeMemberJoinedDataSchema,
  HomeMemberRequestSchema,
  HomeMemberRoleSchema,
  HomeMemberSchema,
  HomeQuestionIdSchema,
  HomeQuestionSchema,
  HomeQuestionStatusSchema,
  HomeRecallVerdictSchema,
  HomeRequestSchema,
  HomeSchema,
  HomeSetupChecklistSchema,
  HomeSpaceSchema,
  HomeTaskSubscriptionIdSchema,
  HomeTaskSubscriptionSchema,
  INFO_TYPES,
  IdentifyEquipmentRequestSchema,
  IdentifyEquipmentResponseSchema,
  IfDataSchema,
  IfOpSchema,
  IfTransformFnSchema,
  ImageDataSchema,
  ImageEnhancedDataSchema,
  ImageObjectSchema,
  ImageOptionsSchema,
  ImageSchema,
  InAppNotificationSchema,
  InfoDataSchema,
  InfoQuestionSchema,
  InfoTypeIdSchema,
  InfoTypeSchema,
  InitialRoomsDataSchema,
  InitialRoomsDefaultSchema,
  InitialRoomsPreselectSchema,
  InputDataSchema,
  InputFieldSchema,
  InviteCodeRequestSchema,
  KEY_INFO_TITLE,
  LEARN_ATTRIBUTE_LABEL_MAX,
  LEARN_ATTRIBUTE_VALUE_MAX,
  LESSONS_TITLE,
  LastPerformedEstimateSchema,
  LearnCacheKeySchema,
  LearnCacheSchema,
  LearnIdSchema,
  LearnRequestSchema,
  LearnResponseSchema,
  LegacyModelSpellingSchema,
  LengthUnitSchema,
  LessonIdSchema,
  LessonSchema,
  LessonStatusLabels,
  LessonStatusOptions,
  LessonStatusSchema,
  LessonTypeIdSchema,
  LessonTypeSchema,
  LocalDateSchema,
  LocalePreferenceSchema,
  LookupAuthProfileRequestSchema,
  METADATA_TAGS,
  MailerSendLogSchema,
  ManualDigestSchema,
  ManualFetchPolicySchema,
  ManualFetchTierSchema,
  ManualIngestStatusSchema,
  MaybeLaterDurationSchema,
  MergeBrandsRequestSchema,
  MergeBrandsResponseSchema,
  MergeModelsRequestSchema,
  MergeModelsResponseSchema,
  MergedRecallCheckSchema,
  MergedRecallModelRuleSchema,
  MergedRecallRulesSchema,
  MergedRecallSerialRuleSchema,
  ModelAttributeSchema,
  ModelBrandSuggestionSchema,
  ModelCanonicalSchema,
  ModelDetailsSchema,
  ModelDocClassificationSchema,
  ModelDocStatusSchema,
  ModelDocTagSchema,
  ModelDocumentCandidateSchema,
  ModelDocumentIdSchema,
  ModelDocumentLinkSchema,
  ModelDocumentSchema,
  ModelEnrichmentSchema,
  ModelIdSchema,
  ModelIdentityExtractionSchema,
  ModelIdentityKeyedBySchema,
  ModelIdentitySchema,
  ModelKnownAsKindSchema,
  ModelKnownAsSchema,
  ModelManualDataSchema,
  ModelManualIngestSchema,
  ModelMergePreviewSchema,
  ModelNumberHelpCacheKeySchema,
  ModelNumberHelpCacheSchema,
  ModelNumberHelpIdSchema,
  ModelNumberHelpRequestSchema,
  ModelNumberSchema,
  ModelParkReasonSchema,
  ModelParkSchema,
  ModelRevisitReasonSchema,
  ModelRevisitSchema,
  ModelSchema,
  ModelSpellingEntrySchema,
  ModelSpellingKeySchema,
  ModelSpellingSchema,
  ModelVariantKeySchema,
  MotivationSegmentSchema,
  MyHomeInviteSchema,
  NODE_GUIDE_TYPES,
  NOTIFICATION_CONFIG_DEFAULTS,
  NewConsiderationDataSchema,
  NotificationActionSchema,
  NotificationChannelConfigSchema,
  NotificationChannelDefaultsSchema,
  NotificationConfigDefaultsSchema,
  NotificationConfigSchema,
  NotificationIdSchema,
  NotificationImageSchema,
  NotificationTypeSchema,
  NotionWebhookEventIdSchema,
  NotionWebhookEventSchema,
  OutgoingHandleSchema,
  OwnershipStatusSchema,
  PLAN_REVIEW_HOME_SPACE_KEY,
  PageLanguageSchema,
  PageRangeSchema,
  PlanReadyDataSchema,
  PlanReviewErrorCodeSchema,
  PlanReviewIdSchema,
  PlanReviewProgressSchema,
  PlanReviewSchema,
  PlanReviewSpaceProgressSchema,
  PlanReviewSpaceStatusSchema,
  PlanReviewStatusSchema,
  PlanReviewSummarySchema,
  ProcessHowOftenResultIdSchema,
  ProcessHowOftenResultSchema,
  PropertyLookupRequestIdSchema,
  PropertyLookupRequestSchema,
  PropertyLookupResultSchema,
  PropertyLookupStatusSchema,
  PurchaseLinkSchema,
  PushChannelConfigSchema,
  PushPromptDataSchema,
  PushReceiptSchema,
  PushReceiptStatusSchema,
  RECALLS_STATE_ID,
  RECALL_PROCESS_KIND_LIMIT,
  RECALL_STATUSES,
  RETAILER_IDS,
  ROOM_FLOOR_BASEMENT,
  ROOM_FLOOR_EXTERNAL_OTHER,
  RecallAiCallSchema,
  RecallCheckKindSchema,
  RecallCheckSchema,
  RecallClassificationSchema,
  RecallClassifierResultSchema,
  RecallDisplaySchema,
  RecallDryRunSchema,
  RecallEvidenceSchema,
  RecallExtractionSchema,
  RecallIdSchema,
  RecallImageSchema,
  RecallJobIdSchema,
  RecallJobProgressSchema,
  RecallJobRequestSchema,
  RecallJobSchema,
  RecallJobStatusSchema,
  RecallKeySchema,
  RecallMatchSchema,
  RecallMatchStrengthSchema,
  RecallModelRuleKindSchema,
  RecallModelRuleSchema,
  RecallNeedSchema,
  RecallPageUnavailableSchema,
  RecallPrefixMatchSchema,
  RecallRejectReasonSchema,
  RecallResolutionOutcomeSchema,
  RecallRuleSourceSchema,
  RecallSchema,
  RecallScopeSchema,
  RecallSerialRuleKindSchema,
  RecallSerialRuleSchema,
  RecallSourceSchema,
  RecallStageSchema,
  RecallStatusSchema,
  RecallSyncOutcomeSchema,
  RecallTopicFollowSchema,
  RecallTopicFollowsSchema,
  RecallTopicIconSchema,
  RecallTopicIdSchema,
  RecallTopicSchema,
  RecallTopicStatsSchema,
  RecallVerdictIdSchema,
  RecallVerdictSchema,
  RecallVerdictValueSchema,
  RecallVerificationSchema,
  RecallWithdrawnReasonSchema,
  RecallsStateSchema,
  RecentlyViewedIdSchema,
  RecentlyViewedSchema,
  RefreshBrandOptionSchema,
  RefreshIdSchema,
  RemapEquipmentTypeRequestSchema,
  RemapEquipmentTypeResponseSchema,
  ResearchCheckVerdictSchema,
  ResearchedAliasKindSchema,
  ResearchedAliasSchema,
  ResearchedDomainRoleSchema,
  ResearchedDomainSchema,
  ResearchedModelCheckSchema,
  ResearchedModelSchema,
  ResolveEquipmentTypeRequestSchema,
  ResolveEquipmentTypeResponseSchema,
  RestoreDocumentTrimRequestSchema,
  RestoreDocumentTrimResponseSchema,
  RestoreEquipmentTypeRequestSchema,
  RestoreEquipmentTypeResponseSchema,
  RetailerIdSchema,
  RetireEquipmentTypeRequestSchema,
  RetireEquipmentTypeResponseSchema,
  RetireEquipmentTypeUsageSchema,
  RetiredTypeReferenceKindSchema,
  RetiredTypeReferenceSchema,
  ReturnDataSchema,
  RockDocIdSchema,
  RockManualsJobIdSchema,
  RockManualsJobSchema,
  RockManualsJobStatusSchema,
  RoomDataSchema,
  RoomEnhancementIdSchema,
  RoomEnhancementSchema,
  RoomEnhancementStatusSchema,
  RoomGroupIdSchema,
  RoomGroups,
  RoomGroupsById,
  RoomGuideStarterSchema,
  RoomIdSchema,
  RoomSchema,
  RoomStarterSelectionDataSchema,
  RoomTypeLabels,
  RoomTypeSchema,
  SETUP_CHECKLIST_HOME_ITEMS,
  SETUP_CHECKLIST_RECORDED_ITEMS,
  SETUP_CHECKLIST_USER_ITEMS,
  ScanDataSchema,
  SeasonPartSchema,
  SeasonalWindowSchema,
  SelectDataSchema,
  SelectOptionSchema,
  SerialConfirmationSchema,
  SerialNumberSchema,
  SetDataSchema,
  SetupChecklistHomeItemSchema,
  SetupChecklistItemSchema,
  SetupChecklistMarkerSchema,
  SetupChecklistOutcomeSchema,
  SetupChecklistRecordedItemSchema,
  SetupChecklistUserItemSchema,
  SharedNameplateEntrySchema,
  SharedNameplateRouteSchema,
  SharedNameplateSchema,
  SourcedValueSchema,
  SpaceGroupIdSchema,
  SpaceGroupLabels,
  SpaceGuideContextSchema,
  SpaceIdSchema,
  SpaceSchema,
  SpaceSummaryFinishedViaSchema,
  SpaceSummarySchema,
  SpaceSummaryStatusSchema,
  StatedModelRangeSchema,
  SubGuideDataSchema,
  SupersededViaSchema,
  SuppliesEquipmentConfigSchema,
  SystemInfoDataSchema,
  SystemInfoIdSchema,
  SystemInfoSchema,
  TaskDataSchema,
  TaskEffortSchema,
  TaskGenerationJobRunIdSchema,
  TaskGenerationJobRunSchema,
  TaskGenerationJobRunStatusSchema,
  TaskIdSchema,
  TaskImageOptionSchema,
  TaskRefreshSchema,
  TaskSchema,
  TaskScreenConfigSchema,
  TaskScreenPictureSlotCategorySchema,
  TaskScreenPictureSlotSchema,
  TaskScreenSectionContactSchema,
  TaskScreenSectionHistorySchema,
  TaskScreenSectionInstructionsSchema,
  TaskScreenSectionNotesSchema,
  TaskScreenSectionPicturesSchema,
  TaskScreenSectionSchema,
  TaskScreenSectionSuppliesSchema,
  TaskStatusSchema,
  TaskSubscriptionIntervalSchema,
  TaskSubscriptionIntervalUnitSchema,
  TaskSubscriptionOneTimeSchema,
  TaskSubscriptionRecurringSchema,
  TaskSubscriptionSchema,
  TaskSupplyChangeSchema,
  TaskTypeIdSchema,
  TaskTypeSchema,
  TaskTypeScreenConfigSchema,
  TaskTypeScreenSectionContactSchema,
  TaskTypeScreenSectionHistorySchema,
  TaskTypeScreenSectionInstructionsSchema,
  TaskTypeScreenSectionNotesSchema,
  TaskTypeScreenSectionPicturesSchema,
  TaskTypeScreenSectionSchema,
  TaskTypeScreenSectionSuppliesSchema,
  TimestampSchema,
  TrimCautionSchema,
  TrimProposalOutcomeSchema,
  TrimSelectionSchema,
  TrimTargetSchema,
  TypeConfidenceSchema,
  TypeGuideStarterSchema,
  TypeIdSchema,
  UNTITLED_CONSIDERATION,
  USER_LIMIT_DEFAULTS,
  UiModeSchema,
  UserIdSchema,
  UserLimitKeySchema,
  UserLimitsConfigSchema,
  UserLimitsSchema,
  UserNotificationIdSchema,
  UserNotificationSchema,
  UserOnboardingSchema,
  UserSchema,
  UserSetupChecklistSchema,
  UserTaskScreenConfigSchema,
  UserTaskScreenSectionSchema,
  WEEKEND_MOMENTS,
  WeekendChecklistDataSchema,
  WeekendChecklistReviewItemSchema,
  WeekendChecklistTaskItemSchema,
  WeekendFillSchema,
  WeekendSectionCountsSchema,
  WeekendStartDataSchema,
  WeekendStartSchema,
  WeeklyTasksSchema,
  WelcomeDataSchema,
  YesNoDataSchema,
  appliesToOwnerCopy,
  approvedBrandAssets,
  assertUnreachable,
  brandDisplayLabel,
  buildHomeInvite,
  chatDailyLimitReason,
  chatHintsRegistry,
  collectRockDocRefs,
  considerationTitle,
  daysUntilInviteExpires,
  edgeDataSchemas,
  enhanceDailyLimitReason,
  equipmentBrandQuestionId,
  equipmentRefreshDailyLimitReason,
  finishedSpaceIds,
  foldEmail,
  getAllOutgoingHandles,
  getAllowedNodeTypes,
  getChainedDescription,
  getChatHints,
  getConversationLabel,
  getGuideItemId,
  getGuideType,
  getMultiSelectDefaultValues,
  hasWhenToApplyText,
  highlightForCategory,
  homeInviteLookupMessage,
  homeInviteStatus,
  homeLifecycle,
  homeMemberIds,
  imageForCategory,
  infoBindingForNode,
  infoQuestionId,
  inviteLookupDailyLimitReason,
  isBrandRequestOpen,
  isBrandResearchUnderway,
  isChatWarmupRequest,
  isCompletedStarter,
  isConversationReadOnly,
  isCustomId,
  isDismissedStarter,
  isEquipmentRecallVerdict,
  isHomeFull,
  isHomeMember,
  isHomeRecallVerdict,
  isInviteEmail,
  isNodeAllowedInGuide,
  isNotificationType,
  isNumericDataType,
  isObjectSchema,
  isObjectType,
  isOfferableType,
  isOpenRecallVerdict,
  isQuietFyi,
  isRemoteImageUrl,
  isScannableCodeType,
  isSelectField,
  isVisibleRecallVerdict,
  isWithheldStatus,
  jsonParser,
  memberProfileOf,
  nodeDataSchemas,
  openInviteTo,
  orderedImages,
  otherPaidCallsDailyLimitReason,
  outgoingHandles,
  ownerMembership,
  pendingBrandRequestOf,
  placeLookupDailyLimitReason,
  planStartersOnEquipmentCreated,
  primaryImage,
  recallKey,
  recallVerdictId,
  resolveInviteCode,
  resolveUserLimit,
  rockDocRefKey,
  sameMemberProfile,
  sortRecallTopics,
  starterIdForRoom,
  starterIdForType,
  titledConsideration,
  toImageCollection,
  typeChoiceIcon,
  typeChoiceKey,
  typeChoiceSubtitle,
  typeChoiceTitle,
  unmatchedInputFields,
  whenToApplyText,
  withHighlightForCategory,
  withSingularArrayFields
};
//# sourceMappingURL=index.js.map